// CTH Video: the mChapters-style clip player behind videos.coachtonyhockey.com.
//
// The engine is the CTH Studio / CTH Clips / Film Room scrub engine, ported
// nearly verbatim (~/apps/cth-apps/studio/js/scrub.js and
// ~/apps/cth-apps/shared/scrubsource.js). The constants are tuned, not
// chosen; do not round them off. How it runs here:
//   - bytes come from the app's server through HOST.provider (Range reads on a
//     signed link);
//   - the picture is painted onto a fixed overlay canvas laid over the
//     <video> element for the length of a gesture, then the element is
//     hard-seeked to the final frame and the overlay drops away;
//   - playback stays native.
//
// The player is written against a small Obsidian-shaped UI kit
// (web/obsidian-shim.js: DOM helpers, Plugin, Setting, Modal, Notice ...),
// which is why the classes below say plugin, vault, leaf and view.

import '../web/host-setup.js';
import { Plugin, PluginSettingTab, Setting, Modal, Notice, normalizePath, setIcon, copyText } from '../web/obsidian-shim.js';

// HOST: the page around the player (web/app.js) fills globalThis.__CVS_HOST__
// before any player exists: HOST.provider(path) streams video bytes from
// the server, HOST.pathFor(video) names the file a <video> plays, HOST.ClipExporter
// makes mp4s in the browser (WebCodecs), HOST.reveal / HOST.revealFolder show
// a folder in the library, HOST.noFullscreen explains a blocked fullscreen inside an embed.
// The player hands HOST.internals (drawMark and friends) to the exporter.
const HOST = globalThis.__CVS_HOST__;

// "a/b/c.mp4" -> "c.mp4", "a/b"
const baseName = (p) => String(p).replace(/\/+$/, '').split('/').pop() || '';
const dirName = (p) => { const s = String(p).replace(/\/+$/, ''); const i = s.lastIndexOf('/'); return i < 0 ? '' : s.slice(0, i); };

// ============================================================================
// scrubsource: mp4 demuxer + WebCodecs decoder with a frame cache
// ============================================================================

const RANGE_MAX = 96 * 1024 * 1024;

// Fullscreen, real (the browser's) or filled (the stage covers the window or the
// embed's frame, for iPhones and embeds where real fullscreen is blocked).
const fsElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;
const anyFull = () => !!fsElement() || !!document.querySelector('.cvs-stage.is-filled');

// Range reads of the video file, from the server (see HOST).
function byteProvider(path) { return HOST.provider(path); }

const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'edts']);
const fourcc = (dv, p) => String.fromCharCode(dv.getUint8(p), dv.getUint8(p + 1), dv.getUint8(p + 2), dv.getUint8(p + 3));

function readBoxHeader(dv, pos, limit) {
  if (pos + 8 > limit) return null;
  let size = dv.getUint32(pos);
  const type = fourcc(dv, pos + 4);
  let body = pos + 8;
  if (size === 1) {
    if (pos + 16 > limit) return null;
    size = dv.getUint32(pos + 8) * 4294967296 + dv.getUint32(pos + 12);
    body = pos + 16;
  } else if (size === 0) {
    size = limit - pos;
  }
  if (size < 8 || pos + size > limit) return null;
  return { type, start: pos, body, end: pos + size };
}

function eachChild(dv, from, to, fn) {
  let pos = from;
  while (pos < to) {
    const b = readBoxHeader(dv, pos, to);
    if (!b) break;
    fn(b);
    pos = b.end;
  }
}

async function findMoov(provider) {
  let pos = 0;
  const fileSize = provider.size ?? Infinity;
  for (let hops = 0; hops < 64 && pos < fileSize; hops++) {
    const head = await provider.read(pos, 16);
    if (!head || head.length < 8) return null;
    const dv = new DataView(head.buffer, head.byteOffset, head.byteLength);
    let size = dv.getUint32(0);
    const type = fourcc(dv, 4);
    let hdr = 8;
    if (size === 1) {
      if (head.length < 16) return null;
      size = dv.getUint32(8) * 4294967296 + dv.getUint32(12);
      hdr = 16;
    } else if (size === 0) {
      size = (provider.size ?? 0) - pos;
      if (size <= 0) return null;
    }
    if (size < 8) return null;
    if (type === 'moov') {
      if (size - hdr > RANGE_MAX) return null;
      return provider.read(pos + hdr, size - hdr);
    }
    pos += size;
  }
  return null;
}

const version = (dv, b) => dv.getUint8(b.body);

function parseRotation(dv, b) {
  try {
    const off = b.body + (version(dv, b) === 1 ? 52 : 40);
    const ONE = 65536;
    const a = dv.getInt32(off);
    const bb = dv.getInt32(off + 4);
    const c = dv.getInt32(off + 12);
    const d = dv.getInt32(off + 16);
    if (bb === ONE && c === -ONE) return 90;
    if (a === -ONE && d === -ONE) return 180;
    if (bb === -ONE && c === ONE) return 270;
    return 0;
  } catch (_) {
    return 0;
  }
}

function parseMdhd(dv, b) {
  const v = version(dv, b);
  let p = b.body + 4;
  if (v === 1) { p += 16; return { timescale: dv.getUint32(p), duration: Number(dv.getBigUint64(p + 4)) }; }
  p += 8;
  return { timescale: dv.getUint32(p), duration: dv.getUint32(p + 4) };
}

function parseElst(dv, b, mediaTimescale, movieTimescale) {
  const v = version(dv, b);
  const n = dv.getUint32(b.body + 4);
  let p = b.body + 8;
  let shift = 0;
  let mediaStart = 0;
  let seenReal = false;
  for (let i = 0; i < n && p < b.end; i++) {
    let dur; let mt;
    if (v === 1) { dur = Number(dv.getBigUint64(p)); mt = Number(dv.getBigInt64(p + 8)); p += 20; }
    else { dur = dv.getUint32(p); mt = dv.getInt32(p + 4); p += 12; }
    if (mt < 0) { shift += dur / (movieTimescale || 1); continue; }
    if (!seenReal) { mediaStart = mt / mediaTimescale; seenReal = true; }
  }
  return shift - mediaStart;
}

const hexByte = (n) => n.toString(16).padStart(2, '0');
const avcCodec = (cfg) => `avc1.${hexByte(cfg[1])}${hexByte(cfg[2])}${hexByte(cfg[3])}`;
// ISO 14496-15 Annex E: general_profile_compatibility_flags is 32 bits,
// big-endian in hvcC bytes 2..5, written in the codec string in REVERSE bit
// order as hex with leading zeros dropped. Main profile 0x60000000 -> "6".
// `legacy` reproduces the CTH Studio port's byte order for a fallback try.
function hevcCodec(cfg, legacy = false) {
  const b1 = cfg[1];
  const space = ['', 'A', 'B', 'C'][(b1 >> 6) & 3];
  const tier = ((b1 >> 5) & 1) ? 'H' : 'L';
  const profile = b1 & 31;
  let compat = 0;
  if (legacy) for (let i = 0; i < 4; i++) compat = (compat >>> 0) | (((cfg[2 + i] >>> 0) & 0xff) << (i * 8));
  else compat = ((cfg[2] << 24) | (cfg[3] << 16) | (cfg[4] << 8) | cfg[5]) >>> 0;
  let rev = 0;
  for (let i = 0; i < 32; i++) rev = ((rev << 1) | ((compat >>> i) & 1)) >>> 0;
  const constraints = [];
  for (let i = 11; i >= 6; i--) { if (cfg[i] || constraints.length) constraints.unshift(hexByte(cfg[i])); }
  return `hvc1.${space}${profile}.${(rev >>> 0).toString(16)}.${tier}${cfg[12]}${constraints.length ? '.' + constraints.join('.') : ''}`;
}

function parseStsd(dv, bytes, b) {
  const p = b.body + 8;
  const e = readBoxHeader(dv, p, b.end);
  if (!e) return null;
  const out = {
    format: e.type,
    width: dv.getUint16(e.start + 32),
    height: dv.getUint16(e.start + 34),
    codec: null,
    codecAlt: null,
    description: null,
  };
  eachChild(dv, e.start + 86, e.end, (c) => {
    if (c.type === 'avcC' || c.type === 'hvcC') {
      const cfg = bytes.subarray(c.body, c.end);
      out.description = Uint8Array.from(cfg);
      out.codec = c.type === 'avcC' ? avcCodec(cfg) : hevcCodec(cfg);
      out.codecAlt = c.type === 'hvcC' ? hevcCodec(cfg, true) : null;
    }
  });
  return out;
}

function buildSamples(dv, tables, timescale, ptsOffset) {
  const { stts, ctts, stss, stsc, stsz, stco, co64 } = tables;
  if (!stts || !stsc || !stsz || !(stco || co64)) return null;

  const sizeConst = dv.getUint32(stsz.body + 4);
  const count = dv.getUint32(stsz.body + 8);
  if (!count) return null;
  const size = new Uint32Array(count);
  if (sizeConst) size.fill(sizeConst);
  else for (let i = 0; i < count; i++) size[i] = dv.getUint32(stsz.body + 12 + i * 4);

  const dts = new Float64Array(count);
  {
    const n = dv.getUint32(stts.body + 4);
    let p = stts.body + 8; let i = 0; let t = 0;
    for (let e = 0; e < n && i < count; e++) {
      const c = dv.getUint32(p); const d = dv.getUint32(p + 4); p += 8;
      for (let k = 0; k < c && i < count; k++) { dts[i++] = t; t += d; }
    }
    if (i < count) { const d = i > 1 ? dts[i - 1] - dts[i - 2] : timescale / 30; for (; i < count; i++) { dts[i] = t; t += d; } }
  }

  const pts = new Float64Array(count);
  if (ctts) {
    const v = version(dv, ctts);
    const n = dv.getUint32(ctts.body + 4);
    let p = ctts.body + 8; let i = 0;
    for (let e = 0; e < n && i < count; e++) {
      const c = dv.getUint32(p);
      const o = v === 1 ? dv.getInt32(p + 4) : dv.getUint32(p + 4);
      p += 8;
      for (let k = 0; k < c && i < count; k++) { pts[i] = dts[i] + o; i++; }
    }
    for (; i < count; i++) pts[i] = dts[i];
  } else {
    pts.set(dts);
  }
  for (let i = 0; i < count; i++) pts[i] = pts[i] / timescale + ptsOffset;
  for (let i = 0; i < count; i++) dts[i] = dts[i] / timescale + ptsOffset;

  const key = new Uint8Array(count);
  if (stss) {
    const n = dv.getUint32(stss.body + 4);
    for (let e = 0; e < n; e++) {
      const s = dv.getUint32(stss.body + 8 + e * 4) - 1;
      if (s >= 0 && s < count) key[s] = 1;
    }
  } else {
    key.fill(1);
  }
  key[0] = 1;

  const off = new Float64Array(count);
  {
    const chunkBox = co64 || stco;
    const chunkCount = dv.getUint32(chunkBox.body + 4);
    const chunkOff = (i) => (co64
      ? Number(dv.getBigUint64(co64.body + 8 + i * 8))
      : dv.getUint32(stco.body + 8 + i * 4));
    const runs = dv.getUint32(stsc.body + 4);
    let s = 0;
    for (let r = 0; r < runs && s < count; r++) {
      const first = dv.getUint32(stsc.body + 8 + r * 12) - 1;
      const per = dv.getUint32(stsc.body + 8 + r * 12 + 4);
      const lastChunk = r + 1 < runs ? dv.getUint32(stsc.body + 8 + (r + 1) * 12) - 1 : chunkCount;
      for (let c = first; c < lastChunk && s < count; c++) {
        let at = chunkOff(c);
        for (let k = 0; k < per && s < count; k++) { off[s] = at; at += size[s]; s++; }
      }
    }
    if (s < count) return null;
  }

  const order = new Int32Array(count);
  for (let i = 0; i < count; i++) order[i] = i;
  let sorted = true;
  for (let i = 1; i < count; i++) if (pts[i] < pts[i - 1]) { sorted = false; break; }
  if (!sorted) order.sort((a, b) => pts[a] - pts[b]);

  let reorder = 0;
  if (!sorted) {
    const WINDOW = 32;
    for (let i = 0; i < count; i++) {
      const lim = Math.min(count, i + WINDOW + 1);
      for (let j = i + 1; j < lim; j++) if (pts[j] < pts[i] && j - i > reorder) reorder = j - i;
    }
  }

  return { count, reorder, pts, dts, off, size, key, order };
}

async function indexVideo(provider) {
  try {
    const moovBytes = await findMoov(provider);
    if (!moovBytes) return null;
    const dv = new DataView(moovBytes.buffer, moovBytes.byteOffset, moovBytes.byteLength);
    const len = moovBytes.byteLength;

    let movieTimescale = 0;
    eachChild(dv, 0, len, (b) => {
      if (b.type !== 'mvhd') return;
      movieTimescale = version(dv, b) === 1 ? dv.getUint32(b.body + 20) : dv.getUint32(b.body + 12);
    });
    movieTimescale = movieTimescale || 1000;

    let found = null;
    eachChild(dv, 0, len, (trak) => {
      if (found || trak.type !== 'trak') return;
      const tables = {};
      let isVideo = false; let timescale = 0; let mediaDur = 0; let stsd = null; let elst = null; let tkhd = null;
      const walk = (from, to, parent) => eachChild(dv, from, to, (b) => {
        if (CONTAINERS.has(b.type)) { walk(b.body, b.end, b.type); return; }
        if (b.type === 'tkhd' && parent === 'trak') { tkhd = b; return; }
        if (b.type === 'hdlr' && parent === 'mdia') isVideo = fourcc(dv, b.body + 8) === 'vide';
        else if (b.type === 'mdhd') { const m = parseMdhd(dv, b); timescale = m.timescale; mediaDur = m.duration; }
        else if (b.type === 'stsd') stsd = b;
        else if (b.type === 'elst') elst = b;
        else if (['stts', 'ctts', 'stss', 'stsc', 'stsz', 'stz2', 'stco', 'co64'].includes(b.type)) tables[b.type] = b;
      });
      walk(trak.body, trak.end, 'trak');
      if (!isVideo || !timescale || !stsd) return;
      if (tables.stz2 && !tables.stsz) return;
      const desc = parseStsd(dv, moovBytes, stsd);
      if (!desc || !desc.codec || !desc.description) return;
      const ptsOffset = elst ? parseElst(dv, elst, timescale, movieTimescale) : 0;
      const s = buildSamples(dv, tables, timescale, ptsOffset);
      if (!s) return;
      found = {
        codec: desc.codec,
        codecAlt: desc.codecAlt,
        description: desc.description,
        width: desc.width,
        height: desc.height,
        rotation: tkhd ? parseRotation(dv, tkhd) : 0,
        duration: mediaDur / timescale,
        ...s,
      };
    });
    return found;
  } catch (_) {
    return null;
  }
}

const CACHE_BYTES = 640 * 1024 * 1024;
// iOS caps canvas memory near 384 MB and drops the page past it, so phones,
// tablets and small-memory machines get a smaller frame cache (setting frameCacheMB;
// 0 = automatic). Applies the next time a video opens.
const CACHE_BYTES_SMALL = 128 * 1024 * 1024;
function smallDevice() {
  try { return (navigator.maxTouchPoints > 0 && matchMedia('(pointer: coarse)').matches) || (navigator.deviceMemory > 0 && navigator.deviceMemory <= 4); } catch (_) { return false; }
}
function cacheBytesFor(settings) {
  const mb = Number(settings && settings.frameCacheMB) || 0;
  return mb > 0 ? mb * 1024 * 1024 : smallDevice() ? CACHE_BYTES_SMALL : CACHE_BYTES;
}
const CACHE_MAX_W = 1920;
const MAX_LAG = 900;
const WINDOW_FRAMES = 480;
const QUEUE_DEPTH = 48;
const FEED_MAX_CALL = 192;
const PREFETCH_AHEAD = 96;
const FAST_FPS = 1600;
const COARSE_RESEED_MS = 60;
const FWD_THROUGH = 90;
const RESEED_MS = 180;
const RESEED_MISS_MS = 25;
const LOOKAHEAD = 4;
const SOFT_TOL = 1.5;
const IDLE_DROP_MS = 60000;

const openSources = new Map();

function releaseScrubSource(id) {
  const s = openSources.get(id);
  if (s) { openSources.delete(id); s.destroy(); }
}

function releaseAllScrubSources() {
  for (const [k, v] of openSources) { openSources.delete(k); v.destroy(); }
}

// Returns { src } or { src: null, why } so the diagnostics can say what failed.
async function openScrubSource(id, provider, cacheBytes = CACHE_BYTES) {
  if (!id || !provider) return { src: null, why: 'no provider' };
  if (typeof VideoDecoder === 'undefined') return { src: null, why: 'WebCodecs unavailable' };
  if (openSources.has(id)) return { src: openSources.get(id) };

  const ix = await indexVideo(provider).catch(() => null);
  if (!ix || !ix.count || !ix.codec || !ix.description) return { src: null, why: 'could not index mp4 (webm, fragmented mp4, or unsupported codec)' };
  if (ix.rotation) return { src: null, why: `rotated file (${ix.rotation} deg)` };

  const config = {
    codec: ix.codec,
    description: ix.description,
    codedWidth: ix.width,
    codedHeight: ix.height,
    hardwareAcceleration: 'prefer-hardware',
    optimizeForLatency: true,
  };
  let ok = false;
  try { ok = (await VideoDecoder.isConfigSupported(config)).supported; } catch (_) { ok = false; }
  if (!ok && ix.codecAlt) {
    config.codec = ix.codecAlt;
    try { ok = (await VideoDecoder.isConfigSupported(config)).supported; } catch (_) { ok = false; }
    if (ok) ix.codec = ix.codecAlt;
  }
  if (!ok) {
    // no hardware decoder (a Linux or Windows machine without one, some
    // browsers): software decoding still scrubs, just less smoothly
    config.hardwareAcceleration = 'no-preference';
    try { ok = (await VideoDecoder.isConfigSupported(config)).supported; } catch (_) { ok = false; }
  }
  if (!ok) return { src: null, why: `VideoDecoder rejected ${ix.codec}` };

  const src = makeSource(id, provider, ix, config, cacheBytes);
  releaseAllScrubSources();
  openSources.set(id, src);
  return { src };
}

function makeSource(id, provider, ix, config, cacheBytes = CACHE_BYTES) {
  const { count, pts, key, off, size, order } = ix;
  const frameSec = (ix.duration && count) ? ix.duration / count : 1 / 30;
  const WINDOW_BYTES = 32 * 1024 * 1024;

  const cw = Math.min(CACHE_MAX_W, ix.width || CACHE_MAX_W);
  const ch = Math.max(1, Math.round(cw * (ix.height || 1) / (ix.width || 1)));
  const budget = Math.max(8, Math.floor(cacheBytes / (cw * ch * 4)));
  const cache = new Map();
  const pool = [];
  let lastServed = -1;
  let lastDir = 0;
  let stride = 1;

  const takeCanvas = () => {
    if (pool.length) return pool.pop();
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    return { c, ctx: c.getContext('2d', { alpha: false }) };
  };
  const evict = () => {
    if (cache.size <= budget) return;
    const fwd = (lastDir < 0 ? 12 : Math.round(budget * 0.6)) * stride;
    const back = (lastDir < 0 ? Math.round(budget * 0.55) : 24) * stride;
    for (const k of cache.keys()) {
      if (cache.size <= budget) break;
      if (lastServed >= 0 && k >= lastServed - back && k <= lastServed + fwd) continue;
      pool.push(cache.get(k));
      cache.delete(k);
    }
    while (cache.size > budget) {
      const k = cache.keys().next().value;
      pool.push(cache.get(k));
      cache.delete(k);
    }
  };
  const dropCache = () => { for (const v of cache.values()) pool.push(v); cache.clear(); };

  const ptsAt = (j) => pts[order[j]];
  function presFor(t) {
    let lo = 0; let hi = count - 1; let ans = -1;
    while (lo <= hi) { const m = (lo + hi) >> 1; if (ptsAt(m) <= t) { ans = m; lo = m + 1; } else hi = m - 1; }
    return ans;
  }
  const keyAtOrBefore = (i) => { while (i > 0 && !key[i]) i--; return i; };
  const presOf = new Int32Array(count);
  for (let j = 0; j < count; j++) presOf[order[j]] = j;

  function nearest(j, below, above) {
    const nb = Math.max(0, Math.round(below / frameSec));
    const na = Math.max(0, Math.round(above / frameSec));
    for (let d = 1; d <= Math.max(nb, na); d++) {
      if (d <= nb) { const s = cache.get(j - d); if (s) { stats.near++; lastServed = j - d; return { c: s.c, t: ptsAt(j - d) }; } }
      if (d <= na) { const s = cache.get(j + d); if (s) { stats.near++; lastServed = j + d; return { c: s.c, t: ptsAt(j + d) }; } }
    }
    return null;
  }

  function keyTimeBelow(t) {
    const j = presFor(t);
    if (j < 0) return null;
    return pts[keyAtOrBefore(order[j])];
  }

  const wins = [];
  const winFor = (i) => wins.find((w) => i >= w.from && i < w.to);
  let reading = false;

  async function loadWindow(from) {
    if (reading || dead || from < 0 || from >= count) return false;
    reading = true;
    try {
      let to = Math.min(count, from + WINDOW_FRAMES);
      let bytes = (off[to - 1] + size[to - 1]) - off[from];
      while (to > from + 1 && bytes > WINDOW_BYTES) { to = from + Math.max(1, ((to - from) >> 1)); bytes = (off[to - 1] + size[to - 1]) - off[from]; }
      const buf = await provider.read(off[from], bytes);
      if (dead || !buf) return false;
      wins.push({ from, to, bytes: buf });
      while (wins.length > 2) {
        const olds = wins.slice(0, -1);
        const drop = olds.find((w) => !(fedTo + 1 >= w.from && fedTo + 1 < w.to)) || olds[0];
        wins.splice(wins.indexOf(drop), 1);
      }
      return true;
    } catch (_) {
      return false;
    } finally {
      reading = false;
    }
  }

  let dec = null;
  let fedTo = -1;
  let positioned = false;
  let seedAt = Infinity;
  let fillGoal = null;
  let seeding = false;
  let lastReseed = 0;
  let missing = false;
  let lastReqT = null;
  let idleTimer = null;
  let dead = false;
  const stats = { hit: 0, near: 0, decoded: 0, fallback: 0, reseed: 0, err: '' };

  const teardown = () => {
    if (dec) { try { dec.close(); } catch (_) { /* already closed */ } }
    dec = null; fedTo = -1; positioned = false; seedAt = Infinity; fillGoal = null;
  };

  function onFrame(f) {
    const t = f.timestamp / 1e6;
    let lo = 0; let hi = count - 1; let j = -1;
    while (lo <= hi) { const m = (lo + hi) >> 1; if (ptsAt(m) <= t + 1e-6) { j = m; lo = m + 1; } else hi = m - 1; }
    const wanted = stride === 1 || j % stride === 0 || key[order[j]] === 1;
    if (j >= 0 && wanted && !cache.has(j)) {
      const slot = takeCanvas();
      slot.ctx.drawImage(f, 0, 0, cw, ch);
      cache.set(j, slot);
      stats.decoded++;
      evict();
    }
    f.close();
  }

  function newDecoder() {
    teardown();
    dec = new VideoDecoder({
      output: onFrame,
      error: (e) => { stats.err = String(e && e.message || e); teardown(); },
    });
    dec.configure(config);
  }

  const chunkFor = (i, w) => new EncodedVideoChunk({
    type: key[i] ? 'key' : 'delta',
    timestamp: Math.round(pts[i] * 1e6),
    data: w.bytes.subarray(off[i] - off[w.from], off[i] - off[w.from] + size[i]),
  });

  function feed(need, cap = FEED_MAX_CALL, gate = true) {
    if (!dec || dec.state !== 'configured') return;
    let fed = 0;
    while (fedTo < need && fed < cap) {
      if (gate && dec.decodeQueueSize >= QUEUE_DEPTH) break;
      const i = fedTo + 1;
      const w = winFor(i);
      if (!w) { void loadWindow(i); break; }
      try { dec.decode(chunkFor(i, w)); } catch (_) { teardown(); return; }
      fedTo = i; fed++;
      if (fedTo + PREFETCH_AHEAD >= w.to && w.to < count && !winFor(w.to)) void loadWindow(w.to);
    }
  }

  async function reseed(atDecodeIdx) {
    if (seeding || dead) return;
    seeding = true;
    stats.reseed++;
    try {
      const k = keyAtOrBefore(Math.max(0, atDecodeIdx));
      if (!winFor(k)) { if (!await loadWindow(k)) return; }
      if (dead) return;
      newDecoder();
      fedTo = k - 1;
      seedAt = k;
      positioned = true;
    } catch (_) {
      teardown();
    } finally {
      seeding = false;
    }
  }

  const reseedOk = (now) => now - lastReseed > (missing ? RESEED_MISS_MS : RESEED_MS);

  function request(t, speed = 0, tol = 0) {
    if (dead) return null;
    clearTimeout(idleTimer);
    const j = presFor(t);
    if (j < 0) return null;
    const want = Math.max(1, Math.min(8, Math.round(speed / 144)));
    stride += Math.sign(want - stride);
    const dir = lastReqT == null ? 0 : (t > lastReqT + 1e-4 ? 1 : (t < lastReqT - 1e-4 ? -1 : lastDir));
    lastDir = dir;
    lastReqT = t;
    const target = order[j];

    if (speed > FAST_FPS) {
      const exact = cache.get(j);
      if (exact) { stats.hit++; lastServed = j; missing = false; return { c: exact.c, t: ptsAt(j) }; }
      const kIdx = keyAtOrBefore(target);
      const kJ = presOf[kIdx];
      const kSlot = cache.get(kJ);
      if (!kSlot) {
        const standing = dec && dec.state === 'configured' && positioned && seedAt === kIdx;
        if (standing) {
          feed(Math.min(count - 1, kIdx + LOOKAHEAD + 2));
        } else if (!seeding) {
          const now = performance.now();
          if (now - lastReseed > COARSE_RESEED_MS) { lastReseed = now; fillGoal = null; void reseed(kIdx); }
        }
      }
      const gopBack = Math.max(0, t - ptsAt(kJ));
      const near = nearest(j, gopBack + frameSec, frameSec * 4);
      if (near) { missing = false; return near; }
      if (kSlot) { stats.hit++; lastServed = kJ; missing = false; return { c: kSlot.c, t: ptsAt(kJ) }; }
      stats.fallback++;
      missing = true;
      return null;
    }

    const warm = dec && dec.state === 'configured' && positioned;
    if (fillGoal != null && warm) {
      feed(fillGoal);
      if (fedTo >= fillGoal) fillGoal = null;
    } else {
      const walkThrough = warm && target >= fedTo && target - fedTo <= FWD_THROUGH;
      const reachable = warm && target >= seedAt
        && (walkThrough || keyAtOrBefore(target) <= fedTo + 1)
        && target - fedTo <= MAX_LAG;
      if (reachable) {
        const ahead = Math.min(Math.round(budget * 0.5), Math.round(speed * 0.025) + LOOKAHEAD + 8);
        feed(Math.min(count - 1, target + ahead));
      } else if (!seeding && !(warm && seedAt === keyAtOrBefore(target))) {
        const now = performance.now();
        if (reseedOk(now)) { lastReseed = now; fillGoal = null; void reseed(target); }
      }
    }

    const slot = cache.get(j);
    if (slot) { stats.hit++; lastServed = j; missing = false; return { c: slot.c, t: ptsAt(j) }; }

    const catching = seeding || fillGoal != null
      || (warm && target - fedTo > LOOKAHEAD + 2);
    const latt = stride * frameSec * 1.5;
    const range = Math.min(SOFT_TOL, Math.max(tol, latt, catching ? SOFT_TOL : 0));
    if (range > 0) {
      const near = nearest(j, range, range);
      if (near) { missing = false; return near; }
    }
    const inRun = warm && !seeding && seedAt === keyAtOrBefore(target);
    if (!inRun && !seeding) {
      const now = performance.now();
      if (reseedOk(now)) { lastReseed = now; fillGoal = null; void reseed(target); }
    }
    stats.fallback++;
    missing = true;
    return null;
  }

  function prime(t) {
    if (dead || seeding) return;
    const j = presFor(t);
    if (j < 0) return;
    const target = order[j];
    if (dec && dec.state === 'configured' && positioned
      && target >= seedAt && keyAtOrBefore(target) <= fedTo + 1 && target >= fedTo) {
      feed(Math.min(count - 1, target + LOOKAHEAD), 4096, false);
      return;
    }
    reseed(target).then(() => {
      if (dead || !dec || dec.state !== 'configured') return;
      feed(Math.min(count - 1, target + LOOKAHEAD), 4096, false);
    });
  }

  return {
    id,
    width: cw,
    height: ch,
    duration: ix.duration,
    frameSec,
    codec: ix.codec,
    count,
    stats,
    request,
    keyTimeBelow,
    prime,
    rest() {
      teardown();
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { dropCache(); wins.length = 0; }, IDLE_DROP_MS);
    },
    destroy() { dead = true; clearTimeout(idleTimer); teardown(); dropCache(); wins.length = 0; if (provider.close) void provider.close(); },
  };
}

// ============================================================================
// scrub engine: wheel -> seconds, eased motion, decoder-first pump
// ============================================================================

const GESTURE_IDLE_MS = 260;

function deltaSeconds(deltaX, dtMs, sensitivity = 1, fine = false) {
  const dt = Math.max(4, Math.min(40, Number(dtMs) || 8));
  const rawV = Math.abs(Number(deltaX) || 0) / dt;
  const knee = 0.65;
  const v = knee * Math.asinh(rawV / knee);
  const scale = fine ? 0.002 : 0.015;
  return Math.sign(deltaX || 0) * v * dt * scale * Math.max(0.3, Math.min(3, sensitivity || 1));
}

function motionStep(pos, aim, dtMs, frameRate = 30) {
  const gap = aim - pos;
  if (Math.abs(gap) < 0.5 / Math.max(10, frameRate || 30)) return aim;
  const x = Math.max(0, Math.min(1, (Math.abs(gap) - 0.08) / 1.42));
  const blend = x * x * (3 - 2 * x);
  const tau = 46 + (18 - 46) * blend;
  const alpha = 1 - Math.exp(-Math.max(4, Math.min(40, dtMs || 8)) / tau);
  return pos + gap * alpha;
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

function fmtTime(t, frameSec, withFrames) {
  const s = Math.max(0, t || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const fps = Math.max(1, Math.round(1 / (frameSec || 1 / 30)));
  const fr = Math.floor((s - Math.floor(s)) * fps);
  const core = (h ? `${h}:${String(m).padStart(2, '0')}` : String(m)) + ':' + String(sec).padStart(2, '0');
  return withFrames ? `${core}.${String(fr).padStart(2, '0')}` : core;
}

// One Scrubber per <video> element.
class Scrubber {
  constructor(plugin, video, path, surface) {
    this.plugin = plugin;
    this.video = video;
    this.surface = surface || video;
    this.path = path;
    this.id = path;
    this.src = null;
    this.srcWhy = 'not opened yet';
    this.frameSec = 1 / 30;
    this.aim = null;
    this.pos = null;
    this.raf = 0;
    this.busy = false;
    this.ourSeek = null;
    this.lastPumpAt = 0;
    this.lastPumped = null;
    this.endTimer = 0;
    this.lastWheel = 0;
    this.primeTimer = 0;
    this.dead = false;
    this.overlay = null;
    this.canvas = null;
    this.ctx = null;
    this.hud = null;
    this.hideOnSeeked = false;
    this.hideTimer = 0;
    this.unbinders = [];
    this.#bind();
  }

  get settings() { return this.plugin.settings; }
  get duration() { const d = this.video.duration; return Number.isFinite(d) ? d : (this.src ? this.src.duration : 0); }
  get time() { return this.pos != null ? this.pos : this.video.currentTime; }

  #on(node, ev, fn, opts) {
    node.addEventListener(ev, fn, opts);
    this.unbinders.push(() => node.removeEventListener(ev, fn, opts));
  }

  #bind() {
    const v = this.video;
    v.classList.add('cth-scrub-bound');
    this.#on(v, 'seeked', () => {
      this.busy = false;
      if (this.ourSeek != null && Math.abs(v.currentTime - this.ourSeek) > 0.5) this.aim = null;
      this.ourSeek = null;
      if (this.hideOnSeeked) { this.hideOnSeeked = false; this.#hideOverlay(); }
    });
    this.#on(this.surface, 'wheel', (e) => this.#onWheel(e), { passive: false });
    this.#on(v, 'play', () => { this.stopGesture(); });
    this.#on(v, 'pause', () => { this.#primeSoon(); });
    if (this.settings.useDecoder) void this.#openDecoder();
  }

  async #openDecoder() {
    const provider = byteProvider(this.path);
    if (!provider) { this.srcWhy = 'file not readable: ' + this.path; this.plugin.diag(this); return; }
    const r = await openScrubSource(this.id, provider, cacheBytesFor(this.settings));
    if (this.dead) { if (r.src) releaseScrubSource(this.id); return; }
    this.src = r.src;
    this.srcWhy = r.src ? 'ok' : r.why;
    if (this.src) { this.frameSec = this.src.frameSec || this.frameSec; this.#primeSoon(100); }
    this.plugin.diag(this);
  }

  // ---- overlay -------------------------------------------------------------

  #ensureOverlay() {
    if (this.overlay) return;
    const o = document.body.createDiv({ cls: 'cth-scrub-overlay' });
    o.toggleClass('is-card', this.plugin.players.has(this.video) && this.plugin.settings.stageStyle !== 'edge' && !anyFull());
    this.canvas = o.createEl('canvas');
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.overlay = o;
  }

  // The overlay covers only the picture, not the element's letterbox, so the
  // area around the video stays whatever the view paints there and a scrub
  // looks like the same player, just moving.
  #pictureRect() {
    const v = this.video;
    const r = v.getBoundingClientRect();
    const pw = v.videoWidth || 16; const ph = v.videoHeight || 9;
    const scale = Math.min(r.width / pw, r.height / ph);
    const w = pw * scale; const h = ph * scale;
    return { left: r.left + (r.width - w) / 2, top: r.top + (r.height - h) / 2, width: w, height: h };
  }

  #placeOverlay() {
    this.overlay.toggleClass('is-card', this.plugin.players.has(this.video) && this.plugin.settings.stageStyle !== 'edge' && !anyFull());
    const r = this.#pictureRect();
    const o = this.overlay;
    o.style.left = r.left + 'px';
    o.style.top = r.top + 'px';
    o.style.width = r.width + 'px';
    o.style.height = r.height + 'px';
    // While annotate is zoomed in the video hangs past its card; clip the
    // overlay to clipEl so a scrub never paints outside it.
    const c = this.clipEl && this.clipEl.isConnected ? this.clipEl.getBoundingClientRect() : null;
    o.style.clipPath = c ? `inset(${Math.max(0, c.top - r.top)}px ${Math.max(0, r.left + r.width - c.right)}px ${Math.max(0, r.top + r.height - c.bottom)}px ${Math.max(0, c.left - r.left)}px)` : '';
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(r.width * dpr));
    const h = Math.max(1, Math.round(r.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
    const s = this.settings;
    o.addClass('is-live');
    return r;
  }

  #hideOverlay() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-live');
  }

  #paint(picture, t) {
    if (!this.overlay) return;
    this.#placeOverlay();
    const cw = this.canvas.width; const ch = this.canvas.height;
    try { this.ctx.drawImage(picture, 0, 0, cw, ch); } catch (_) { /* element not ready */ }
    const pl = this.plugin.players.get(this.video);
    if (pl) pl.onScrubTime(t);
  }

  // ---- gesture -------------------------------------------------------------

  #onWheel(e) {
    const s = this.settings;
    if (!s.enabled) return;
    if (e.ctrlKey || e.metaKey) return;
    const ax = Math.abs(e.deltaX); const ay = Math.abs(e.deltaY);
    let delta;
    if (s.axis === 'horizontal') { if (ax <= ay) return; delta = e.deltaX; }
    else { delta = ax >= ay ? e.deltaX : e.deltaY; }
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    const now = performance.now();
    const dt = this.lastWheel ? now - this.lastWheel : 8;
    this.lastWheel = now;
    const dir = s.reverse ? 1 : -1;
    const fine = s.fineModifier === 'shift' ? e.shiftKey : s.fineModifier === 'alt' ? e.altKey : false;
    const step = deltaSeconds(delta, dt, s.sensitivity || 1, fine);
    if (!this.video.paused) this.video.pause();
    this.aim = clamp((this.aim == null ? this.time : this.aim) + dir * step, 0, this.duration);
    if (this.pos == null) { this.pos = this.time; this.#beginGesture(); }
    this.#schedule();
    clearTimeout(this.endTimer);
    this.endTimer = setTimeout(() => this.stopGesture(), GESTURE_IDLE_MS);
  }

  // Timeline drag: the same eased, decoder-served path as a wheel gesture.
  dragTo(t) {
    if (!this.video.paused) this.video.pause();
    this.aim = clamp(t, 0, this.duration);
    if (this.pos == null) { this.pos = this.time; this.#beginGesture(); }
    this.#schedule();
    // A lost pointerup must not leave the gesture open: settle after a pause.
    clearTimeout(this.endTimer);
    this.endTimer = setTimeout(() => this.stopGesture(), 1500);
  }

  endDrag() { this.stopGesture(); }

  #beginGesture() {
    clearTimeout(this.primeTimer);
    this.hideOnSeeked = false;
    this.#ensureOverlay();
    this.#paint(this.video, this.video.currentTime);
    this.plugin.active = this;
  }

  stopGesture() {
    clearTimeout(this.endTimer);
    if (this.pos == null) return;
    const final = this.aim != null ? this.aim : this.pos;
    this.pos = null; this.aim = null;
    cancelAnimationFrame(this.raf); this.raf = 0;
    this.seek(final, true);
  }

  // Hard seek: land the element exactly, keep the overlay up until the element
  // has the frame, then drop it.
  seek(t, hard = true) {
    const target = clamp(t, 0, this.duration);
    const v = this.video;
    if (Math.abs(v.currentTime - target) > 1e-4) {
      this.busy = true; this.ourSeek = target;
      this.hideOnSeeked = !!(this.overlay && this.overlay.classList.contains('is-live'));
      v.currentTime = target;
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { if (this.pos == null) { this.hideOnSeeked = false; this.#hideOverlay(); } }, 700);
    } else {
      this.busy = false; this.ourSeek = null;
      this.#hideOverlay();
    }
    this.#primeSoon();
    void hard;
  }

  step(frames) {
    if (!this.video.paused) this.video.pause();
    this.stopGesture();
    this.seek(this.video.currentTime + frames * this.frameSec, true);
  }

  #schedule() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => this.#pump());
  }

  #pump() {
    this.raf = 0;
    if (this.dead || this.pos == null || this.aim == null) return;
    const v = this.video;
    if (!v.isConnected) { this.destroy(); return; }
    const now = performance.now();
    const dt = Math.max(4, now - (this.lastPumpAt || now - 8));
    this.pos = motionStep(this.pos, this.aim, dt, 1 / this.frameSec);
    const travel = Math.abs(this.pos - (this.lastPumped == null ? this.pos : this.lastPumped));
    const speed = (travel / this.frameSec) / (dt / 1000);
    this.lastPumped = this.pos;
    this.lastPumpAt = now;

    let served = false;
    if (this.src) {
      const tol = Math.max(this.frameSec * 1.5, travel * 1.25);
      const r = this.src.request(this.pos, speed, tol);
      if (r) { this.#paint(r.c, r.t); served = true; }
    }
    if (!served) {
      this.#paint(v, v.currentTime);
      if (!this.busy && Math.abs(this.pos - v.currentTime) >= this.frameSec / 2) {
        let t = this.pos;
        if (this.src) { const kt = this.src.keyTimeBelow(this.pos); if (kt != null && this.pos - kt <= travel) t = kt; }
        this.busy = true; this.ourSeek = t;
        v.currentTime = t;
      }
    }
    if (Math.abs(this.aim - this.pos) > 1e-4) this.#schedule();
    else { this.pos = this.aim; }
  }

  #primeSoon(delay = 350) {
    clearTimeout(this.primeTimer);
    this.primeTimer = setTimeout(() => {
      if (this.dead || !this.src || this.pos != null) return;
      if (this.video.paused && this.video.readyState >= 1) this.src.prime(this.video.currentTime);
    }, delay);
  }

  destroy() {
    if (this.dead) return;
    this.dead = true;
    clearTimeout(this.endTimer); clearTimeout(this.primeTimer); clearTimeout(this.hideTimer);
    cancelAnimationFrame(this.raf);
    for (const u of this.unbinders) u();
    this.unbinders = [];
    this.video.classList.remove('cth-scrub-bound');
    if (this.overlay) { this.overlay.remove(); this.overlay = null; }
    releaseScrubSource(this.id);
    this.src = null;
    if (this.plugin.active === this) this.plugin.active = null;
  }
}


// ============================================================================
// chapters: timestamped titles per video, stored in a sidecar note
// ============================================================================

const TAG_LINE = /^\s*-\s*(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?\s*\|\s*(.*?)\s*$/;
const TAG_TAIL = /\s*\|\s*(?:(in|out)\s+(\d{1,2}:\d{2}:\d{2}(?:\.\d{1,3})?)|(tags)\s+(#[^|]*?))\s*$/;
// "#dz-breakout": lowercase, spaces to hyphens, one leading #, letters digits - _ / only
function normalizeTag(raw) {
  const t = String(raw || '').trim().replace(/^#+/, '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-_\/]/g, '').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
  return t ? '#' + t : '';
}
// Tags are separated by "#" or commas; spaces inside a tag become hyphens, so
// "DZ Breakout, good #97 Avery" is "#dz-breakout #good #97-avery".
function parseTagList(text) {
  const out = [];
  for (const part of String(text || '').split(/,|(?=#)/)) { const t = normalizeTag(part); if (t && !out.includes(t)) out.push(t); }
  return out;
}
// The same rules applied while typing, without eating the character just typed.
function liveTagText(v) {
  let x = v.toLowerCase().replace(/,\s*/g, ' #').replace(/\s+(?!#)/g, '-').replace(/-{2,}/g, '-').replace(/[^a-z0-9\-_\/#\s]/g, '');
  x = x.replace(/(^|\s)(?!#)(?=[a-z0-9])/g, '$1#').replace(/\s+#/g, ' #').replace(/-(\s|$)/g, (m, sp) => sp === '' ? '-' : sp);
  return x.replace(/^\s+/, '');
}
// ---- clip names ----------------------------------------------------------------
// A clip name is "<clip>-<tag>-<tag>": all lowercase, hyphen separated, no #.
// The first segment is always the clip; every later segment is a tag. So
// "Breakout" with #good and #low is "breakout-good-low".
function slugName(raw) {
  return String(raw || '').toLowerCase().replace(/[#,_\s/]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
}
// The same rules while typing, keeping a trailing hyphen so the next word can follow.
function liveSlug(v) {
  return String(v || '').toLowerCase().replace(/[#,_\s/]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').replace(/^-+/, '');
}
// Words from a button's tag field: "good, low" / "#good #low" / "good low".
function parseWords(text) {
  const out = [];
  for (const w of String(text || '').split(/[\s,#]+/)) { const s = slugName(w); if (s && !out.includes(s)) out.push(s); }
  return out;
}
const nameClip = (title) => String(title || '').split('-')[0] || '';
const nameTags = (title) => String(title || '').split('-').slice(1).filter(Boolean);
// "breakout-good" + ["low"] = "breakout-good-low"; tags already present (or equal
// to the clip itself) are not repeated. remove=true takes them out instead.
function nameWithTags(title, words, remove = false, slug = true) {
  const base = slug ? slugName(title) : String(title || '').trim();
  const parts = base ? base.split('-') : [];
  const clip = parts.shift() || '';
  let tags = parts;
  for (const w0 of words) {
    const w = slugName(String(w0).replace(/^#/, ''));
    if (!w) continue;
    const wp = w.split('-');
    if (remove) {
      for (let i = 0; i + wp.length <= tags.length; i++) if (wp.every((x, k) => tags[i + k] === x)) { tags.splice(i, wp.length); break; }
    } else if (w !== clip && !(`-${tags.join('-')}-`).includes(`-${w}-`)) tags = tags.concat(wp);
  }
  if (!clip && tags.length) return tags.join('-');
  return [clip, ...tags].filter(Boolean).join('-');
}
// ---- clip names and the Tags property (Tony, 2026-10-02) ----------------------
// A clip is named after its button, then a number when that name repeats, then the day:
// "Breakout 100226", "Breakout 2 100226" (numbered in time order). Its tags are a separate
// property, written "#good #d-zone" (each tag lowercase, hyphens for spaces).
const clipTags = (tag) => (Array.isArray(tag && tag.tags) ? tag.tags : []);
const fmtTagsProp = (words) => (words || []).map((w) => `#${w}`).join(' ');
const pad2 = (n) => String(n).padStart(2, '0');
function mmddyy(d = new Date()) { return `${pad2(d.getMonth() + 1)}${pad2(d.getDate())}${pad2(d.getFullYear() % 100)}`; }
// "Breakout 2 100226" -> "Breakout" (the name the Clip filter groups by)
function clipBase(name) { return String(name || '').trim().replace(/\s\d{6}$/, '').replace(/\s\d+$/, '').trim() || String(name || '').trim(); }
const escRe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// A clip button's name: what was typed as its clip name, else its label.
function clipTitleOf(item) { const t = String(item.title || '').trim(); return t && t !== slugName(item.label) ? t : String(item.label || t || 'Clip').trim(); }
// Pre-2026-10-02 names carried the tags: "breakout-good-low". Split them using the panel's own
// clip names and tag words (longest match first); the clip part takes its button's label.
function splitLegacyName(name, known) {
  const n = String(name || '').trim();
  if (!n || /[A-Z\s]/.test(n) || !/^[a-z0-9-]+$/.test(n)) return null;
  const segs = n.split('-');
  let i = 1;
  for (let k = segs.length; k >= 1; k--) if (known.clips.has(segs.slice(0, k).join('-'))) { i = k; break; }
  const clip = segs.slice(0, i).join('-');
  const rest = segs.slice(i); const tags = [];
  for (let a = 0; a < rest.length;) {
    let took = 1;
    for (let k = rest.length - a; k >= 2; k--) if (known.tags.has(rest.slice(a, a + k).join('-'))) { took = k; break; }
    tags.push(rest.slice(a, a + took).join('-')); a += took;
  }
  const label = known.labels.get(clip);
  const title = label || (clip.charAt(0).toUpperCase() + clip.slice(1)).replace(/-/g, ' ');
  return { title, tags };
}
const parseHms = (str) => { const m = /^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/.exec(str); return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + (m[4] ? Number(m[4].padEnd(3, '0')) / 1000 : 0) : null; };

// hh:mm:ss.fff, with `decimals` fraction digits (0 to 3). The note always
// stores 3; the display truncates (like a player clock: 8.595 shows as
// 00:00:08, never 00:00:09).
let DISPLAY_DECIMALS = 1;
function fmtTag(t, decimals = DISPLAY_DECIMALS) {
  const d = Math.max(0, Math.min(3, decimals));
  const unit = Math.pow(10, d);
  const s = Math.floor(Math.max(0, t || 0) * unit + 1e-6) / unit;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const frac = Math.round((s - Math.floor(s)) * unit);
  const core = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return d ? `${core}.${String(frac).padStart(d, '0')}` : core;
}

function fmtClock(t, mode, frameSec) {
  if (mode === 'frames') return fmtTime(t, frameSec, true).replace(/^(\d+):/, (m, a) => a.padStart(2, '0') + ':');
  return fmtTag(t);
}
const fmtFull = (t) => fmtTag(t, 3);

function parseTagTime(str) {
  const m = /^\s*(?:(\d+):)?(?:(\d+):)?(\d+(?:\.\d+)?)\s*$/.exec(str || '');
  if (!m) return null;
  const parts = [m[1], m[2], m[3]].filter((x) => x != null).map(Number);
  let t = 0;
  for (const x of parts) t = t * 60 + x;
  return Number.isFinite(t) ? t : null;
}

// Annotations (freeze frames and their marks) ride in the same note, one JSON
// line per clip inside a fenced block, keyed by the clip's Start to the ms.
const ANNO_BLOCK = /```cvs-annotations\r?\n([\s\S]*?)\r?\n?```/;
// Key of each clip in the annotations block: its Start, plus "#2", "#3" ... for
// later clips that share the same Start (two buttons pressed on one frame).
function annoKeys(sortedTags) {
  const seen = new Map();
  return sortedTags.map((tg) => { const k = fmtFull(tg.t); const n = (seen.get(k) || 0) + 1; seen.set(k, n); return n > 1 ? `${k}#${n}` : k; });
}

// opts.known: the panel's clip names and tag words, to split pre-format-2 names ("breakout-good").
// Returns the clips; `tags.migrated` is true when the note needs rewriting.
function parseTagsNote(text, opts = {}) {
  const tags = [];
  let migrated = false;
  // format 2 (2026-10-02): names without tags, a "| tags #a #b" tail; older notes are split once
  const fmBlock = /^---\r?\n([\s\S]*?)\r?\n---/.exec(text || '');
  const v2 = !!fmBlock && /^format:\s*2\s*$/m.test(fmBlock[1]);
  for (const line of (text || '').split('\n')) {
    const m = TAG_LINE.exec(line);
    if (!m) continue;
    const t = Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + (m[4] ? Number(m[4].padEnd(3, '0')) / 1000 : 0);
    // trailing "| in hh:mm:ss.mmm" / "| out hh:mm:ss.mmm" fields, any order
    let title = m[5]; const tag = { t, title: '', in: null, out: null };
    let legacy = [];
    for (let k = 0; k < 3; k++) {
      const tm = TAG_TAIL.exec(title); if (!tm) break;
      if (tm[3]) legacy = parseTagList(tm[4]); else tag[tm[1]] = parseHms(tm[2]);
      title = title.slice(0, tm.index);
    }
    title = title.trim();
    tag.title = title;
    tag.tags = legacy.map((x) => x.replace(/^#/, ''));
    if (!v2 && opts.known) { const sp = splitLegacyName(title, opts.known); if (sp && (sp.title !== title || sp.tags.length)) { tag.title = sp.title; tag.tags = [...new Set([...sp.tags, ...tag.tags])]; } }
    if (!v2) migrated = true;
    tags.push(tag);
  }
  tags.sort((a, b) => a.t - b.t);
  // annotations
  const am = ANNO_BLOCK.exec(text || '');
  const orphans = [];
  if (am) {
    const keys = annoKeys(tags);
    const byKey = new Map(tags.map((tg, i) => [keys[i], tg]));
    for (const line of am[1].split('\n')) {
      const s = line.trim(); if (!s.startsWith('{')) continue;
      try {
        const o = JSON.parse(s);
        const tg = byKey.get(o.clip);
        if (tg && Array.isArray(o.freezes)) tg.freezes = o.freezes; else orphans.push(o);
      } catch (_) { /* skip a damaged line; the rest still load */ }
    }
  }
  tags.migrated = migrated;
  tags.orphans = orphans;
  return tags;
}

// The note keeps whatever other frontmatter it has (CTH Templates adds folder
// properties); only `video` and `type` are ours. Lines after the frontmatter
// that are not clip lines are dropped, since this file is generated.
function serializeTagsNote(videoVaultPath, tags, existing, orphans) {
  const own = { video: `"${videoVaultPath.replace(/"/g, '\\"')}"`, type: 'video-clips', format: '2' };
  let fm = [];
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(existing || '');
  if (m) fm = m[1].split(/\r?\n/).filter((l) => !/^(video|type|format)\s*:/.test(l));
  const lines = ['---', ...fm, `video: ${own.video}`, `type: ${own.type}`, `format: ${own.format}`, '---', ''];
  const sorted = [...tags].sort((a, b) => a.t - b.t);
  for (const tg of sorted) {
    let line = `- ${fmtFull(tg.t)} | ${tg.title || ''}`;
    if (clipTags(tg).length) line += ` | tags ${fmtTagsProp(clipTags(tg))}`;
    if (tg.in != null) line += ` | in ${fmtFull(tg.in)}`;
    if (tg.out != null) line += ` | out ${fmtFull(tg.out)}`;
    lines.push(line);
  }
  const keys = annoKeys(sorted);
  const anno = sorted.map((tg, i) => (tg.freezes && tg.freezes.length ? JSON.stringify({ clip: keys[i], freezes: tg.freezes.map(packFreeze) }) : null)).filter(Boolean);
  for (const o of orphans || []) anno.push(JSON.stringify(o));
  if (anno.length) lines.push('', '## Annotations', '', '```cvs-annotations', ...anno, '```');
  return lines.join('\n') + '\n';
}

// Round normalized coordinates so the note stays compact and diffs stay calm.
const r4 = (v) => Math.round(v * 10000) / 10000;
function packFreeze(f) {
  return { id: f.id, at: Math.round(f.at * 1000) / 1000, dur: f.dur, marks: (f.marks || []).map((mk) => { const o = { ...mk }; o.pts = (mk.pts || []).map((p) => ({ x: r4(p.x), y: r4(p.y) })); for (const k of ['r', 'tilt', 'curve', 'bw']) if (typeof o[k] === 'number') o[k] = r4(o[k]); return o; }) };
}

class ConfirmModal extends Modal {
  constructor(app, title, body, okText, onOk) { super(app); this.t = title; this.b = body; this.ok = okText; this.onOk = onOk; }
  onOpen() {
    this.titleEl.setText(this.t);
    this.contentEl.createEl('p', { text: this.b });
    const row = this.contentEl.createDiv({ cls: 'modal-button-container' });
    row.createEl('button', { text: 'Cancel' }).onclick = () => this.close();
    const b = row.createEl('button', { text: this.ok, cls: 'mod-cta' });
    b.onclick = () => { this.close(); this.onOk(); };
  }
}


// ============================================================================
// CvsMenu: a shadcn ContextMenu / DropdownMenu, drawn in the player's tokens.
// The player's own shadcn menus (the kit's Menu is only a plain fallback).
// Same call shape as Menu: addItem, addSeparator, showAtMouseEvent.
// ============================================================================

class CvsMenu {
  constructor(player) {
    this.player = player;
    this.items = [];
    this.el = null;
  }
  addItem(cb) { const it = new CvsMenuItem(); cb(it); this.items.push(it); return this; }
  addSeparator() { this.items.push({ separator: true }); return this; }
  showAtMouseEvent(e) {
    e.preventDefault();
    // a keyboard or programmatic click has no pointer position: anchor to the element
    if (!e.clientX && !e.clientY && e.currentTarget && e.currentTarget.getBoundingClientRect) return this.showBelow(e.currentTarget);
    this.showAtPosition({ x: e.clientX, y: e.clientY });
  }
  // DropdownMenu placement: under the trigger (or above it when there is no
  // room), aligned to its start or end edge.
  showBelow(anchor, align = 'start') {
    const r = anchor.getBoundingClientRect();
    this.anchor = { r, align };
    this.showAtPosition({ x: align === 'end' ? r.right : r.left, y: r.bottom + 4 });
  }
  showAtPosition(pos) {
    CvsMenu.closeOpen();
    const hasCheck = this.items.some((it) => it.checked != null);
    const el = document.body.createDiv({ cls: 'cvs-menu', attr: { role: 'menu' } });
    el.toggleClass('is-dark', !!(this.player && this.player.view.contentEl.classList.contains('is-dark')));
    el.toggleClass('has-checks', hasCheck);
    this.el = el;
    this.buttons = [];
    for (const it of this.items) {
      if (it.separator) { el.createDiv({ cls: 'cvs-menu-sep', attr: { role: 'separator' } }); continue; }
      const b = el.createDiv({ cls: 'cvs-menu-item', attr: { role: 'menuitem', tabindex: '-1' } });
      if (it.disabled) b.addClass('is-disabled');
      if (it.checked != null) { const c = b.createSpan({ cls: 'cvs-menu-check' }); if (it.checked) setIcon(c, 'check'); }
      if (it.icon) { const ic = b.createSpan({ cls: 'cvs-menu-icon' }); setIcon(ic, it.icon); }
      b.createSpan({ cls: 'cvs-menu-label', text: it.title });
      if (it.shortcut) b.createSpan({ cls: 'cvs-menu-kbd', text: it.shortcut });
      if (!it.disabled) {
        b.onclick = (ev) => { ev.stopPropagation(); this.close(); if (it.click) it.click(ev); };
        b.onmouseenter = () => this.#focus(this.buttons.indexOf(b));
        this.buttons.push(b);
      }
    }
    // place, then flip if it would leave the window
    el.style.left = pos.x + 'px'; el.style.top = pos.y + 'px';
    el.style.transform = 'none'; // measure at full size (the open transition starts scaled)
    const r = el.getBoundingClientRect();
    el.style.transform = '';
    const vw = window.innerWidth; const vh = window.innerHeight;
    if (this.anchor) {
      const a = this.anchor;
      if (a.align === 'end') el.style.left = Math.max(8, a.r.right - r.width) + 'px';
      if (r.bottom > vh - 8) el.style.top = Math.max(8, a.r.top - 4 - r.height) + 'px'; // open upward
      if (parseFloat(el.style.left) + r.width > vw - 8) el.style.left = Math.max(8, vw - 8 - r.width) + 'px';
    } else {
      if (r.right > vw - 8) el.style.left = Math.max(8, pos.x - r.width) + 'px';
      if (r.bottom > vh - 8) el.style.top = Math.max(8, Math.min(pos.y, vh - 8 - r.height)) + 'px';
    }
    requestAnimationFrame(() => el.addClass('is-open'));
    this.idx = -1;
    this.onDown = (ev) => { if (!el.contains(ev.target)) this.close(); };
    this.onKey = (ev) => {
      if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); this.close(); return; }
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') { ev.preventDefault(); ev.stopPropagation(); const n = this.buttons.length; if (!n) return; this.#focus(((this.idx < 0 ? (ev.key === 'ArrowDown' ? -1 : 0) : this.idx) + (ev.key === 'ArrowDown' ? 1 : -1) + n) % n); return; }
      if ((ev.key === 'Enter' || ev.key === ' ') && this.idx >= 0) { ev.preventDefault(); ev.stopPropagation(); this.buttons[this.idx].click(); return; }
      if (ev.key === 'Home' || ev.key === 'End') { ev.preventDefault(); this.#focus(ev.key === 'Home' ? 0 : this.buttons.length - 1); }
    };
    this.onAway = () => this.close();
    setTimeout(() => { document.addEventListener('mousedown', this.onDown, true); }, 0);
    document.addEventListener('keydown', this.onKey, true);
    window.addEventListener('resize', this.onAway);
    window.addEventListener('blur', this.onAway);
    document.addEventListener('scroll', this.onAway, true);
    CvsMenu.open = this;
    el.focus({ preventScroll: true });
  }
  #focus(i) {
    if (i < 0 || i >= this.buttons.length) return;
    this.buttons.forEach((b, k) => b.toggleClass('is-focus', k === i));
    this.idx = i;
  }
  close() {
    if (!this.el) return;
    document.removeEventListener('mousedown', this.onDown, true);
    document.removeEventListener('keydown', this.onKey, true);
    window.removeEventListener('resize', this.onAway);
    window.removeEventListener('blur', this.onAway);
    document.removeEventListener('scroll', this.onAway, true);
    const el = this.el; this.el = null;
    el.removeClass('is-open');
    setTimeout(() => el.remove(), 120);
    if (CvsMenu.open === this) CvsMenu.open = null;
  }
  static closeOpen() { if (CvsMenu.open) CvsMenu.open.close(); }
}
CvsMenu.open = null;

class CvsMenuItem {
  constructor() { this.title = ''; this.icon = null; this.checked = null; this.disabled = false; this.click = null; this.shortcut = ''; }
  setTitle(t) { this.title = t; return this; }
  setIcon(i) { this.icon = i; return this; }
  setChecked(c) { this.checked = !!c; return this; }
  setDisabled(d) { this.disabled = !!d; return this; }
  setShortcut(k) { this.shortcut = k; return this; }
  onClick(fn) { this.click = fn; return this; }
}

// ============================================================================
// Sortable: smooth drag to reorder, with the feel of shadcn Studio's Sortable 1 and 5
// (dnd-kit). A lifted copy follows the pointer with a soft shadow, the original stays as
// a faded placeholder, the other items glide aside (220 ms), lists of one group trade
// items (Sortable 5's two columns), the list scrolls near its edges, and on release the
// copy settles into its new slot. A short press still clicks: dragging starts after 5 px
// (mouse) or a 200 ms hold (touch). Escape cancels and everything glides back.
// Markup: a list has data-sort-list="<id>" (and data-sort-group to trade with others);
// its items, direct children, have data-sort-item="<id>"; an item that has a child with
// data-sort-handle drags only by that handle. data-sort-axis="y" on a list stacks it
// vertically (default x). Children with data-sort-fixed stay put but glide aside.
//   makeSortable(root, { accepts(item, list), onDrop({ id, from, fromIndex, to, toIndex }) })
// onDrop changes the data and re-renders; the copy then settles on the new element with
// the same data-sort-item. Off under prefers-reduced-motion (instant moves).
// ============================================================================

const SORT_MS = 220;
const SORT_EASE = 'cubic-bezier(0.25, 1, 0.5, 1)'; // dnd-kit's default
const sortReduce = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; } };
// The lifted copy lives on <body>, outside the styles that drew the original, so it takes
// a snapshot of every computed style (element and children).
function sortSnapshot(src, dst) {
  const cs = getComputedStyle(src);
  let txt = '';
  for (let i = 0; i < cs.length; i++) { const k = cs[i]; txt += `${k}:${cs.getPropertyValue(k)};`; }
  dst.style.cssText = txt;
  for (let i = 0; i < src.children.length; i++) if (dst.children[i]) sortSnapshot(src.children[i], dst.children[i]);
}
function sortScroller(el, axis) {
  for (let e = el; e && e !== document.body; e = e.parentElement) {
    const cs = getComputedStyle(e); const o = axis === 'x' ? cs.overflowX : cs.overflowY;
    if ((o === 'auto' || o === 'scroll') && (axis === 'x' ? e.scrollWidth > e.clientWidth : e.scrollHeight > e.clientHeight)) return e;
  }
  return null;
}

function makeSortable(root, opts) {
  let axis = 'x'; let A = null; // set per drag from the list's data-sort-axis
  const setAxis = (a) => { axis = a === 'y' ? 'y' : 'x'; A = axis === 'x' ? { pos: 'left', size: 'width', scroll: 'scrollLeft' } : { pos: 'top', size: 'height', scroll: 'scrollTop' }; };
  let pend = null; let drag = null;

  const allLists = () => [...(root.matches('[data-sort-list]') ? [root] : []), ...root.querySelectorAll('[data-sort-list]')];
  const listsFor = (fromList, itemEl) => allLists().filter((l) => l === fromList || (l.dataset.sortGroup && l.dataset.sortGroup === fromList.dataset.sortGroup && (!opts.accepts || opts.accepts(itemEl, l))));
  const kids = (list) => [...list.children].filter((k) => k.dataset.sortItem != null || k.dataset.sortFixed != null);

  function onDown(e) {
    if (drag || e.button !== 0 || e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const item = e.target.closest('[data-sort-item]');
    if (!item || !root.contains(item)) return;
    const list = item.parentElement;
    if (!list || list.dataset.sortList == null) return;
    const handle = [...item.querySelectorAll('[data-sort-handle]')].find((h) => h.closest('[data-sort-item]') === item);
    if (handle && !handle.contains(e.target)) return;
    pend = { item, list, x: e.clientX, y: e.clientY, id: e.pointerId, touch: e.pointerType !== 'mouse', t: Date.now() };
    if (pend.touch) pend.timer = setTimeout(() => { if (pend) begin(pend.x, pend.y); }, 200);
    addEventListener('pointermove', onMove, true);
    addEventListener('pointerup', onUp, true);
    addEventListener('pointercancel', onCancel, true);
    addEventListener('keydown', onKey, true);
    addEventListener('touchmove', noScroll, { capture: true, passive: false });
  }
  function noScroll(e) { if (drag) e.preventDefault(); }

  function begin(x, y) {
    const { item, list } = pend; clearTimeout(pend.timer); pend = null;
    setAxis(list.dataset.sortAxis);
    const lists = listsFor(list, item).map((l) => {
      const scroller = sortScroller(l, axis);
      const ks = kids(l).map((k) => ({ el: k, r: k.getBoundingClientRect(), fixed: k.dataset.sortItem == null }));
      const cs = getComputedStyle(l);
      let gap = parseFloat(axis === 'x' ? cs.columnGap : cs.rowGap);
      if (!(gap >= 0)) gap = ks.length > 1 ? Math.max(0, ks[1].r[A.pos] - (ks[0].r[A.pos] + ks[0].r[A.size])) : 0;
      return { el: l, rect: l.getBoundingClientRect(), scroller, s0: scroller ? scroller[A.scroll] : 0, ks, gap };
    });
    const src = lists.find((l) => l.el === list);
    const r = item.getBoundingClientRect();
    const ov = item.cloneNode(true);
    item.classList.add('is-sort-lift'); // its resting look, not hovered or pressed
    sortSnapshot(item, ov);
    item.classList.remove('is-sort-lift');
    ov.removeAttribute('id'); ov.setAttribute('aria-hidden', 'true');
    Object.assign(ov.style, { position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, margin: '0', zIndex: '2147483000', pointerEvents: 'none', transition: `transform ${SORT_MS}ms ${SORT_EASE}, box-shadow ${SORT_MS}ms ${SORT_EASE}`, transform: 'none', boxShadow: '0 1px 2px rgb(0 0 0 / 0.05)', opacity: '1', boxSizing: 'border-box' });
    document.body.appendChild(ov);
    requestAnimationFrame(() => { if (drag && drag.ov === ov) { ov.style.transform = drag.tf(); ov.style.boxShadow = '0 12px 28px -8px rgb(0 0 0 / 0.28), 0 4px 10px -4px rgb(0 0 0 / 0.14)'; } });
    const ms = sortReduce() ? 0 : SORT_MS;
    for (const l of lists) for (const k of l.ks) { k.el.style.transition = `transform ${ms}ms ${SORT_EASE}`; }
    item.classList.add('is-sort-source');
    document.documentElement.classList.add('cvs-sorting');
    drag = { item, id: item.dataset.sortItem, src, lists, r, ov, x0: x, y0: y, x, y, over: src, at: src.ks.filter((k) => !k.fixed).findIndex((k) => k.el === item), from: src.ks.filter((k) => !k.fixed).findIndex((k) => k.el === item), raf: 0, scrollRaf: 0,
      tf() { return `translate(${this.x - this.x0}px, ${this.y - this.y0}px) scale(1.02)`; } };
    layout();
    autoScroll();
  }

  // where each item ends up if the drop happened now: a cumulative walk along the axis
  function layout() {
    const d = drag; const size = d.r[A.size];
    for (const l of d.lists) {
      const scrolled = l.scroller ? l.scroller[A.scroll] - l.s0 : 0;
      // the target slot: how many movable items (not the dragged one) have their middle before the pointer
      if (l === d.over) {
        const p = (axis === 'x' ? d.x : d.y) + scrolled;
        const movable = l.ks.filter((k) => !k.fixed && k.el !== d.item);
        d.at = movable.filter((k) => k.r[A.pos] + k.r[A.size] / 2 < p).length;
      }
      const order = l.ks.filter((k) => k.el !== d.item);
      if (l === d.over) { // insert the dragged slot before the d.at-th movable item
        let seen = 0; let idx = order.length;
        for (let i = 0; i < order.length; i++) { if (!order[i].fixed) { if (seen === d.at) { idx = i; break; } seen++; } }
        order.splice(idx, 0, { el: d.item, r: d.r, slot: true });
      }
      let pos = l.ks.length ? l.ks[0].r[A.pos] : 0;
      for (const k of order) {
        if (k.slot) { if (l === d.src) d.item.style.transform = axis === 'x' ? `translateX(${pos - d.r.left}px)` : `translateY(${pos - d.r.top}px)`; pos += size + l.gap; continue; }
        const shift = pos - k.r[A.pos];
        k.el.style.transform = Math.abs(shift) < 0.5 ? '' : (axis === 'x' ? `translateX(${shift}px)` : `translateY(${shift}px)`);
        pos += k.r[A.size] + l.gap;
      }
      if (l === d.src && l !== d.over) d.item.style.transform = ''; // its slot closes; the copy carries it
    }
  }

  function pickList() {
    const d = drag;
    const hit = d.lists.find((l) => { const r = l.el.getBoundingClientRect(); const pad = 6; return d.x >= r.left - pad && d.x <= r.right + pad && d.y >= r.top - pad && d.y <= r.bottom + pad; });
    if (hit) d.over = hit;
  }

  function autoScroll() {
    const d = drag; if (!d) return;
    const sc = d.over.scroller;
    if (sc) {
      const r = sc.getBoundingClientRect(); const p = axis === 'x' ? d.x : d.y;
      const lo = axis === 'x' ? r.left : r.top; const hi = axis === 'x' ? r.right : r.bottom; const edge = 36;
      const v = p < lo + edge ? -Math.ceil((lo + edge - p) / 4) : p > hi - edge ? Math.ceil((p - (hi - edge)) / 4) : 0;
      if (v) { sc[A.scroll] += v; layout(); }
    }
    d.scrollRaf = requestAnimationFrame(autoScroll);
  }

  function onMove(e) {
    if (pend) {
      const dist = Math.hypot(e.clientX - pend.x, e.clientY - pend.y);
      if (pend.touch) { if (dist > 6) { cleanupListeners(); clearTimeout(pend.timer); pend = null; } return; }
      if (dist < 5) return;
      begin(pend.x, pend.y);
    }
    if (!drag) return;
    e.preventDefault();
    drag.x = e.clientX; drag.y = e.clientY;
    if (!drag.raf) drag.raf = requestAnimationFrame(() => { if (!drag) return; drag.raf = 0; drag.ov.style.transition = `box-shadow ${SORT_MS}ms ${SORT_EASE}`; drag.ov.style.transform = drag.tf(); pickList(); layout(); });
  }

  function cleanupListeners() {
    removeEventListener('pointermove', onMove, true);
    removeEventListener('pointerup', onUp, true);
    removeEventListener('pointercancel', onCancel, true);
    removeEventListener('keydown', onKey, true);
    removeEventListener('touchmove', noScroll, { capture: true });
  }
  // the copy glides onto `target` (an element), then everything is put back
  function settle(target) {
    const d = drag; drag = null;
    cancelAnimationFrame(d.raf); cancelAnimationFrame(d.scrollRaf);
    document.documentElement.classList.remove('cvs-sorting');
    const done = () => {
      d.ov.remove();
      for (const l of d.lists) for (const k of l.ks) { k.el.style.transition = ''; k.el.style.transform = ''; }
      d.item.classList.remove('is-sort-source'); d.item.style.transform = '';
      if (target) target.classList.remove('is-sort-source');
    };
    const to = target && target.isConnected ? target.getBoundingClientRect() : null;
    if (!to || sortReduce()) { done(); return; }
    target.classList.add('is-sort-source');
    d.ov.style.transition = `transform ${SORT_MS}ms ${SORT_EASE}, box-shadow ${SORT_MS}ms ${SORT_EASE}`;
    d.ov.style.transform = `translate(${to.left - d.r.left}px, ${to.top - d.r.top}px)`;
    d.ov.style.boxShadow = '0 1px 2px rgb(0 0 0 / 0.05)';
    setTimeout(done, SORT_MS + 20);
  }
  function swallowClick() {
    const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
    addEventListener('click', stop, true);
    setTimeout(() => removeEventListener('click', stop, true), 0);
  }

  function onUp() {
    cleanupListeners();
    if (pend) { clearTimeout(pend.timer); pend = null; return; } // a click
    if (!drag) return;
    swallowClick();
    const d = drag;
    layout();
    const same = d.over === d.src && d.at === d.from;
    if (same) { settle(d.item); return; }
    // the placeholder's slot is where the copy lands; the data change re-renders the lists
    for (const l of d.lists) for (const k of l.ks) { k.el.style.transition = 'none'; }
    try { opts.onDrop({ id: d.id, from: d.src.el.dataset.sortList, fromIndex: d.from, to: d.over.el.dataset.sortList, toIndex: d.at }); } catch (err) { console.error('[cth-video] sort', err); }
    const fresh = root.querySelector(`[data-sort-item="${CSS.escape(d.id)}"]`);
    settle(fresh);
  }
  function onCancel() {
    cleanupListeners();
    if (pend) { clearTimeout(pend.timer); pend = null; return; }
    if (!drag) return;
    for (const l of drag.lists) for (const k of l.ks) k.el.style.transform = '';
    drag.item.style.transform = '';
    settle(drag.item);
  }
  function onKey(e) { if (e.key === 'Escape' && (drag || pend)) { e.preventDefault(); e.stopPropagation(); swallowClick(); onCancel(); } }

  root.addEventListener('pointerdown', onDown);
  return { destroy() { root.removeEventListener('pointerdown', onDown); cleanupListeners(); } };
}

// ============================================================================
// ButtonPanel: rows of slim buttons above the timeline. One "clips" row adds a
// clip at the current frame; up to MAX_TAG_ROWS "tags" rows add tags to the
// last-added or selected clip. Rows are draggable, buttons are draggable,
// dividers can sit between buttons, rows can be saved to a library and loaded
// back. Layout lives in settings.panel; the library in settings.panelLibrary.
// ============================================================================

const MAX_TAG_ROWS = 5;
const PANEL_COLORS = [
  { key: '', label: 'Default' },
  { key: 'gray', label: 'Gray' },
  { key: 'red', label: 'Red' },
  { key: 'orange', label: 'Orange' },
  { key: 'amber', label: 'Amber' },
  { key: 'green', label: 'Green' },
  { key: 'teal', label: 'Teal' },
  { key: 'blue', label: 'Blue' },
  { key: 'violet', label: 'Violet' },
  { key: 'pink', label: 'Pink' },
];
const uid = () => Math.random().toString(36).slice(2, 9);

function defaultPanel() {
  return {
    rows: [
      { id: uid(), kind: 'clips', name: 'Clips', items: [
        { id: uid(), label: 'Clip', title: '' },
        { id: uid(), label: 'Goal', title: 'goal', inBuf: 8, outBuf: 4 },
        { id: uid(), label: 'Shot', title: 'shot', inBuf: 5, outBuf: 3 },
        { id: uid(), label: 'Chance', title: 'chance', inBuf: 6, outBuf: 4 },
        { id: uid(), divider: true },
        { id: uid(), label: 'Breakout', title: 'breakout', inBuf: 4, outBuf: 8 },
        { id: uid(), label: 'Entry', title: 'entry', inBuf: 4, outBuf: 6 },
        { id: uid(), label: 'Faceoff', title: 'faceoff', inBuf: 2, outBuf: 6 },
      ] },
      { id: uid(), kind: 'tags', name: 'Quality', items: [
        { id: uid(), label: 'Good', tags: ['good'] },
        { id: uid(), label: 'Bad', tags: ['bad'] },
        { id: uid(), label: 'Teach', tags: ['teach'] },
      ] },
    ],
  };
}

// Where a new clip's In/Out land: the row's buffer unless the button sets its
// own; null means "leave empty".
function bufferFor(item, row, s) {
  const pick = (k) => (item[k] != null ? item[k] : row[k] != null ? row[k] : s.panelDefaultIn != null && k === 'inBuf' ? s.panelDefaultIn : s.panelDefaultOut != null && k === 'outBuf' ? s.panelDefaultOut : null);
  return { inBuf: pick('inBuf'), outBuf: pick('outBuf') };
}

// A button's tag words, whatever shape older settings stored them in ("#good").
function btnWords(item) {
  const list = item && item.tags && item.tags.length ? item.tags : item && item.label ? [item.label] : [];
  return parseWords(list.join(' '));
}

class ButtonPanel {
  constructor(player, host) {
    this.p = player;
    this.s = player.s;
    this.host = host;
    this.el = host.createDiv({ cls: 'cvs-panel' });
    this.rowEls = new Map();
    // rows reorder by their handle; buttons and dividers reorder within a row and move between rows
    this.sorter = makeSortable(this.el, {
      accepts: (itemEl, list) => list.dataset.sortList === 'rows' || itemEl.dataset.divider != null || list.dataset.kind === itemEl.parentElement.dataset.kind,
      onDrop: ({ id, from, to, toIndex }) => {
        if (from === 'rows') this.#placeRow(id, toIndex);
        else this.#placeItem(from.slice(4), id, to.slice(4), toIndex);
      },
    });
    this.#wireTips();
    this.render();
  }

  // shadcn-style tooltip on panel buttons: the name and the keyboard shortcut (or "No shortcut").
  // Shows after 400 ms, at once while moving along the row (within 800 ms of the last), never
  // while dragging or a menu is open; above the button, below when there is no room.
  #wireTips() {
    let timer = 0; let shownAt = 0; let tip = null; let over = null;
    const hide = () => { clearTimeout(timer); if (tip) { tip.remove(); tip = null; shownAt = Date.now(); } over = null; };
    this.hideTip = hide;
    const show = (b) => {
      if (!b.isConnected || document.documentElement.classList.contains('cvs-sorting') || document.querySelector('.cvs-menu')) return;
      const host = this.p.view?.contentEl || document.body;
      tip = host.createDiv({ cls: 'cvs-tooltip', attr: { role: 'tooltip' } });
      tip.createSpan({ text: b.dataset.label || b.textContent });
      if (b.dataset.hotkey) tip.createEl('kbd', { cls: 'cvs-tooltip-kbd', text: b.dataset.hotkey.toUpperCase() });
      else tip.createSpan({ cls: 'cvs-tooltip-none', text: 'No shortcut' });
      const r = b.getBoundingClientRect(); const t = tip.getBoundingClientRect();
      const left = Math.max(4, Math.min(innerWidth - t.width - 4, r.left + r.width / 2 - t.width / 2));
      const above = r.top - t.height - 6 >= 4;
      tip.style.left = `${left}px`; tip.style.top = `${above ? r.top - t.height - 8 : r.bottom + 8}px`;
      tip.style.setProperty('--arrow-x', `${Math.max(10, Math.min(t.width - 10, r.left + r.width / 2 - left))}px`);
      tip.toggleClass('is-below', !above);
    };
    this.el.addEventListener('pointerover', (e) => {
      const b = e.target.closest?.('.cvs-pbtn');
      if (!b || b === over || this.s.panelTooltips === false || e.pointerType === 'touch') return;
      hide(); over = b;
      timer = setTimeout(() => show(b), Date.now() - shownAt < 800 ? 0 : 100);
    });
    this.el.addEventListener('pointerout', (e) => { const b = e.target.closest?.('.cvs-pbtn'); if (b && !b.contains(e.relatedTarget)) hide(); });
    this.el.addEventListener('pointerdown', hide, true);
    this.el.addEventListener('contextmenu', hide, true);
  }

  get panel() {
    if (!this.s.panel || !Array.isArray(this.s.panel.rows)) this.s.panel = defaultPanel();
    return this.s.panel;
  }
  get rows() { return this.panel.rows; }
  save() { this.p.plugin.saveSoon(); }
  rerenderAll() { for (const pl of this.p.plugin.players.values()) if (pl.panel) pl.panel.render(); }

  // ---- render ----------------------------------------------------------------

  render() {
    const el = this.el;
    this.hideTip?.();
    el.empty();
    this.rowEls.clear();
    el.dataset.sortList = 'rows'; el.dataset.sortAxis = 'y';
    el.toggleClass('is-hidden', !this.s.panelOpen);
    const rows = this.rows;
    rows.forEach((row, ri) => {
      const rowEl = el.createDiv({ cls: `cvs-prow is-${row.kind}` });
      rowEl.dataset.id = row.id;
      rowEl.dataset.sortItem = row.id;
      this.rowEls.set(row.id, rowEl);
      // handle + kind icon + name (drag rows by the handle)
      const head = rowEl.createDiv({ cls: 'cvs-prow-head', attr: { 'data-sort-handle': '', 'aria-label': `${row.kind === 'clips' ? 'Clips row' : 'Tags row'}: drag to reorder` } });
      const kind = head.createSpan({ cls: 'cvs-prow-kind' });
      setIcon(kind, row.kind === 'clips' ? 'film' : 'tag');
      head.createSpan({ cls: 'cvs-prow-name', text: row.name || (row.kind === 'clips' ? 'Clips' : 'Tags') });
      head.oncontextmenu = (e) => { e.preventDefault(); this.#rowMenu(e, row); };
      head.ondblclick = () => this.#renameRow(row);

      // scrollable strip
      const strip = rowEl.createDiv({ cls: 'cvs-pstrip', attr: { 'data-sort-list': `row:${row.id}`, 'data-sort-group': 'items', 'data-kind': row.kind } });
      strip.onwheel = (e) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && strip.scrollWidth > strip.clientWidth) { strip.scrollLeft += e.deltaY; e.preventDefault(); } };
      row.items.forEach((item) => strip.appendChild(this.#itemEl(row, item)));
      strip.oncontextmenu = (e) => { if (e.target === strip) { e.preventDefault(); this.#rowMenu(e, row); } };

      // tail: new button, settings
      const tail = rowEl.createDiv({ cls: 'cvs-prow-tail' });
      const add = this.#iconBtn(tail, 'plus', 'New button', () => this.#quickAdd(row));
      add.oncontextmenu = (e) => { e.preventDefault(); const m = new CvsMenu(this.p); m.addItem((x) => x.setTitle('New button').setIcon('plus').onClick(() => this.#quickAdd(row))); m.addItem((x) => x.setTitle('New divider').setIcon('separator-vertical').onClick(() => { row.items.push({ id: uid(), divider: true }); this.save(); this.render(); })); m.showAtMouseEvent(e); };
      this.#iconBtn(tail, 'settings-2', 'Row settings', () => new RowSettingsModal(this.p.app, this, row).open());
    });
    // add-row affordance (only while under the cap)
    const foot = el.createDiv({ cls: 'cvs-panel-foot' });
    const tagRows = rows.filter((r) => r.kind === 'tags').length;
    const addRow = this.#iconBtn(foot, 'plus', 'Add row', (e) => this.#addRowMenu(e, addRow));
    addRow.addClass('cvs-panel-addrow');
    addRow.createSpan({ text: tagRows >= MAX_TAG_ROWS && rows.some((r) => r.kind === 'clips') ? 'Rows' : 'Add row' });
  }

  #iconBtn(parent, icon, tip, fn) {
    const b = parent.createEl('button', { cls: 'cvs-btn cvs-pbtn-icon clickable-icon', attr: { 'aria-label': tip } });
    setIcon(b, icon);
    b.onclick = (e) => { e.preventDefault(); e.stopPropagation(); fn(e); };
    return b;
  }

  #itemEl(row, item) {
    if (item.divider) {
      const d = createDiv({ cls: 'cvs-pdiv', attr: { 'data-sort-item': item.id, 'data-divider': '' } });
      d.dataset.id = item.id;
      d.oncontextmenu = (e) => { e.preventDefault(); e.stopPropagation(); this.#dividerMenu(e, row, item); };
      return d;
    }
    const b = createEl('button', { cls: 'cvs-pbtn', text: item.label || 'Button', attr: { 'data-sort-item': item.id } });
    b.dataset.id = item.id;
    if (item.color) b.dataset.color = item.color;
    b.dataset.label = item.label || 'Button';
    if (item.hotkey) {
      b.dataset.hotkey = item.hotkey; b.setAttr('aria-label', `${item.label}  (${item.hotkey})`);
      if (this.s.panelKeyBadges !== false) { b.addClass('has-key'); b.prepend(createSpan({ cls: 'cvs-pbtn-kbd', text: item.hotkey.toUpperCase(), attr: { 'aria-hidden': 'true' } })); }
    }
    b.onclick = (e) => { e.preventDefault(); this.fire(row, item, e); };
    b.oncontextmenu = (e) => { e.preventDefault(); e.stopPropagation(); this.#itemMenu(e, row, item); };
    return b;
  }

  // ---- actions ---------------------------------------------------------------

  fire(row, item, e) {
    if (row.kind === 'clips') {
      const { inBuf, outBuf } = bufferFor(item, row, this.s);
      const tag = this.p.addClipFromButton({ title: clipTitleOf(item), tags: btnWords({ tags: item.tags || [] }), inBuf, outBuf, edit: !!item.editOnAdd });
      if (tag && item.color) tag.color = item.color;
      if (tag) { this.p.flash(item.label ? `Clip: ${item.label}` : 'Clip added'); }
    } else {
      const words = btnWords(item);
      const remove = !!(e && (e.altKey || e.shiftKey));
      const tag = this.p.addTagsToTarget(words, remove);
      if (tag) this.p.flash(`${tag.title || 'Clip'}  ${fmtTagsProp(clipTags(tag))}`.trim());
    }
  }

  #quickAdd(row, at = null) {
    const item = row.kind === 'clips'
      ? { id: uid(), label: 'New', title: 'New', tags: [] }
      : { id: uid(), label: 'new', tags: ['new'] };
    if (at == null) row.items.push(item); else row.items.splice(at, 0, item);
    this.save(); this.render();
    new ButtonSettingsModal(this.p.app, this, row, item, true).open();
  }

  #removeItem(row, item) { const i = row.items.indexOf(item); if (i >= 0) row.items.splice(i, 1); this.save(); this.render(); }

  // a sorted drop: toIndex counts the target row's items without the moved one
  #placeItem(fromRowId, itemId, toRowId, toIndex) {
    const from = this.rows.find((r) => r.id === fromRowId); const to = this.rows.find((r) => r.id === toRowId);
    if (!from || !to) return;
    const i = from.items.findIndex((x) => x.id === itemId); if (i < 0) return;
    const item = from.items[i];
    if (!item.divider && from.kind !== to.kind) { this.p.flash(from.kind === 'clips' ? 'Clip buttons stay in the Clips row' : 'Tag buttons stay in Tags rows'); return; }
    from.items.splice(i, 1);
    to.items.splice(Math.max(0, Math.min(toIndex, to.items.length)), 0, item);
    this.save(); this.rerenderAll();
  }
  #placeRow(id, toIndex) {
    const rows = this.rows; const i = rows.findIndex((r) => r.id === id); if (i < 0) return;
    const [row] = rows.splice(i, 1);
    rows.splice(Math.max(0, Math.min(toIndex, rows.length)), 0, row);
    this.save(); this.rerenderAll();
  }

  #renameRow(row) {
    new TextPromptModal(this.p.app, 'Row name', row.name || '', (v) => { row.name = v.trim() || row.name; this.save(); this.render(); }).open();
  }

  addRow(kind, name) {
    const rows = this.rows;
    if (kind === 'clips' && rows.some((r) => r.kind === 'clips')) { this.p.flash('Only one Clips row'); return null; }
    if (kind === 'tags' && rows.filter((r) => r.kind === 'tags').length >= MAX_TAG_ROWS) { this.p.flash(`Up to ${MAX_TAG_ROWS} tag rows`); return null; }
    const row = { id: uid(), kind, name: name || (kind === 'clips' ? 'Clips' : 'Tags'), items: [] };
    if (kind === 'clips') rows.unshift(row); else rows.push(row);
    this.save(); this.render();
    return row;
  }

  removeRow(row, keepSaved = true) {
    if (keepSaved && row.items.length) this.saveRowToLibrary(row, true);
    const i = this.rows.indexOf(row); if (i >= 0) this.rows.splice(i, 1);
    this.save(); this.render();
  }

  // ---- library ---------------------------------------------------------------

  get library() { if (!Array.isArray(this.s.panelLibrary)) this.s.panelLibrary = []; return this.s.panelLibrary; }

  saveRowToLibrary(row, quiet) {
    const copy = JSON.parse(JSON.stringify(row)); copy.savedAt = Date.now(); delete copy.id;
    const lib = this.library;
    const k = lib.findIndex((r) => r.name === copy.name && r.kind === copy.kind);
    if (k >= 0) lib[k] = copy; else lib.push(copy);
    this.save();
    if (!quiet) this.p.flash(`Saved row "${copy.name}"`);
  }

  loadRowFromLibrary(saved) {
    const row = JSON.parse(JSON.stringify(saved)); delete row.savedAt;
    row.id = uid(); row.items = (row.items || []).map((it) => ({ ...it, id: uid() }));
    if (row.kind === 'clips') { const cur = this.rows.find((r) => r.kind === 'clips'); if (cur) { if (cur.items.length) this.saveRowToLibrary(cur, true); this.rows.splice(this.rows.indexOf(cur), 1); } this.rows.unshift(row); }
    else { if (this.rows.filter((r) => r.kind === 'tags').length >= MAX_TAG_ROWS) { this.p.flash(`Up to ${MAX_TAG_ROWS} tag rows; remove one first`); return; } this.rows.push(row); }
    this.save(); this.render();
  }

  // ---- menus -----------------------------------------------------------------

  #addRowMenu(e, anchor) {
    const m = new CvsMenu(this.p);
    const hasClips = this.rows.some((r) => r.kind === 'clips');
    const tagRows = this.rows.filter((r) => r.kind === 'tags').length;
    m.addItem((x) => x.setTitle('New Tags row').setIcon('tag').setDisabled(tagRows >= MAX_TAG_ROWS).onClick(() => { const r = this.addRow('tags'); if (r) this.#renameRow(r); }));
    m.addItem((x) => x.setTitle('New Clips row').setIcon('film').setDisabled(hasClips).onClick(() => this.addRow('clips')));
    const lib = this.library;
    if (lib.length) {
      m.addSeparator();
      for (const saved of lib) m.addItem((x) => x.setTitle(`Load "${saved.name}"`).setIcon(saved.kind === 'clips' ? 'film' : 'tag').setDisabled(saved.kind === 'tags' && tagRows >= MAX_TAG_ROWS).onClick(() => this.loadRowFromLibrary(saved)));
      m.addSeparator();
      m.addItem((x) => x.setTitle('Manage saved rows').setIcon('library').onClick(() => new LibraryModal(this.p.app, this).open()));
    }
    m.addSeparator();
    m.addItem((x) => x.setTitle('Reset panel to defaults').setIcon('rotate-ccw').onClick(() => new ConfirmModal(this.p.app, 'Reset the button panel?', 'Current rows are saved to the library first, then the default rows come back.', 'Reset', () => { for (const r of this.rows) if (r.items.length) this.saveRowToLibrary(r, true); this.s.panel = defaultPanel(); this.save(); this.render(); }).open()));
    m.showBelow(anchor, 'start');
  }

  #rowMenu(e, row) {
    const m = new CvsMenu(this.p);
    m.addItem((x) => x.setTitle('Row settings').setIcon('settings-2').onClick(() => new RowSettingsModal(this.p.app, this, row).open()));
    m.addItem((x) => x.setTitle('Rename row').setIcon('pencil').onClick(() => this.#renameRow(row)));
    m.addSeparator();
    m.addItem((x) => x.setTitle('New button').setIcon('plus').onClick(() => this.#quickAdd(row)));
    m.addItem((x) => x.setTitle('New divider').setIcon('separator-vertical').onClick(() => { row.items.push({ id: uid(), divider: true }); this.save(); this.render(); }));
    m.addSeparator();
    const ri = this.rows.indexOf(row);
    m.addItem((x) => x.setTitle('Move row up').setIcon('arrow-up').setDisabled(ri <= 0).onClick(() => this.#placeRow(row.id, ri - 1)));
    m.addItem((x) => x.setTitle('Move row down').setIcon('arrow-down').setDisabled(ri >= this.rows.length - 1).onClick(() => this.#placeRow(row.id, ri + 1)));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Save row to library').setIcon('save').onClick(() => this.saveRowToLibrary(row)));
    m.addItem((x) => x.setTitle('Duplicate row').setIcon('copy').setDisabled(row.kind === 'clips' || this.rows.filter((r) => r.kind === 'tags').length >= MAX_TAG_ROWS).onClick(() => { const copy = JSON.parse(JSON.stringify(row)); copy.id = uid(); copy.name = row.name + ' copy'; copy.items.forEach((it) => { it.id = uid(); }); this.rows.splice(this.rows.indexOf(row) + 1, 0, copy); this.save(); this.render(); }));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Remove row (kept in library)').setIcon('archive').onClick(() => this.removeRow(row, true)));
    m.addItem((x) => x.setTitle('Delete row').setIcon('trash').onClick(() => new ConfirmModal(this.p.app, `Delete row "${row.name}"?`, 'The row and its buttons are removed and not saved to the library.', 'Delete', () => this.removeRow(row, false)).open()));
    m.showAtMouseEvent(e);
  }

  // ---- right-click menus (buttons, dividers, rows) ------------------------------------------
  #moveWithin(row, item, to) { const i = row.items.indexOf(item); if (i < 0) return; row.items.splice(i, 1); row.items.splice(Math.max(0, Math.min(to, row.items.length)), 0, item); this.save(); this.rerenderAll(); }
  #moveItems(m, row, item) { // move left / right / to start / to end
    const i = row.items.indexOf(item); const last = row.items.length - 1;
    m.addItem((x) => x.setTitle('Move left').setIcon('chevron-left').setDisabled(i <= 0).onClick(() => this.#moveWithin(row, item, i - 1)));
    m.addItem((x) => x.setTitle('Move right').setIcon('chevron-right').setDisabled(i >= last).onClick(() => this.#moveWithin(row, item, i + 1)));
    m.addItem((x) => x.setTitle('Move to start').setIcon('chevrons-left').setDisabled(i <= 0).onClick(() => this.#moveWithin(row, item, 0)));
    m.addItem((x) => x.setTitle('Move to end').setIcon('chevrons-right').setDisabled(i >= last).onClick(() => this.#moveWithin(row, item, last)));
  }
  // a second menu at the same spot listing the other rows a button or divider can go to
  #pickRow(e, row, item, copy) {
    const rows = this.rows.filter((r) => r !== row && (item.divider || r.kind === row.kind));
    const m = new CvsMenu(this.p);
    m.addItem((x) => x.setTitle(copy ? 'Copy to row' : 'Move to row').setDisabled(true));
    for (const r of rows) m.addItem((x) => x.setTitle(r.name || (r.kind === 'clips' ? 'Clips' : 'Tags')).setIcon(r.kind === 'clips' ? 'film' : 'tag').onClick(() => {
      if (copy) r.items.push({ ...JSON.parse(JSON.stringify(item)), id: uid() });
      else { row.items.splice(row.items.indexOf(item), 1); r.items.push(item); }
      this.save(); this.rerenderAll();
      this.p.flash(`${copy ? 'Copied' : 'Moved'} to ${r.name || 'row'}`);
    }));
    m.showAtPosition({ x: e.clientX, y: e.clientY });
  }
  #otherRows(row, item) { return this.rows.some((r) => r !== row && (item.divider || r.kind === row.kind)); }

  #itemMenu(e, row, item) {
    const m = new CvsMenu(this.p);
    const at = () => row.items.indexOf(item);
    m.addItem((x) => x.setTitle('Edit button').setIcon('pencil').onClick(() => new ButtonSettingsModal(this.p.app, this, row, item).open()));
    m.addItem((x) => x.setTitle('Rename').setIcon('type').onClick(() => new TextPromptModal(this.p.app, 'Button label', item.label || '', (v) => { if (!v.trim()) return; const was = item.label; item.label = v.trim(); if (row.kind === 'clips' && (!item.title || item.title === was)) item.title = item.label; if (row.kind === 'tags' && (!item.tags || !item.tags.length || btnWords({ tags: item.tags }).join(' ') === slugName(was))) item.tags = [slugName(item.label)]; this.save(); this.render(); }).open()));
    m.addItem((x) => x.setTitle('Color').setIcon('palette').onClick(() => { const cm = new CvsMenu(this.p); for (const c of PANEL_COLORS) cm.addItem((y) => y.setTitle(c.label).setChecked((item.color || '') === c.key).onClick(() => { if (c.key) item.color = c.key; else delete item.color; this.save(); this.rerenderAll(); })); cm.showAtPosition({ x: e.clientX, y: e.clientY }); }));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Duplicate').setIcon('copy').onClick(() => { row.items.splice(at() + 1, 0, { ...JSON.parse(JSON.stringify(item)), id: uid() }); this.save(); this.rerenderAll(); }));
    m.addItem((x) => x.setTitle('Copy to row...').setIcon('copy-plus').setDisabled(!this.#otherRows(row, item)).onClick(() => this.#pickRow(e, row, item, true)));
    m.addItem((x) => x.setTitle('Move to row...').setIcon('arrow-right-left').setDisabled(!this.#otherRows(row, item)).onClick(() => this.#pickRow(e, row, item, false)));
    m.addSeparator();
    m.addItem((x) => x.setTitle('New button after').setIcon('plus').onClick(() => this.#quickAdd(row, at() + 1)));
    m.addItem((x) => x.setTitle('Insert divider before').setIcon('separator-vertical').onClick(() => { row.items.splice(at(), 0, { id: uid(), divider: true }); this.save(); this.rerenderAll(); }));
    m.addItem((x) => x.setTitle('Insert divider after').setIcon('separator-vertical').onClick(() => { row.items.splice(at() + 1, 0, { id: uid(), divider: true }); this.save(); this.rerenderAll(); }));
    m.addSeparator();
    this.#moveItems(m, row, item);
    m.addSeparator();
    m.addItem((x) => x.setTitle('Delete button').setIcon('trash').onClick(() => this.#removeItem(row, item)));
    m.showAtMouseEvent(e);
  }

  #dividerMenu(e, row, item) {
    const m = new CvsMenu(this.p);
    const at = () => row.items.indexOf(item);
    m.addItem((x) => x.setTitle('Duplicate divider').setIcon('copy').onClick(() => { row.items.splice(at() + 1, 0, { id: uid(), divider: true }); this.save(); this.rerenderAll(); }));
    m.addItem((x) => x.setTitle('New button after').setIcon('plus').onClick(() => this.#quickAdd(row, at() + 1)));
    m.addItem((x) => x.setTitle('Move to row...').setIcon('arrow-right-left').setDisabled(!this.#otherRows(row, item)).onClick(() => this.#pickRow(e, row, item, false)));
    m.addSeparator();
    this.#moveItems(m, row, item);
    m.addSeparator();
    m.addItem((x) => x.setTitle('Remove divider').setIcon('trash').onClick(() => this.#removeItem(row, item)));
    m.showAtMouseEvent(e);
  }

  // hotkey dispatch: single printable keys assigned to buttons
  handleKey(e) {
    if (!this.s.panelOpen || e.metaKey || e.ctrlKey || e.altKey) return false;
    const k = e.key.length === 1 ? e.key : '';
    if (!k) return false;
    for (const row of this.rows) for (const item of row.items) if (!item.divider && item.hotkey && item.hotkey.toLowerCase() === k.toLowerCase() && (item.hotkey === k || item.hotkey.length === 1)) { this.fire(row, item, e); return true; }
    return false;
  }

  destroy() { this.hideTip?.(); this.el.remove(); }
}

// ---- modals ------------------------------------------------------------------

class TextPromptModal extends Modal {
  constructor(app, title, value, onOk) { super(app); this.t = title; this.v = value; this.onOk = onOk; }
  onOpen() {
    this.titleEl.setText(this.t);
    const input = this.contentEl.createEl('input', { type: 'text', cls: 'cvs-modal-input', value: this.v });
    input.focus(); input.select();
    const done = () => { this.close(); this.onOk(input.value); };
    input.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); done(); } };
    const row = this.contentEl.createDiv({ cls: 'modal-button-container' });
    row.createEl('button', { text: 'Cancel' }).onclick = () => this.close();
    row.createEl('button', { text: 'OK', cls: 'mod-cta' }).onclick = done;
  }
}

// Full control over one row: name, kind (fixed), buffers that apply to every
// button, plus a table of buttons with per-button fields and a "fill down"
// on the buffer columns.
class RowSettingsModal extends Modal {
  constructor(app, panel, row) { super(app); this.panel = panel; this.row = row; }
  onOpen() {
    this.modalEl.addClass('cvs-modal', 'cvs-modal-wide');
    const row = this.row; const panel = this.panel;
    this.titleEl.setText(`${row.kind === 'clips' ? 'Clips row' : 'Tags row'}: ${row.name}`);
    const c = this.contentEl;
    new Setting(c).setName('Row name').addText((t) => t.setValue(row.name).onChange((v) => { row.name = v; }));
    if (row.kind === 'clips') {
      new Setting(c).setName('In buffer (seconds before Start)').setDesc('Applied to every button in this row that has no buffer of its own. Blank leaves In empty.').addText((t) => t.setPlaceholder('blank').setValue(row.inBuf == null ? '' : String(row.inBuf)).onChange((v) => { row.inBuf = v.trim() === '' ? null : Math.max(0, Number(v) || 0); }));
      new Setting(c).setName('Out buffer (seconds after Start)').addText((t) => t.setPlaceholder('blank').setValue(row.outBuf == null ? '' : String(row.outBuf)).onChange((v) => { row.outBuf = v.trim() === '' ? null : Math.max(0, Number(v) || 0); }));
      new Setting(c).setName('Open the clip name for editing after adding').setDesc('Off: the clip lands with the button title and you keep watching.').addToggle((t) => t.setValue(!!row.editOnAdd).onChange((v) => { row.editOnAdd = v; row.items.forEach((it) => { if (!it.divider) it.editOnAdd = v; }); }));
    } else {
      new Setting(c).setName('Behavior').setDesc('Clicking a tag button adds its tags to the end of the selected clip\'s name (else the last-added clip), like "breakout" to "breakout-good". Shift-click removes them instead.');
    }
    c.createEl('h4', { text: 'Buttons' });
    const table = c.createDiv({ cls: 'cvs-btable', attr: { 'data-sort-list': 'btable', 'data-sort-axis': 'y' } });
    for (const it of row.items) if (!it.id) it.id = uid();
    // drag a row by its grip to reorder the buttons (the same smooth sort as the panel)
    makeSortable(table, { onDrop: ({ id, toIndex }) => { const i = row.items.findIndex((x) => x.id === id); if (i < 0) return; const [m] = row.items.splice(i, 1); row.items.splice(toIndex, 0, m); this.#renderRows(table, row); } });
    const head = table.createDiv({ cls: 'cvs-btable-row is-head' });
    const cols = row.kind === 'clips' ? ['', 'Label', 'Clip name', 'Tags added', 'In', 'Out', 'Color', 'Key', ''] : ['', 'Label', 'Tags added', 'Color', 'Key', ''];
    table.style.setProperty('--cols', row.kind === 'clips' ? '20px 1fr 1fr 1fr 52px 52px 84px 40px 28px' : '20px 1fr 1.6fr 84px 40px 28px');
    cols.forEach((h) => head.createSpan({ text: h }));
    const fill = (key) => {
      const first = row.items.find((it) => !it.divider && it[key] != null);
      const v = first ? first[key] : null;
      row.items.forEach((it) => { if (!it.divider) { if (v == null) delete it[key]; else it[key] = v; } });
      this.#renderRows(table, row);
    };
    this.fill = fill;
    this.#renderRows(table, row);
    const foot = c.createDiv({ cls: 'cvs-btable-foot' });
    foot.createEl('button', { text: 'Add button' }).onclick = () => { row.items.push(row.kind === 'clips' ? { id: uid(), label: 'New', title: 'new', tags: [] } : { id: uid(), label: 'new', tags: ['new'] }); this.#renderRows(table, row); };
    foot.createEl('button', { text: 'Add divider' }).onclick = () => { row.items.push({ id: uid(), divider: true }); this.#renderRows(table, row); };
    if (row.kind === 'clips') {
      foot.createEl('button', { text: 'Fill In down' }).onclick = () => fill('inBuf');
      foot.createEl('button', { text: 'Fill Out down' }).onclick = () => fill('outBuf');
      foot.createEl('button', { text: 'Clear all buffers' }).onclick = () => { row.items.forEach((it) => { delete it.inBuf; delete it.outBuf; }); this.#renderRows(table, row); };
    }
    foot.createEl('button', { text: 'Fill color down' }).onclick = () => fill('color');
    const bar = c.createDiv({ cls: 'modal-button-container' });
    bar.createEl('button', { text: 'Save row to library' }).onclick = () => panel.saveRowToLibrary(row);
    bar.createEl('button', { text: 'Done', cls: 'mod-cta' }).onclick = () => this.close();
  }
  #renderRows(table, row) {
    table.querySelectorAll('.cvs-btable-row:not(.is-head)').forEach((r) => r.remove());
    row.items.forEach((it, i) => {
      const r = table.createDiv({ cls: 'cvs-btable-row' + (it.divider ? ' is-divider' : ''), attr: { 'data-sort-item': it.id } });
      const grip = r.createSpan({ cls: 'cvs-btable-grip', attr: { 'data-sort-handle': '', 'aria-label': 'Drag to reorder' } }); setIcon(grip, 'grip-vertical');
      // right-click a row: duplicate, insert, move, delete
      r.oncontextmenu = (e) => {
        if (e.target.closest('input, select')) return; // the text box keeps its own menu (copy, paste)
        e.preventDefault();
        const at = () => row.items.indexOf(it); const redo = () => this.#renderRows(table, row);
        const m = new CvsMenu(this.panel.p);
        m.addItem((x) => x.setTitle(it.divider ? 'Duplicate divider' : 'Duplicate').setIcon('copy').onClick(() => { row.items.splice(at() + 1, 0, { ...JSON.parse(JSON.stringify(it)), id: uid() }); redo(); }));
        m.addItem((x) => x.setTitle('Insert button below').setIcon('plus').onClick(() => { row.items.splice(at() + 1, 0, row.kind === 'clips' ? { id: uid(), label: 'New', title: 'new', tags: [] } : { id: uid(), label: 'new', tags: ['new'] }); redo(); }));
        m.addItem((x) => x.setTitle('Insert divider below').setIcon('separator-vertical').onClick(() => { row.items.splice(at() + 1, 0, { id: uid(), divider: true }); redo(); }));
        m.addSeparator();
        m.addItem((x) => x.setTitle('Move up').setIcon('arrow-up').setDisabled(at() <= 0).onClick(() => { const i = at(); row.items.splice(i, 1); row.items.splice(i - 1, 0, it); redo(); }));
        m.addItem((x) => x.setTitle('Move down').setIcon('arrow-down').setDisabled(at() >= row.items.length - 1).onClick(() => { const i = at(); row.items.splice(i, 1); row.items.splice(i + 1, 0, it); redo(); }));
        m.addSeparator();
        m.addItem((x) => x.setTitle(it.divider ? 'Delete divider' : 'Delete button').setIcon('trash').onClick(() => { row.items.splice(at(), 1); redo(); }));
        m.showAtMouseEvent(e);
      };
      if (it.divider) {
        const span = r.createSpan({ text: 'divider', cls: 'cvs-btable-divlabel' });
        span.style.gridColumn = `2 / ${row.kind === 'clips' ? 9 : 6}`;
      } else {
        const text = (key, ph, norm) => { const inp = r.createEl('input', { type: 'text', value: it[key] == null ? '' : (Array.isArray(it[key]) ? btnWords({ tags: it[key] }).join(' ') : String(it[key])), attr: { placeholder: ph || '' } }); inp.onchange = () => { const v = inp.value; if (norm) it[key] = norm(v); else if (v.trim() === '') delete it[key]; else it[key] = v; if (key === 'label' && row.kind === 'tags' && (!it.tags || !it.tags.length)) it.tags = [slugName(v)]; }; return inp; };
        text('label', 'Label');
        if (row.kind === 'clips') text('title', 'Clip name', (v) => v.trim());
        text('tags', row.kind === 'clips' ? 'tags added' : 'tag', (v) => parseWords(v));
        if (row.kind === 'clips') {
          const num = (key) => { const inp = r.createEl('input', { type: 'text', value: it[key] == null ? '' : String(it[key]), attr: { placeholder: 'row' } }); inp.onchange = () => { const v = inp.value.trim(); if (v === '') delete it[key]; else it[key] = Math.max(0, Number(v) || 0); }; };
          num('inBuf'); num('outBuf');
        }
        const sel = r.createEl('select'); for (const cc of PANEL_COLORS) { const o = sel.createEl('option', { text: cc.label, value: cc.key }); if ((it.color || '') === cc.key) o.selected = true; } sel.onchange = () => { if (sel.value) it.color = sel.value; else delete it.color; };
        const hk = r.createEl('input', { type: 'text', value: it.hotkey || '', attr: { maxlength: '1', placeholder: '' } }); hk.onchange = () => { const v = hk.value.trim(); if (v) it.hotkey = v; else delete it.hotkey; };
      }
      const del = r.createEl('button', { cls: 'clickable-icon' }); setIcon(del, 'x'); del.onclick = () => { row.items.splice(i, 1); this.#renderRows(table, row); };
    });
  }
  onClose() { this.panel.save(); this.panel.render(); }
}

class ButtonSettingsModal extends Modal {
  constructor(app, panel, row, item, isNew) { super(app); this.panel = panel; this.row = row; this.item = item; this.isNew = isNew; }
  onOpen() {
    this.modalEl.addClass('cvs-modal');
    const { row, item } = this;
    this.titleEl.setText(this.isNew ? 'New button' : `Button: ${item.label}`);
    const c = this.contentEl;
    let labelInput;
    new Setting(c).setName('Label').addText((t) => { labelInput = t; t.setValue(item.label || '').onChange((v) => { item.label = v; }); });
    if (row.kind === 'clips') {
      new Setting(c).setName('Clip name').setDesc('What the new clip is called, before the number and the day ("Breakout" makes "Breakout 100226"). Blank uses the label.').addText((t) => t.setValue(item.title || '').onChange((v) => { item.title = v.trim(); }));
      new Setting(c).setName('Tags added').setDesc('Put in the new clip\'s Tags, like "pp" adds #pp.').addText((t) => t.setPlaceholder('pp').setValue(btnWords({ tags: item.tags }).join(' ')).onChange((v) => { item.tags = parseWords(v); }));
      new Setting(c).setName('In buffer (seconds before Start)').setDesc('Blank uses the row buffer.').addText((t) => t.setPlaceholder('row').setValue(item.inBuf == null ? '' : String(item.inBuf)).onChange((v) => { if (v.trim() === '') delete item.inBuf; else item.inBuf = Math.max(0, Number(v) || 0); }));
      new Setting(c).setName('Out buffer (seconds after Start)').addText((t) => t.setPlaceholder('row').setValue(item.outBuf == null ? '' : String(item.outBuf)).onChange((v) => { if (v.trim() === '') delete item.outBuf; else item.outBuf = Math.max(0, Number(v) || 0); }));
      new Setting(c).setName('Open the clip name for editing after adding').addToggle((t) => t.setValue(!!item.editOnAdd).onChange((v) => { item.editOnAdd = v; }));
    } else {
      new Setting(c).setName('Tags').setDesc('Added to the selected clip\'s Tags (else the last-added clip), like "good" adds #good. Blank uses the label. Alt or Shift-click takes it out.').addText((t) => t.setPlaceholder('good').setValue(btnWords({ tags: item.tags }).join(' ')).onChange((v) => { item.tags = parseWords(v); }));
    }
    new Setting(c).setName('Color').addDropdown((d) => { for (const cc of PANEL_COLORS) d.addOption(cc.key, cc.label); d.setValue(item.color || '').onChange((v) => { if (v) item.color = v; else delete item.color; }); });
    new Setting(c).setName('Hotkey').setDesc('One key, pressed while the video is focused. Leave blank for none. Avoid keys the player uses (space, T, I, O, J, K, L, S, R, M, F).').addText((t) => t.setValue(item.hotkey || '').onChange((v) => { const k = v.trim().slice(0, 1); if (k) item.hotkey = k; else delete item.hotkey; }));
    const bar = c.createDiv({ cls: 'modal-button-container' });
    if (this.isNew) bar.createEl('button', { text: 'Cancel' }).onclick = () => { const i = row.items.indexOf(item); if (i >= 0) row.items.splice(i, 1); this.cancelled = true; this.close(); };
    bar.createEl('button', { text: 'Done', cls: 'mod-cta' }).onclick = () => this.close();
    setTimeout(() => { if (labelInput) { labelInput.inputEl.focus(); labelInput.inputEl.select(); } }, 30);
  }
  onClose() {
    const { row, item } = this;
    if (!this.cancelled) {
      if (!item.label) item.label = row.kind === 'clips' ? (item.title || 'Clip') : ((item.tags && item.tags[0]) || 'tag').replace(/^#/, '');
      if (row.kind === 'tags' && (!item.tags || !item.tags.length)) item.tags = [slugName(item.label)];
    }
    this.panel.save(); this.panel.render();
  }
}

class LibraryModal extends Modal {
  constructor(app, panel) { super(app); this.panel = panel; }
  onOpen() {
    this.modalEl.addClass('cvs-modal');
    this.titleEl.setText('Saved rows');
    this.#list();
  }
  #list() {
    const c = this.contentEl; c.empty();
    const lib = this.panel.library;
    if (!lib.length) c.createEl('p', { text: 'No saved rows yet. Use "Save row to library" on any row.' });
    for (const saved of lib) {
      new Setting(c).setName(saved.name).setDesc(`${saved.kind === 'clips' ? 'Clips row' : 'Tags row'}, ${saved.items.filter((i) => !i.divider).length} buttons`)
        .addButton((b) => b.setButtonText('Load').onClick(() => { this.panel.loadRowFromLibrary(saved); this.close(); }))
        .addButton((b) => b.setIcon('trash').setTooltip('Delete saved row').onClick(() => { lib.splice(lib.indexOf(saved), 1); this.panel.save(); this.#list(); }));
    }
  }
}

// Every player key, in groups (the player's own sheet and the app's Settings > Shortcuts read this).
function shortcutGroups(s) {
  return [
    ['Playback', [['Space', 'Play / pause'], ['Left / Right', `Step ${s.stepFrames} frame${s.stepFrames === 1 ? '' : 's'}`], ['Shift + Left / Right', `Step ${s.stepFramesShift} frames`], ['J / L', `Skip ${s.skipSmall} s back / forward`], ['Shift + J / L', `Skip ${s.skipLarge} s back / forward`], ['Home / End', 'Start / end'], ['- / =', 'Slower / faster'], ['0', 'Normal speed'], ['R', 'Loop on / off'], ['M', 'Mute'], ['F', 'Fullscreen']]],
    ['Clips', [['T', 'Add clip at the current frame'], ['Up / Down', 'Previous / next clip'], ['Enter', 'Rename the selected clip'], ['Tab', 'Next cell: Clip, Start, In, Out'], ['X', 'Tick / untick the selected clip for export'], ['E', 'Export the ticked clips (or the selected one)'], ['A', 'Annotate the selected clip'], ['Backspace', 'Delete the selected clip'], ['I / O', 'Set In / Out of the selected clip to the current frame'], ['Shift + I / O', 'Clear In / Out'], ['Cmd + Z', 'Undo the last clip change'], ['Esc', 'Cancel an edit / clear selection']]],
    ['Annotate (the timeline is the clip, freezes are real pauses)', [['Space', 'Play / pause the clip with its freezes'], ['P', 'Play from the start'], ['Draw on any frame', 'Freezes it there automatically'], ['F', 'Freeze / unfreeze the current frame'], ['Left / Right', 'Frame by frame (a freeze in one step)'], ['Up / Down', 'Previous / next freeze'], ['Drag a freeze block', 'Move it; drag its right edge to change its length'], ['V  Z', 'Select, zoom'], ['A  D  S  W', 'Arrow, pass, shoot, skate with the puck'], ['Q  E', 'Line, pen'], ['1  2', 'Spotlight, beam'], ['R  C  H  B', 'Zone, circle, focus, wall'], ['N  T', 'Note, label chip'], ['3  4', 'Players (right-click for number and color)'], ['Pinch / Cmd + scroll', 'Zoom at the pointer; scroll or drag to pan; Cmd + 0 fits'], ['Square handle', 'Resize the selected mark'], ['Alt + drag', 'Copy a mark'], ['Arrows', 'Nudge the selected mark (Shift x10)'], ['Enter / double-click', 'Edit its text'], ['Cmd + D / C / V', 'Duplicate, copy, paste marks'], ['Cmd + Z / Shift + Cmd + Z', 'Undo / redo'], ['Cmd + E', 'Export this clip and return'], ['Esc', 'Pause, Select tool, deselect, fit zoom, then done']]],
    ['Button panel', [['Click a Clips button', 'New clip with that name and In/Out buffers'], ['Click a Tags button', 'Appends its tag to the selected (or last-added) clip\'s name'], ['Shift + click a Tags button', 'Removes that tag from the name'], ['Right-click a button', 'Edit, color, duplicate, copy or move to a row, divider, delete'], ['Right-click a row', 'Settings, rename, new button, move, duplicate, delete'], ['Hover a button', 'Shows its keyboard shortcut'], ['Drag a button or a row handle', 'Rearrange'], ['B', 'Show / hide the panel']]],
    ['Window', [['S', 'Show / hide the clip sidebar'], ['?', 'This list']]],
    ['Trackpad', [['Two-finger swipe on the video', 'Scrub'], [`${s.fineModifier === 'alt' ? 'Option' : 'Shift'} + swipe`, 'Fine scrub, frame by frame'], ['Drag the timeline', 'Scrub']]],
  ];
}

class ShortcutsModal extends Modal {
  constructor(app, plugin) { super(app); this.plugin = plugin; }
  onOpen() {
    const s = this.plugin.settings;
    this.titleEl.setText('Player shortcuts');
    const groups = shortcutGroups(s);
    const wrap = this.contentEl.createDiv({ cls: 'cvs-keys' });
    for (const [name, rows] of groups) {
      wrap.createEl('h4', { text: name });
      const t = wrap.createEl('table');
      for (const [k, d] of rows) { const tr = t.createEl('tr'); tr.createEl('td', { text: k, cls: 'cvs-key' }); tr.createEl('td', { text: d }); }
    }
  }
}

// ============================================================================
// annotate: freeze frames with telestration marks, and the mp4 exporter.
// The drawing vocabulary is CTH Studio's marks.js (one renderer paints the
// editor, the preview and every exported frame, so they can never disagree),
// plus Diagrams-style player discs and rink items, and note boxes for players.
// Geometry is normalized 0..1 of the picture; sizes are fractions of its height.
// ============================================================================

const ANNO_COLORS = {
  red:    { base: '#ff2d2d', lo: '#b30000', hi: '#ff8a8a', ink: '#ffffff' },
  yellow: { base: '#ffd21e', lo: '#c79500', hi: '#fff3a8', ink: '#1a1400' },
  orange: { base: '#ff8c1a', lo: '#c25a00', hi: '#ffc98a', ink: '#2a1400' },
  green:  { base: '#2ecc4a', lo: '#0f7a24', hi: '#a6f5b4', ink: '#04230b' },
  cyan:   { base: '#14b8ff', lo: '#00619a', hi: '#a6e6ff', ink: '#001a26' }, // the default: reads on white ice
  white:  { base: '#ffffff', lo: '#9aa0a6', hi: '#ffffff', ink: '#0a0a0a' },
  black:  { base: '#101010', lo: '#000000', hi: '#4a4a4a', ink: '#ffffff' },
};
const ANNO_COLOR_KEYS = Object.keys(ANNO_COLORS);
const annoPal = (c) => ANNO_COLORS[c] || ANNO_COLORS.red;
// Player disc colors (CTH Diagrams palette); INK is the ring.
const INK = '#1e1e1e';
const PLAYER_FILLS = [['Black', '#1e1e1e'], ['Blue', '#75d8ff'], ['Grey', '#d9d9d9'], ['White', '#ffffff'], ['Red', '#dc2626'], ['Green', '#16a34a'], ['Orange', '#f59e0b'], ['Purple', '#8b5cf6']];
const PLAYER_LABELS = ['1', '2', '3', '4', '5', 'C', 'LW', 'RW', 'LD', 'RD', 'F1', 'F2', 'F3', 'D1', 'D2', 'G', 'X', 'O'];
const MDEF = { spot: 0.055, beamH: 0.28, arrowW: 0.013, penW: 0.008, label: 0.038, player: 0.045 };
const ITEM_SIZE = { puck: 0.02, cone: 0.05, net: 0.1, coach: 0.065 };
const NOTE_FS = { s: 0.026, m: 0.033, l: 0.043, xl: 0.058 };
const ANNO_FONT = '"Geist Variable", Inter, -apple-system, "Helvetica Neue", sans-serif';
const IN_DUR = 0.26;
const OUT_DUR = 0.2;
const annoImgs = {}; // name -> HTMLImageElement (same origin, so export canvases never taint)

function loadAnnoImages() {
  for (const n of ['cone', 'net', 'coach']) {
    if (annoImgs[n]) continue;
    const img = new Image(); img.src = `assets/${n}.png`; annoImgs[n] = img;
  }
}

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOutCubic = (v) => 1 - Math.pow(1 - clamp01(v), 3);
function easeOutBack(v) { const c1 = 1.70158; const c3 = c1 + 1; const x = clamp01(v); return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); }

function hexA(hex, a) {
  const h = String(hex || '#000').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((x) => x + x).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
function luminance(hex) {
  const h = String(hex || '#000').replace('#', ''); const n = parseInt(h.length === 3 ? h.split('').map((x) => x + x).join('') : h, 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

// 0 before a mark exists, 1 while it is up, eased at both ends (Studio).
function markPhase(m, t) {
  const t0 = m.t0 ?? 0; const t1 = m.t1 ?? t0 + 2;
  if (t < t0 - 1e-6 || t > t1 + 1e-6) return null;
  if (m.anim === 'none') return { alpha: 1, grow: 1, scale: 1, rise: 1 };
  const inD = Math.min(IN_DUR, (t1 - t0) * 0.4) || 1e-6;
  const outD = Math.min(OUT_DUR, (t1 - t0) * 0.3) || 1e-6;
  const rise = clamp01((t - t0) / inD);
  const fall = clamp01((t1 - t) / outD);
  const grow = m.anim === 'draw' ? easeOutCubic(rise) : 1;
  const scale = m.anim === 'pop' ? 1 + 0.12 * (1 - easeOutBack(rise)) : 1;
  return { alpha: Math.min(easeOutCubic(rise), easeOutCubic(fall)), grow, scale, rise };
}

const defaultAnim = (kind) => (kind === 'arrow' || kind === 'line' || kind === 'pen' || kind === 'barrier' ? 'draw' : kind === 'shade' || kind === 'zone' ? 'fade' : 'pop');

// view: { toX(nx), toY(ny), scale (px per 1.0 of frame height), x0, y0, w, h }
function drawMark(ctx, m, t, view, opts = {}) {
  let p = markPhase(m, t);
  if (opts.solid) p = { alpha: 1, grow: 1, scale: 1, rise: 1 };
  if (!p || p.alpha <= 0.002) return;
  const pts = (m.pts || []).map((q) => ({ x: view.toX(q.x), y: view.toY(q.y) }));
  if (!pts.length) return;
  const c = annoPal(m.color); const S = view.scale;
  ctx.save();
  ctx.globalAlpha = p.alpha;
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  switch (m.kind) {
    case 'spot': drawSpot(ctx, m, pts, c, S, p); break;
    case 'beam': drawBeam(ctx, m, pts, c, S, p); break;
    case 'arrow': drawArrow(ctx, m, pts, c, S, p, true); break;
    case 'line': drawArrow(ctx, m, pts, c, S, p, false); break;
    case 'zone': drawZone(ctx, m, pts, c, S, p); break;
    case 'barrier': drawBarrier(ctx, m, pts, c, S, p); break;
    case 'label': drawLabel(ctx, m, pts, c, S, p); break;
    case 'pen': drawPen(ctx, m, pts, c, S, p); break;
    case 'shade': drawShade(ctx, m, pts, c, S, p, view); break;
    case 'player': drawPlayer(ctx, m, pts, S, p); break;
    case 'item': drawItem(ctx, m, pts, S, p); break;
    case 'note': drawNote(ctx, m, pts, c, S, p, view); break;
    default: break;
  }
  ctx.restore();
}

function ellipseFor(m, pt, S, mul = 1) {
  const r = (m.r ?? MDEF.spot) * S * (m.w || 1) * mul;
  const ny = m.tilt != null ? m.tilt : clamp01(pt.yN ?? 0.5);
  const squash = 0.3 + 0.28 * clamp01(ny);
  return { rx: r, ry: r * squash };
}

function drawSpot(ctx, m, pts, c, S, p) {
  const pt = pts[0];
  pt.yN = m.tilt != null ? m.tilt : (m.pts[0] ? m.pts[0].y : 0.5);
  const { rx, ry } = ellipseFor(m, pt, S, p.scale);
  const lw = Math.max(1.5, 0.009 * S * (m.w || 1));
  const g = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, rx);
  g.addColorStop(0, hexA(c.base, 0.32)); g.addColorStop(0.72, hexA(c.base, 0.14)); g.addColorStop(1, hexA(c.base, 0));
  ctx.save();
  ctx.translate(pt.x, pt.y); ctx.scale(1, ry / rx); ctx.translate(-pt.x, -pt.y);
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(pt.x, pt.y, rx, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(pt.x, pt.y, rx, ry, 0, 0, Math.PI * 2);
  ctx.lineWidth = lw * 2.1; ctx.strokeStyle = hexA('#000000', 0.35); ctx.stroke();
  ctx.lineWidth = lw; ctx.strokeStyle = c.base; ctx.stroke();
  if (m.text) chip(ctx, m.text, pt.x, pt.y + ry + 0.028 * S, c, S, 0.85);
}

function drawBeam(ctx, m, pts, c, S, p) {
  const pt = pts[0];
  pt.yN = m.tilt != null ? m.tilt : (m.pts[0] ? m.pts[0].y : 0.5);
  const { rx } = ellipseFor(m, pt, S, p.scale);
  const h = (m.h ?? MDEF.beamH) * S * p.grow;
  const g = ctx.createLinearGradient(0, pt.y - h, 0, pt.y);
  g.addColorStop(0, hexA(c.base, 0)); g.addColorStop(0.55, hexA(c.base, 0.16)); g.addColorStop(1, hexA(c.base, 0.34));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(pt.x - rx * 0.82, pt.y); ctx.lineTo(pt.x - rx * 0.62, pt.y - h);
  ctx.lineTo(pt.x + rx * 0.62, pt.y - h); ctx.lineTo(pt.x + rx * 0.82, pt.y);
  ctx.closePath(); ctx.fill();
  drawSpot(ctx, { ...m, text: null }, pts, c, S, p);
  if (m.text) chip(ctx, m.text, pt.x, pt.y - h - 0.012 * S, c, S, 0.9);
}

// Evenly spaced points along a path (by length), with unit normals.
function resamplePath(pts, step) {
  const out = []; if (pts.length < 2) return out;
  let acc = 0; let next = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]; const b = pts[i]; const d = Math.hypot(b.x - a.x, b.y - a.y); if (!d) continue;
    const nx = -(b.y - a.y) / d; const ny = (b.x - a.x) / d;
    while (next <= acc + d) { const r = (next - acc) / d; out.push({ x: a.x + (b.x - a.x) * r, y: a.y + (b.y - a.y) * r, nx, ny, s: next }); next += step; }
    acc += d;
  }
  const L = pts[pts.length - 1]; const P = out[out.length - 1];
  if (!P || Math.hypot(L.x - P.x, L.y - P.y) > 0.5) { const q = pts[pts.length - 2]; const d = Math.hypot(L.x - q.x, L.y - q.y) || 1; out.push({ x: L.x, y: L.y, nx: -(L.y - q.y) / d, ny: (L.x - q.x) / d, s: acc }); }
  return out;
}

// Hockey notation on the Studio arrow (CTH Diagrams conventions): pass =
// dashed, shoot = the doubled line, puck = a tight shallow squiggle (carrying
// the puck). All keep the taper-free body, bevel, shadow and arrowhead.
function motionBodies(body, motion, w) {
  if (motion === 'shoot') {
    const rs = resamplePath(body, Math.max(1, w * 0.5)); const off = w * 0.95;
    return [rs.map((q) => ({ x: q.x + q.nx * off, y: q.y + q.ny * off })), rs.map((q) => ({ x: q.x - q.nx * off, y: q.y - q.ny * off }))];
  }
  if (motion === 'puck') {
    const rs = resamplePath(body, Math.max(1, w * 0.35)); const total = rs.length ? rs[rs.length - 1].s : 0;
    const amp = w * 1.35; const wave = w * 5.5;
    return [rs.map((q) => { const fade = Math.min(1, q.s / (wave * 0.45)) * Math.min(1, (total - q.s) / (wave * 0.5)); const off = Math.sin((q.s / wave) * Math.PI * 2) * amp * Math.max(0, fade); return { x: q.x + q.nx * off, y: q.y + q.ny * off }; })];
  }
  return null;
}

// Tapered, bevelled, shadowed (Studio). `head` false draws a plain line.
function drawArrow(ctx, m, pts, c, S, p, withHead) {
  const path = curvePoints(pts, m.curve ?? 0);
  const grown = takeFraction(path, p.grow);
  if (grown.length < 2) return;
  const w = (m.aw ?? MDEF.arrowW) * S * (m.w || 1);
  const shoot = m.motion === 'shoot';
  const headL = withHead ? w * (shoot ? 3.8 : 3.4) : 0;
  const total = pathLength(grown);
  if (total < Math.max(2, headL * 1.05)) return;
  const body = withHead ? trimEnd(grown, headL * 0.82) : grown;
  const bodies = motionBodies(body, m.motion, w);
  if (bodies) {
    // decorated bodies: constant width strokes, same four layers as the plain arrow
    const bw = shoot ? w * 0.62 : w * 0.78;
    const layer = (offY, style, widen) => { ctx.save(); ctx.translate(0, offY); ctx.strokeStyle = style; ctx.lineWidth = Math.max(1, bw + widen); for (const b of bodies) { if (b.length < 2) continue; ctx.beginPath(); ctx.moveTo(b[0].x, b[0].y); for (let i = 1; i < b.length; i++) ctx.lineTo(b[i].x, b[i].y); ctx.stroke(); } ctx.restore(); };
    const tip = grown[grown.length - 1]; const before = grown[grown.length - 2] || body[body.length - 1];
    const ang = Math.atan2(tip.y - before.y, tip.x - before.x);
    const hw = shoot ? w * 1.9 : w * 1.5;
    const head = (offY, style, widen = 0) => { if (!withHead) return; ctx.save(); ctx.translate(tip.x, tip.y + offY); ctx.rotate(ang); ctx.fillStyle = style; ctx.beginPath(); ctx.moveTo(headL * 0.34 + widen, 0); ctx.lineTo(-headL * 0.72 - widen * 0.4, -hw - widen); ctx.lineTo(-headL * 0.44, 0); ctx.lineTo(-headL * 0.72 - widen * 0.4, hw + widen); ctx.closePath(); ctx.fill(); ctx.restore(); };
    layer(w * 0.55, hexA('#000000', 0.3), w * 0.25); head(w * 0.55, hexA('#000000', 0.3), w * 0.2);
    layer(0, c.lo, w * 0.34); head(0, c.lo, w * 0.22);
    layer(0, c.base, 0); head(0, c.base);
    layer(-w * 0.16, hexA(c.hi, 0.5), -bw * 0.55);
    if (m.text) chip(ctx, m.text, body[0].x, body[0].y - w * 2.6, c, S, 0.85);
    return;
  }
  const tip = grown[grown.length - 1];
  const before = grown[grown.length - 2] || body[body.length - 1];
  const ang = Math.atan2(tip.y - before.y, tip.x - before.x);
  const drawBody = (offY, style, widen = 0) => {
    ctx.save(); ctx.translate(0, offY); ctx.strokeStyle = style;
    if (m.dash) { ctx.setLineDash([w * 1.9, w * 1.35]); ctx.lineDashOffset = 0; }
    if (!withHead || m.dash) {
      ctx.beginPath(); ctx.moveTo(body[0].x, body[0].y);
      for (let i = 1; i < body.length; i++) ctx.lineTo(body[i].x, body[i].y);
      ctx.lineWidth = w + widen; ctx.stroke();
    } else {
      const n = body.length - 1;
      for (let i = 0; i < n; i++) {
        const f = i / Math.max(1, n - 1);
        ctx.beginPath(); ctx.moveTo(body[i].x, body[i].y); ctx.lineTo(body[i + 1].x, body[i + 1].y);
        ctx.lineWidth = (w * (1.18 - 0.3 * f)) + widen; ctx.stroke();
      }
    }
    ctx.restore();
  };
  const head = (offY, style, widen = 0) => {
    if (!withHead) return;
    ctx.save(); ctx.translate(tip.x, tip.y + offY); ctx.rotate(ang); ctx.fillStyle = style;
    ctx.beginPath();
    ctx.moveTo(headL * 0.34 + widen, 0);
    ctx.lineTo(-headL * 0.72 - widen * 0.4, -w * 1.5 - widen);
    ctx.lineTo(-headL * 0.44, 0);
    ctx.lineTo(-headL * 0.72 - widen * 0.4, w * 1.5 + widen);
    ctx.closePath(); ctx.fill(); ctx.restore();
  };
  drawBody(w * 0.55, hexA('#000000', 0.3), w * 0.25); head(w * 0.55, hexA('#000000', 0.3), w * 0.2);
  drawBody(0, c.lo, w * 0.34); head(0, c.lo, w * 0.22);
  drawBody(0, c.base); head(0, c.base);
  drawBody(-w * 0.22, hexA(c.hi, 0.55), -w * 0.5);
  if (m.text) chip(ctx, m.text, body[0].x, body[0].y - w * 2.2, c, S, 0.85);
}

function zonePath(ctx, m, pts) {
  ctx.beginPath();
  if (m.shape === 'ellipse' && pts.length >= 2) {
    const cx = (pts[0].x + pts[1].x) / 2; const cy = (pts[0].y + pts[1].y) / 2;
    ctx.ellipse(cx, cy, Math.abs(pts[1].x - pts[0].x) / 2, Math.abs(pts[1].y - pts[0].y) / 2, 0, 0, Math.PI * 2);
  } else if (pts.length === 2) {
    ctx.rect(Math.min(pts[0].x, pts[1].x), Math.min(pts[0].y, pts[1].y), Math.abs(pts[1].x - pts[0].x), Math.abs(pts[1].y - pts[0].y));
  } else {
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
  }
}

function drawZone(ctx, m, pts, c, S, p) {
  if (pts.length < 2) return;
  zonePath(ctx, m, pts);
  ctx.fillStyle = hexA(c.base, 0.2 * p.grow); ctx.fill();
  const lw = Math.max(1.5, 0.007 * S * (m.w || 1));
  ctx.lineWidth = lw * 2; ctx.strokeStyle = hexA('#000000', 0.3); ctx.stroke();
  ctx.lineWidth = lw; ctx.strokeStyle = c.base;
  if (m.dash) ctx.setLineDash([0.018 * S, 0.012 * S]);
  ctx.stroke();
  if (m.text) {
    const cx = pts.reduce((a, q) => a + q.x, 0) / pts.length; const cy = pts.reduce((a, q) => a + q.y, 0) / pts.length;
    chip(ctx, m.text, cx, cy, c, S, 0.9);
  }
}

function drawBarrier(ctx, m, pts, c, S, p) {
  if (pts.length < 2) return;
  const a = pts[0]; const b = pts[1];
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y) * p.grow;
  const th = (m.th ?? 0.032) * S * (m.w || 1);
  if (len < 2) return;
  ctx.save(); ctx.translate(a.x, a.y); ctx.rotate(ang);
  ctx.beginPath(); ctx.rect(0, -th / 2, len, th);
  ctx.save(); ctx.clip();
  ctx.fillStyle = hexA(c.base, 0.9); ctx.fillRect(0, -th / 2, len, th);
  const step = th * 0.66;
  ctx.fillStyle = hexA(m.alt ? (ANNO_COLORS[m.alt] || ANNO_COLORS.yellow).base : (m.color === 'yellow' ? '#101010' : '#ffe14d'), 0.95);
  for (let x = -th; x < len + th; x += step * 2) {
    ctx.beginPath(); ctx.moveTo(x, th / 2); ctx.lineTo(x + step, th / 2); ctx.lineTo(x + step + th, -th / 2); ctx.lineTo(x + th, -th / 2); ctx.closePath(); ctx.fill();
  }
  ctx.restore();
  ctx.lineWidth = Math.max(1.2, 0.004 * S); ctx.strokeStyle = hexA('#000000', 0.45); ctx.stroke();
  ctx.restore();
  if (m.text) chip(ctx, m.text, (a.x + b.x) / 2, (a.y + b.y) / 2 - th, c, S, 0.85);
}

// Label chips are drawn at 1.3x the caption size so they read like the other tools.
const LABEL_SCALE = 1.3;
function drawLabel(ctx, m, pts, c, S, p) {
  const pt = pts[0];
  if (pts.length > 1) leader(ctx, pt, pts[1], c, S, (m.w || 1) * 1.35);
  chip(ctx, m.text || 'LABEL', pt.x, pt.y, c, S, p.scale * (m.w || 1) * LABEL_SCALE, m.solid !== false);
}

function leader(ctx, from, to, c, S, k = 1) {
  ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y);
  ctx.lineWidth = Math.max(2, 0.006 * S * k); ctx.strokeStyle = hexA('#000000', 0.45); ctx.stroke();
  ctx.lineWidth = Math.max(1.2, 0.0034 * S * k); ctx.strokeStyle = c.base; ctx.stroke();
  const r = Math.max(3, 0.0065 * S * k);
  ctx.beginPath(); ctx.arc(to.x, to.y, r, 0, Math.PI * 2);
  ctx.fillStyle = c.base; ctx.fill();
  ctx.lineWidth = Math.max(1, 0.0025 * S); ctx.strokeStyle = hexA('#000000', 0.55); ctx.stroke();
}

function drawPen(ctx, m, pts, c, S, p) {
  const path = takeFraction(pts, p.grow);
  if (path.length < 2) return;
  const w = (m.aw ?? MDEF.penW) * S * (m.w || 1);
  ctx.beginPath(); ctx.moveTo(path[0].x, path[0].y);
  for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
  if (m.dash) ctx.setLineDash([w * 2.2, w * 1.6]);
  ctx.lineWidth = w * 2.2; ctx.strokeStyle = hexA('#000000', 0.32); ctx.stroke();
  ctx.lineWidth = w; ctx.strokeStyle = c.base; ctx.stroke();
}

function drawShade(ctx, m, pts, c, S, p, view) {
  ctx.save();
  ctx.fillStyle = hexA('#000000', (m.amount ?? 0.55) * (m.anim === 'draw' ? p.grow : 1));
  ctx.beginPath();
  ctx.rect(view.x0, view.y0, view.w, view.h);
  if (pts.length === 2) {
    const x = Math.min(pts[0].x, pts[1].x); const y = Math.min(pts[0].y, pts[1].y);
    const w = Math.abs(pts[1].x - pts[0].x); const h = Math.abs(pts[1].y - pts[0].y);
    if (m.shape === 'ellipse') ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2, true);
    else { ctx.moveTo(x, y); ctx.lineTo(x, y + h); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w, y); ctx.closePath(); }
  }
  ctx.fill('evenodd');
  ctx.restore();
}

// Diagrams player: a filled disc with a dark ring and a 1-2 character label,
// plus a soft shadow so it reads over white ice and dark boards alike.
function drawPlayer(ctx, m, pts, S, p) {
  const pt = pts[0];
  const r = (m.r ?? MDEF.player) * S * (m.w || 1) * p.scale;
  const fill = m.fill || INK;
  ctx.save();
  ctx.shadowColor = hexA('#000000', 0.45); ctx.shadowBlur = r * 0.5; ctx.shadowOffsetY = r * 0.12;
  ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill();
  ctx.restore();
  ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(2, r * 0.18); ctx.strokeStyle = INK; ctx.stroke();
  if (m.label) {
    ctx.fillStyle = luminance(fill) > 0.55 ? INK : '#ffffff';
    ctx.font = `800 ${Math.round(r * (m.label.length > 1 ? 0.82 : 1.0))}px ${ANNO_FONT}`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(m.label, pt.x, pt.y + r * 0.05);
  }
  if (m.text) chip(ctx, m.text, pt.x, pt.y + r + 0.03 * S, annoPal(m.color), S, 0.8);
}

function itemBox(m, pt, S) {
  const base = (m.s ?? ITEM_SIZE[m.item] ?? 0.05) * S * (m.w || 1);
  if (m.item === 'net') return { w: base * 0.57, h: base };
  return { w: base, h: base };
}

function drawItem(ctx, m, pts, S, p) {
  const pt = pts[0];
  const { w, h } = itemBox(m, pt, S);
  const sw = w * p.scale; const sh = h * p.scale;
  ctx.save();
  ctx.shadowColor = hexA('#000000', 0.4); ctx.shadowBlur = Math.max(2, sh * 0.15); ctx.shadowOffsetY = sh * 0.05;
  if (m.item === 'puck') {
    ctx.beginPath(); ctx.ellipse(pt.x, pt.y, sw / 2, sw / 2 * 0.62, 0, 0, Math.PI * 2); ctx.fillStyle = '#0d0d0d'; ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = Math.max(1, sw * 0.08); ctx.strokeStyle = hexA('#ffffff', 0.55); ctx.stroke();
  } else {
    const img = annoImgs[m.item];
    if (img && img.complete && img.naturalWidth) {
      if (m.flip) { ctx.translate(pt.x, pt.y); ctx.scale(-1, 1); ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh); }
      else ctx.drawImage(img, pt.x - sw / 2, pt.y - sh / 2, sw, sh);
    } else { ctx.beginPath(); ctx.arc(pt.x, pt.y, sw / 2, 0, Math.PI * 2); ctx.fillStyle = '#ff8c1a'; ctx.fill(); }
  }
  ctx.restore();
  if (m.text) chip(ctx, m.text, pt.x, pt.y + sh / 2 + 0.025 * S, annoPal(m.color), S, 0.8);
}

// ---- note boxes: multi-line coaching notes for players ------------------------
// pts[0] is the box's top-left; pts[1] (optional) is what the leader points at.
// Styles: dark (default), light, color (filled with the mark color), clear.
// The first line can be a heading ("#18 Avery"), drawn bold in the accent.
function noteLayout(ctx, m, pts, S, view) {
  const fs = (NOTE_FS[m.fs || 'm'] || NOTE_FS.m) * S * (m.w || 1);
  const maxW = Math.max(fs * 4, (m.bw ?? 0.3) * view.w);
  const pad = fs * 0.62;
  const lines = [];
  const raw = String(m.text || '').split('\n');
  ctx.save();
  raw.forEach((para, pi) => {
    const head = pi === 0 && m.head !== false && raw.length > 1;
    ctx.font = `${head ? 800 : 560} ${fs * (head ? 1.04 : 1)}px ${ANNO_FONT}`;
    const words = para.split(/\s+/).filter((w) => w.length);
    if (!words.length) { lines.push({ text: '', head, w: 0 }); return; }
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (ctx.measureText(test).width <= maxW - pad * 2 || !cur) cur = test;
      else { lines.push({ text: cur, head, w: ctx.measureText(cur).width }); cur = w; }
    }
    if (cur) lines.push({ text: cur, head, w: ctx.measureText(cur).width });
  });
  ctx.restore();
  const lh = fs * 1.32;
  const textW = Math.max(fs * 2, ...lines.map((l) => l.w));
  const bw = Math.min(maxW, textW + pad * 2 + (m.style === 'clear' ? 0 : fs * 0.22));
  const bh = Math.max(lh, lines.length * lh) + pad * 1.5;
  return { x: pts[0].x, y: pts[0].y, w: bw, h: bh, fs, pad, lh, lines };
}

function drawNote(ctx, m, pts, c, S, p, view) {
  const L = noteLayout(ctx, m, pts, S, view);
  const style = m.style || 'dark';
  const cx = L.x + L.w / 2; const cy = L.y + L.h / 2;
  ctx.save();
  if (p.scale !== 1) { ctx.translate(cx, cy); ctx.scale(p.scale, p.scale); ctx.translate(-cx, -cy); }
  if (pts.length > 1) {
    const to = pts[1];
    const from = { x: clamp(to.x, L.x, L.x + L.w), y: clamp(to.y, L.y, L.y + L.h) };
    if (from.x === to.x && from.y === to.y) { from.x = L.x; from.y = L.y + L.h / 2; }
    leader(ctx, from, to, c, S);
  }
  const r = L.fs * 0.42;
  const bg = style === 'light' ? hexA('#ffffff', 0.94) : style === 'color' ? hexA(c.base, 0.95) : style === 'clear' ? null : hexA('#0b0b0c', 0.86);
  if (bg) {
    ctx.save();
    ctx.shadowColor = hexA('#000000', 0.45); ctx.shadowBlur = L.fs * 0.9; ctx.shadowOffsetY = L.fs * 0.18;
    ctx.beginPath(); roundRect(ctx, L.x, L.y, L.w, L.h, r); ctx.fillStyle = bg; ctx.fill();
    ctx.restore();
    if (style !== 'color') {
      // accent rail on the left edge, in the mark color
      ctx.save(); ctx.beginPath(); roundRect(ctx, L.x, L.y, L.w, L.h, r); ctx.clip();
      ctx.fillStyle = c.base; ctx.fillRect(L.x, L.y, L.fs * 0.22, L.h);
      ctx.restore();
    }
  }
  const textCol = style === 'light' ? '#111111' : style === 'color' ? c.ink : '#ffffff';
  const headCol = style === 'light' ? (m.color === 'white' || m.color === 'yellow' ? '#111111' : c.lo) : style === 'color' ? c.ink : c.base;
  ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
  if (style === 'clear') { ctx.shadowColor = hexA('#000000', 0.9); ctx.shadowBlur = L.fs * 0.5; }
  const x0 = L.x + L.pad + (style === 'clear' ? 0 : L.fs * 0.22);
  L.lines.forEach((ln, i) => {
    ctx.font = `${ln.head ? 800 : 560} ${L.fs * (ln.head ? 1.04 : 1)}px ${ANNO_FONT}`;
    ctx.fillStyle = ln.head ? headCol : textCol;
    ctx.fillText(ln.text, x0, L.y + L.pad * 0.75 + L.lh * (i + 0.5));
  });
  ctx.restore();
}

function chipSize(S, scale = 1) { return Math.max(9, 0.024 * S * scale); }
function chip(ctx, text, x, y, c, S, scale = 1, solid = true) {
  const size = chipSize(S, scale);
  ctx.save();
  ctx.font = `800 ${size}px ${ANNO_FONT}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const str = String(text).toUpperCase();
  const w = ctx.measureText(str).width;
  const padX = size * 0.55; const padY = size * 0.38;
  const bw = w + padX * 2; const bh = size + padY * 2;
  if (solid) {
    ctx.beginPath(); roundRect(ctx, x - bw / 2, y - bh / 2, bw, bh, bh * 0.32);
    ctx.fillStyle = hexA('#000000', 0.86);
    ctx.shadowColor = hexA('#000000', 0.5); ctx.shadowBlur = size * 0.5; ctx.shadowOffsetY = size * 0.12;
    ctx.fill();
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.lineWidth = Math.max(1, size * 0.075); ctx.strokeStyle = hexA(c.base, 0.9); ctx.stroke();
  }
  ctx.fillStyle = solid ? c.base : '#ffffff';
  if (!solid) { ctx.shadowColor = hexA('#000000', 0.8); ctx.shadowBlur = size * 0.55; }
  ctx.fillText(str, x, y);
  ctx.restore();
  return { w: bw, h: bh };
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr); ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr); ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function curvePoints(pts, curve = 0, steps = 34) {
  if (pts.length < 2) return pts;
  if (pts.length === 2) {
    const [a, b] = pts;
    if (!curve) return [a, b];
    const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2; const dx = b.x - a.x; const dy = b.y - a.y;
    const cx = mx - dy * curve; const cy = my + dx * curve;
    const out = [];
    for (let i = 0; i <= steps; i++) { const t = i / steps; const u = 1 - t; out.push({ x: u * u * a.x + 2 * u * t * cx + t * t * b.x, y: u * u * a.y + 2 * u * t * cy + t * t * b.y }); }
    return out;
  }
  const out = [];
  const P = [pts[0], ...pts, pts[pts.length - 1]];
  for (let i = 1; i < P.length - 2; i++) {
    for (let j = 0; j < steps; j++) {
      const t = j / steps; const t2 = t * t; const t3 = t2 * t;
      out.push({
        x: 0.5 * ((2 * P[i].x) + (-P[i - 1].x + P[i + 1].x) * t + (2 * P[i - 1].x - 5 * P[i].x + 4 * P[i + 1].x - P[i + 2].x) * t2 + (-P[i - 1].x + 3 * P[i].x - 3 * P[i + 1].x + P[i + 2].x) * t3),
        y: 0.5 * ((2 * P[i].y) + (-P[i - 1].y + P[i + 1].y) * t + (2 * P[i - 1].y - 5 * P[i].y + 4 * P[i + 1].y - P[i + 2].y) * t2 + (-P[i - 1].y + 3 * P[i].y - 3 * P[i + 1].y + P[i + 2].y) * t3),
      });
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}
// Ramer-Douglas-Peucker: a pen stroke keeps its shape with a fraction of the points.
function simplifyPath(pts, eps) {
  if (!pts || pts.length < 3) return pts;
  const keep = new Array(pts.length).fill(false); keep[0] = keep[pts.length - 1] = true;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [i0, i1] = stack.pop(); let dmax = 0; let idx = -1;
    for (let i = i0 + 1; i < i1; i++) { const dd = distToSeg(pts[i].x, pts[i].y, pts[i0], pts[i1]); if (dd > dmax) { dmax = dd; idx = i; } }
    if (dmax > eps && idx > 0) { keep[idx] = true; stack.push([i0, idx], [idx, i1]); }
  }
  return pts.filter((_, i) => keep[i]);
}
function pathLength(pts) { let n = 0; for (let i = 1; i < pts.length; i++) n += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y); return n; }
function takeFraction(pts, f) {
  if (f >= 1 || pts.length < 2) return pts;
  if (f <= 0) return [];
  const want = pathLength(pts) * f; const out = [pts[0]]; let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (acc + d >= want) { const r = (want - acc) / (d || 1); out.push({ x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * r, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * r }); return out; }
    acc += d; out.push(pts[i]);
  }
  return out;
}
function trimEnd(pts, by) { const total = pathLength(pts); return takeFraction(pts, total <= by ? 0 : (total - by) / total); }
function distToSeg(px, py, a, b) {
  const dx = b.x - a.x; const dy = b.y - a.y; const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / l2)) : 0;
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

// Does a canvas point (px) land on the mark? Needs ctx for note text metrics.
function hitMark(ctx, m, view, px, py, slop = 10) {
  const pts = (m.pts || []).map((q) => ({ x: view.toX(q.x), y: view.toY(q.y) }));
  if (!pts.length) return false;
  const S = view.scale;
  if (m.kind === 'spot' || m.kind === 'beam') {
    const pt = { ...pts[0], yN: m.tilt ?? m.pts[0].y };
    const { rx, ry } = ellipseFor(m, pt, S);
    const dx = (px - pt.x) / (rx + slop); const dy = (py - pt.y) / (ry + slop);
    if (dx * dx + dy * dy <= 1) return true;
    if (m.kind === 'beam') { const h = (m.h ?? MDEF.beamH) * S; return px > pt.x - rx && px < pt.x + rx && py < pt.y && py > pt.y - h; }
    return false;
  }
  if (m.kind === 'label') { const sz = chipSize(S, (m.w || 1) * LABEL_SCALE); ctx.save(); ctx.font = `800 ${sz}px ${ANNO_FONT}`; const tw = ctx.measureText(String(m.text || 'LABEL').toUpperCase()).width; ctx.restore(); return Math.abs(px - pts[0].x) < tw / 2 + sz * 0.6 + slop && Math.abs(py - pts[0].y) < sz * 0.9 + slop; }
  if (m.kind === 'player') return Math.hypot(px - pts[0].x, py - pts[0].y) < (m.r ?? MDEF.player) * S * (m.w || 1) + slop;
  if (m.kind === 'item') { const b = itemBox(m, pts[0], S); return Math.abs(px - pts[0].x) < b.w / 2 + slop && Math.abs(py - pts[0].y) < b.h / 2 + slop; }
  if (m.kind === 'note') { const L = noteLayout(ctx, m, pts, S, view); return px > L.x - slop && px < L.x + L.w + slop && py > L.y - slop && py < L.y + L.h + slop; }
  if (m.kind === 'zone' || m.kind === 'shade') {
    if (pts.length < 2) return false;
    return px > Math.min(pts[0].x, pts[1].x) - slop && px < Math.max(pts[0].x, pts[1].x) + slop && py > Math.min(pts[0].y, pts[1].y) - slop && py < Math.max(pts[0].y, pts[1].y) + slop;
  }
  const path = m.kind === 'arrow' || m.kind === 'line' ? curvePoints(pts, m.curve ?? 0) : pts;
  const w = ((m.kind === 'barrier' ? (m.th ?? 0.032) : (m.aw ?? MDEF.arrowW)) * S * (m.w || 1)) / 2 + slop;
  for (let i = 1; i < path.length; i++) if (distToSeg(px, py, path[i - 1], path[i]) < w) return true;
  return false;
}

// Marks of one freeze at time tRel into the hold. Reveal "sequence" brings
// them in one after another (gap seconds apart); "together" all at once.
// Focus (darken) marks paint first so they never dim the marks on top.
const paintOrder = (marks) => [...(marks || []).filter((m) => m.kind === 'shade'), ...(marks || []).filter((m) => m.kind !== 'shade')];

function freezeTimeline(f, s) {
  const marks = paintOrder(f.marks);
  const dur = f.dur || 3;
  const lead = Math.min(0.12, dur * 0.05); // a beat on the clean frame before the first mark lands
  let gap = s.annoReveal === 'together' ? 0 : Math.max(0, Number(s.annoRevealGap) || 0.35);
  // every mark is fully in by 60% of the hold, however many there are
  if (marks.length > 1) gap = Math.min(gap, Math.max(0, dur * 0.6 - lead - IN_DUR) / (marks.length - 1));
  return marks.map((m, i) => ({ ...m, t0: lead + i * gap, t1: dur + 5, anim: m.anim || defaultAnim(m.kind) }));
}

function paintFreeze(ctx, f, tRel, view, s, solid) {
  const marks = solid ? paintOrder(f.marks) : freezeTimeline(f, s);
  for (const m of marks) drawMark(ctx, m, tRel, view, { solid });
}

// ============================================================================
// Annotator: annotate mode on one clip. Toolbar + freeze strip replace the
// button panel; a canvas over the picture takes the drawing.
// ============================================================================

const SVG = (d) => `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const ANNO_TOOLS = [
  { id: 'select', key: 'v', icon: 'mouse-pointer-2', label: 'Select, move, resize' },
  { id: 'zoom', key: 'z', icon: 'zoom-in', label: 'Zoom: click in, Option-click out, drag to pan, double-click to reset. Pinch works anywhere.' },
  null,
  { id: 'arrow', key: 'a', icon: 'move-up-right', label: 'Arrow' },
  { id: 'pass', key: 'd', motion: 'pass', svg: SVG('<path d="M5 19l2.5-2.5M10 14l2.5-2.5M15 9l2-2" /><path d="M10 6h8v8" />'), label: 'Pass (dashed arrow)' },
  { id: 'shoot', key: 's', motion: 'shoot', svg: SVG('<path d="M3.5 16.5l10-10M6.5 19.5l10-10" /><path d="M11 5h8v8" />'), label: 'Shoot (double line)' },
  { id: 'skate', key: 'w', motion: 'puck', svg: SVG('<path d="M3 18c1.5-1.5 3 0 4.5-1.5S7.5 13.5 9 12s3 0 4.5-1.5" /><path d="M11 6h7v7" /><path d="M18 6l-4.5 4.5" />'), label: 'Skate with the puck (squiggle)' },
  { id: 'line', key: 'q', icon: 'minus', label: 'Line' },
  { id: 'pen', key: 'e', icon: 'pencil', label: 'Pen' },
  null,
  { id: 'spot', key: '1', icon: 'circle-dot', label: 'Spotlight a player' },
  { id: 'beam', key: '2', icon: 'triangle', label: 'Beam of light' },
  { id: 'zone', key: 'r', icon: 'square-dashed', label: 'Zone (rectangle)' },
  { id: 'circle', key: 'c', icon: 'circle-dashed', label: 'Circle zone' },
  { id: 'shade', key: 'h', icon: 'focus', label: 'Focus: darken everything else' },
  { id: 'barrier', key: 'b', icon: 'equal', label: 'Wall / closed lane' },
  null,
  { id: 'note', key: 'n', icon: 'sticky-note', label: 'Note for a player' },
  { id: 'label', key: 't', icon: 'type', label: 'Label chip' },
  null,
  { id: 'p0', key: '3', player: 0, label: 'Player (right-click: number, color)' },
  { id: 'p1', key: '4', player: 1, label: 'Player (right-click: number, color)' },
];
// Which tool a mark was made with (for "make this the default").
const toolOfMark = (m) => (m.kind === 'arrow' ? ({ pass: 'pass', shoot: 'shoot', puck: 'skate' }[m.motion] || 'arrow') : m.kind === 'zone' ? (m.shape === 'ellipse' ? 'circle' : 'zone') : m.kind === 'player' ? 'p0' : m.kind === 'item' ? m.item : m.kind);
const fmtRel = (t) => { const x = Math.max(0, t || 0); const m = Math.floor(x / 60); const sec = x - m * 60; return `${String(m).padStart(2, '0')}:${sec.toFixed(1).padStart(4, '0')}`; };
const TOOL_OF = Object.fromEntries(ANNO_TOOLS.filter(Boolean).map((t) => [t.id, t]));
// The toolbar's order (setting annoToolOrder: tool ids and '|' for a separator; [] = as built).
// Tony drags tools to reorder them; unknown ids drop out, new tools join at the end, and
// separators never double up or sit at an end.
const DEFAULT_TOOL_ORDER = ANNO_TOOLS.map((t) => (t ? t.id : '|'));
function annoToolOrder(s) {
  const want = Array.isArray(s.annoToolOrder) && s.annoToolOrder.length ? s.annoToolOrder : DEFAULT_TOOL_ORDER;
  const out = []; const seen = new Set();
  for (const id of want) { if (id === '|') out.push('|'); else if (TOOL_OF[id] && !seen.has(id)) { seen.add(id); out.push(id); } }
  for (const t of ANNO_TOOLS) if (t && !seen.has(t.id)) out.push(t.id);
  return out.filter((x, i, a) => x !== '|' || (i > 0 && i < a.length - 1 && a[i - 1] !== '|'));
}
const HAS_TEXT = new Set(['note', 'label', 'player', 'spot', 'beam', 'arrow', 'zone', 'item', 'barrier']);

class Annotator {
  constructor(player, tag) {
    this.p = player;
    this.s = player.s;
    this.tag = tag;
    if (!Array.isArray(tag.freezes)) tag.freezes = [];
    this.tool = 'select';
    this.sel = null;       // selected mark
    this.freeze = null;    // freeze at the current frame
    this.undoStack = []; this.redoStack = [];
    this.pending = {};     // player slot -> label to place next
    this.dead = false;
    this.returnT = player.annoReturnT != null ? player.annoReturnT : player.time; // where Done / Export take you back to
    loadAnnoImages();
    this.#build();
    // start on the clip's own timeline: its first freeze, else its first frame
    this.p.video.pause();
    const tl = this.tl();
    const firstHold = tl.segs.find((g) => g.kind === 'hold');
    this.seekOut(firstHold ? firstHold.o0 : 0);
    this.p.syncPlayIcon();
    this.p.flash(`Annotate: ${tag.title || 'clip'}`);
  }

  // ---- DOM ------------------------------------------------------------------

  #build() {
    const p = this.p;
    p.view.contentEl.addClass('cvs-annotating');
    this.host = p.panelHost.createDiv({ cls: 'cvs-anno' });
    // toolbar
    const outer = this.host.createDiv({ cls: 'cvs-anno-bar' });
    // tools scroll sideways in a narrow pane; Preview / Export / Done stay pinned
    const bar = outer.createDiv({ cls: 'cvs-anno-scroll' });
    bar.onwheel = (e) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && bar.scrollWidth > bar.clientWidth) { bar.scrollLeft += e.deltaY; e.preventDefault(); } };
    const tools = bar.createDiv({ cls: 'cvs-anno-group cvs-anno-tools', attr: { 'data-sort-list': 'tools' } });
    this.toolsEl = tools;
    this.#renderTools();
    // drag tools (and separators) into any order; a short click still picks the tool
    makeSortable(tools, { onDrop: ({ id, toIndex }) => {
      const ids = [...tools.children].map((k) => k.dataset.sortItem).filter(Boolean);
      const i = ids.indexOf(id); if (i < 0) return;
      ids.splice(i, 1); ids.splice(toIndex, 0, id);
      this.s.annoToolOrder = ids.map((x) => (x.startsWith('sep-') ? '|' : x));
      this.p.plugin.saveSoon();
      this.#renderTools(); this.#syncBar();
    } });
    bar.createDiv({ cls: 'cvs-anno-sep' });
    const colors = bar.createDiv({ cls: 'cvs-anno-group cvs-anno-colors' });
    this.colorEls = {};
    for (const k of ANNO_COLOR_KEYS) {
      const b = colors.createEl('button', { cls: 'cvs-anno-swatch', attr: { 'aria-label': k[0].toUpperCase() + k.slice(1) } });
      b.style.setProperty('--c', ANNO_COLORS[k].base);
      b.onclick = (e) => { e.preventDefault(); this.setColor(k); };
      this.colorEls[k] = b;
    }
    bar.createDiv({ cls: 'cvs-anno-sep' });
    const opts = bar.createDiv({ cls: 'cvs-anno-group' });
    this.sizeEls = {};
    for (const [k, v, lab] of [['s', 0.7, 'Small'], ['m', 1, 'Medium'], ['l', 1.4, 'Large']]) {
      const b = opts.createEl('button', { cls: 'cvs-anno-size is-' + k, attr: { 'aria-label': `${lab} size` } });
      b.createSpan({ cls: 'cvs-anno-size-dot' });
      b.onclick = (e) => { e.preventDefault(); this.setSize(v); };
      this.sizeEls[v] = b;
    }
    this.dashEl = opts.createEl('button', { cls: 'cvs-anno-tool', attr: { 'aria-label': 'Dashed' } });
    setIcon(this.dashEl, 'git-commit-horizontal');
    this.dashEl.onclick = (e) => { e.preventDefault(); this.setDash(!(this.sel ? this.sel.dash : this.#def(this.tool).dash)); };
    bar.createDiv({ cls: 'cvs-anno-sep' });
    const edit = bar.createDiv({ cls: 'cvs-anno-group' });
    this.undoEl = this.#ib(edit, 'undo-2', 'Undo (Cmd+Z)', () => this.undo());
    this.redoEl = this.#ib(edit, 'redo-2', 'Redo (Cmd+Shift+Z)', () => this.redo());
    this.delEl = this.#ib(edit, 'trash-2', 'Delete the selected mark (Backspace)', () => this.deleteSel());
    this.#ib(edit, 'more-horizontal', 'More', (e, b) => this.#moreMenu(b));
    const right = outer.createDiv({ cls: 'cvs-anno-group cvs-anno-right' });
    // icon-only, each with its key shown
    this.previewEl = right.createEl('button', { cls: 'cvs-anno-cta is-ghost is-icon has-kbd', attr: { 'aria-label': 'Play the clip from the start with its freezes (P)' } });
    this.previewEl.onclick = (e) => { e.preventDefault(); this.togglePreview(); };
    this.exportEl = right.createEl('button', { cls: 'cvs-anno-cta is-icon has-kbd', attr: { 'aria-label': 'Export this clip as mp4 and return to the video (Cmd+E)' } });
    setIcon(this.exportEl.createSpan(), 'download'); this.exportEl.createSpan({ cls: 'cvs-anno-kbd', text: '⌘E' });
    this.exportEl.onclick = (e) => { e.preventDefault(); this.exportAndClose(); };
    const done = right.createEl('button', { cls: 'cvs-anno-cta is-ghost is-icon has-kbd', attr: { 'aria-label': 'Done: return to the whole video (Esc)' } });
    setIcon(done.createSpan(), 'check'); done.createSpan({ cls: 'cvs-anno-kbd', text: 'Esc' });
    done.onclick = (e) => { e.preventDefault(); this.close(); };
    // freeze strip
    this.strip = this.host.createDiv({ cls: 'cvs-anno-strip' });
    // canvas over the picture
    this.cv = p.frameEl.createEl('canvas', { cls: 'cvs-anno-canvas' });
    this.ctx = this.cv.getContext('2d');
    this.hud = p.frameEl.createDiv({ cls: 'cvs-anno-hud' });
    // zoom (view only; exports are never zoomed)
    this.zoom = { z: 1, x: 0, y: 0 };
    this.zoomChip = p.frameEl.createDiv({ cls: 'cvs-anno-zoom', attr: { 'aria-label': 'Click to fit the picture again' } });
    this.zoomChip.onclick = (e) => { e.stopPropagation(); this.resetZoom(); };
    this.onWheel = (e) => this.#wheel(e);
    p.stageEl.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
    // the clip's own timeline: freezes as pause blocks on the transport
    this.tlLayer = p.timelineEl.createDiv({ cls: 'cvs-tl-holds' });
    this.outT = 0;
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(p.frameEl);
    this.onMeta = () => this.layout();
    p.video.addEventListener('loadedmetadata', this.onMeta);
    this.#bindCanvas();
    this.#syncBar();
    this.renderStrip();
    this.layout();
  }

  #ib(parent, icon, tip, fn) {
    const b = parent.createEl('button', { cls: 'cvs-anno-tool', attr: { 'aria-label': tip } });
    setIcon(b, icon);
    b.onclick = (e) => { e.preventDefault(); e.stopPropagation(); fn(e, b); };
    return b;
  }

  #slot(i) {
    const slots = this.s.annoSlots || [];
    return { fill: (slots[i] && slots[i].fill) || ['#1e1e1e', '#75d8ff', '#d9d9d9'][i] };
  }
  #paintSlot(i, disc) {
    const sl = this.#slot(i);
    disc.style.setProperty('--c', sl.fill);
    disc.setText(this.pending[i] || '');
    disc.style.color = luminance(sl.fill) > 0.55 ? INK : '#ffffff';
  }

  #slotMenu(i, anchor) {
    const m = new CvsMenu(this.p);
    m.addItem((x) => x.setTitle('Blank').setChecked(!this.pending[i]).onClick(() => { this.pending[i] = ''; this.setTool('p' + i); this.#syncBar(); }));
    for (const lab of PLAYER_LABELS) m.addItem((x) => x.setTitle(lab).setChecked(this.pending[i] === lab).onClick(() => { this.pending[i] = lab; this.setTool('p' + i); this.#syncBar(); }));
    m.addSeparator();
    for (const [name, hex] of PLAYER_FILLS) m.addItem((x) => x.setTitle(`Color: ${name}`).setChecked(this.#slot(i).fill === hex).onClick(() => {
      const slots = (this.s.annoSlots = Array.isArray(this.s.annoSlots) ? this.s.annoSlots : []);
      slots[i] = { fill: hex }; this.p.plugin.saveSoon(); this.#syncBar();
      if (this.sel && this.sel.kind === 'player') { this.#mutate(() => { this.sel.fill = hex; }); }
    }));
    m.showBelow(anchor, 'start');
  }

  layout() {
    if (this.dead) return;
    const fr = this.p.frameEl; const v = this.p.video;
    const W = fr.clientWidth; const H = fr.clientHeight;
    const vw = v.videoWidth || 16; const vh = v.videoHeight || 9;
    const sc = Math.min(W / vw, H / vh);
    const w = Math.round(vw * sc); const h = Math.round(vh * sc);
    const x = Math.round((W - w) / 2); const y = Math.round((H - h) / 2);
    Object.assign(this.cv.style, { left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px' });
    const dpr = window.devicePixelRatio || 1;
    this.cv.width = Math.max(1, Math.round(w * dpr)); this.cv.height = Math.max(1, Math.round(h * dpr));
    this.dpr = dpr;
    this.applyZoom(false);
    this.draw();
  }

  get view() {
    const W = this.cv.width; const H = this.cv.height;
    // sizes follow the picture's shorter side, so a portrait clip is not drawn huge
    return { toX: (nx) => nx * W, toY: (ny) => ny * H, fromX: (px) => px / W, fromY: (py) => py / H, scale: Math.min(W, H), x0: 0, y0: 0, w: W, h: H };
  }

  // ---- state ----------------------------------------------------------------

  get freezes() { return this.tag.freezes; }
  freezeAt(t) { const tol = Math.max(0.02, this.p.frameSec * 0.6); return this.freezes.find((f) => Math.abs(f.at - t) <= tol) || null; }

  // ---- the clip's own timeline ---------------------------------------------------
  // Output time runs 0..T over the clip: play segments of the video with each
  // freeze injected as a hold of its length (the same shape the export has).
  tl() {
    const { a, b } = this.p.clipRange(this.tag);
    const fz = this.freezes.filter((f) => f.at >= a - 1e-3 && f.at <= b + 1e-3).sort((x, y) => x.at - y.at);
    const segs = []; let o = 0; let cur = a;
    for (const f of fz) {
      const at = clamp(f.at, a, b);
      if (at - cur > 1e-4) { segs.push({ kind: 'play', o0: o, o1: o + at - cur, s0: cur, s1: at }); o += at - cur; cur = at; }
      const d = Math.max(0.2, f.dur || 3);
      segs.push({ kind: 'hold', o0: o, o1: o + d, s0: at, s1: at, f }); o += d;
    }
    if (b - cur > 1e-4) { segs.push({ kind: 'play', o0: o, o1: o + b - cur, s0: cur, s1: b }); o += b - cur; }
    return { a, b, segs, T: Math.max(o, 0.001) };
  }
  segAt(o, tl) { for (const g of tl.segs) if (o >= g.o0 - 1e-6 && o < g.o1 - 1e-6) return g; return tl.segs[tl.segs.length - 1] || null; }
  holdAt(o, tl) { const g = this.segAt(o, tl); return g && g.kind === 'hold' ? g : null; }
  get tol() { return Math.max(0.02, this.p.frameSec * 0.6); }
  // A source time on the clip timeline; the freeze frame itself maps to its hold's start.
  outAt(src, tl) {
    if (src <= tl.a + 1e-6 && !(tl.segs[0] && tl.segs[0].kind === 'hold')) return 0;
    for (const g of tl.segs) {
      if (g.kind === 'hold' && src >= g.s0 - 1e-6 && src - g.s0 <= this.tol) return g.o0;
      if (g.kind === 'play' && src >= g.s0 && src < g.s1) return g.o0 + (src - g.s0);
    }
    return src < tl.a ? 0 : tl.T;
  }
  srcAt(o, tl) { const g = this.segAt(o, tl); if (!g) return tl.a; return g.kind === 'hold' ? g.s0 : g.s0 + (o - g.o0); }

  // Go to a point on the clip timeline (drag = smooth decoder scrub).
  seekOut(o, drag = false) {
    const tl = this.tl();
    this.outT = clamp(o, 0, tl.T);
    const src = this.srcAt(this.outT, tl);
    this.p.video.pause();
    this.syncing = true;
    if (drag) this.p.scrubber.dragTo(src); else this.p.seek(src);
    this.syncing = false;
    this.syncFromOut(tl);
  }

  // The source moved on its own (trackpad scrub): follow it on the clip timeline.
  onTime(t) {
    if (this.dead || this.playing || this.syncing || this.tlDrag) return;
    const tl = this.tl();
    const h = this.holdAt(this.outT, tl);
    if (!(h && Math.abs(t - h.s0) <= this.tol)) this.outT = this.outAt(t, tl);
    this.syncFromOut(tl);
  }

  syncFromOut(tl = this.tl()) {
    if (this.dead) return;
    const h = this.holdAt(this.outT, tl);
    const f = h ? h.f : null;
    if (f !== this.freeze) {
      this.freeze = f;
      if (!f || (this.sel && !f.marks.includes(this.sel))) this.sel = null;
      this.closeEditor();
      this.renderStrip();
      this.#syncBar();
    }
    this.renderTl(tl);
    this.draw();
  }

  onClipsChanged() {
    if (this.dead) return;
    if (!this.p.tags.includes(this.tag)) {
      // an undo in the clip list swapped the object; follow it by Start, else close
      const same = this.p.tags.find((x) => Math.abs(x.t - this.tag.t) < 1e-6);
      if (same) { this.tag = same; if (!Array.isArray(same.freezes)) same.freezes = []; }
      else { this.close(true); return; }
    }
    // In / Out may have moved: keep the playhead on the same source frame
    const tl = this.tl();
    if (!this.playing) { const h = this.freeze && tl.segs.find((g) => g.f === this.freeze); this.outT = h ? h.o0 : this.outAt(this.p.time, tl); }
    this.renderStrip(); this.syncFromOut(tl);
  }

  // Timeline display: fill, head, clocks, and one block per freeze (drag its
  // right edge for length, its body to move it; click to go there).
  renderTl(tl = this.tl()) {
    if (this.dead) return;
    const p = this.p; const T = tl.T;
    const fr = clamp(this.outT / T, 0, 1);
    p.tlFill.style.width = (fr * 100) + '%'; p.tlHead.style.left = (fr * 100) + '%';
    p.curEl.setText(fmtRel(this.outT)); p.durEl.setText(fmtRel(T));
    const holds = tl.segs.filter((g) => g.kind === 'hold');
    const key = holds.map((g) => `${g.f.id}:${g.o0.toFixed(3)}:${g.o1.toFixed(3)}:${g.f === this.freeze ? 1 : 0}:${g.f.marks.length}`).join('|') + '/' + T.toFixed(3);
    if (key === this.tlKey) return;
    this.tlKey = key;
    const layer = this.tlLayer; layer.empty();
    holds.forEach((g, i) => {
      const b = layer.createDiv({ cls: 'cvs-tl-hold' + (g.f === this.freeze ? ' is-on' : ''), attr: { 'aria-label': `Freeze ${i + 1}: holds ${g.f.dur}s. Drag the right edge to change it, drag the block to move it.` } });
      b.style.left = (100 * g.o0 / T) + '%'; b.style.width = (100 * (g.o1 - g.o0) / T) + '%';
      const lab = b.createSpan({ cls: 'cvs-tl-hold-label' });
      setIcon(lab.createSpan({ cls: 'cvs-tl-hold-icon' }), 'snowflake');
      lab.createSpan({ text: `${g.f.dur}s` });
      const grip = b.createDiv({ cls: 'cvs-tl-hold-grip' });
      this.#bindHold(b, grip, g.f, i);
    });
  }

  #bindHold(block, grip, f, idx) {
    const track = () => this.p.tlTrack.getBoundingClientRect();
    grip.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault(); e.stopPropagation(); this.pause();
      try { grip.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      const W = track().width || 1; const T0 = this.tl().T; const d0 = f.dur; const x0 = e.clientX; const before = this.#snap();
      document.body.addClass('cvs-col-resizing');
      const mv = (ev) => { f.dur = clamp(Math.round((d0 + ((ev.clientX - x0) / W) * T0) * 10) / 10, 0.2, 60); this.renderTl(); this.renderStrip(); };
      const up = (ev) => {
        grip.removeEventListener('pointermove', mv); document.body.removeClass('cvs-col-resizing');
        try { grip.releasePointerCapture(ev.pointerId); } catch (_) { /* fine */ }
        if (f.dur !== d0) { this.undoStack.push(before); this.redoStack = []; this.changed(); this.p.flash(`Freeze holds ${f.dur}s`); }
      };
      grip.addEventListener('pointermove', mv);
      grip.addEventListener('pointerup', up, { once: true });
    });
    block.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.target === grip) return;
      e.preventDefault(); e.stopPropagation(); this.pause();
      try { block.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      const tl0 = this.tl(); const W = track().width || 1; const at0 = f.at; const x0 = e.clientX; const before = this.#snap(); let moved = false;
      const others = this.freezes.filter((g) => g !== f).map((g) => g.at);
      const fsec = this.p.frameSec;
      const mv = (ev) => {
        const dx = ev.clientX - x0; if (!moved && Math.abs(dx) < 4) return;
        moved = true; this.tlDrag = true;
        let at = clamp(at0 + (dx / W) * tl0.T, tl0.a, tl0.b);
        at = tl0.a + Math.round((at - tl0.a) / fsec) * fsec; // whole frames
        if (others.some((o) => Math.abs(o - at) < fsec * 1.5)) return; // never onto another freeze
        f.at = Math.round(at * 1000) / 1000;
        this.p.scrubber.dragTo(f.at);
        const h = this.tl().segs.find((g) => g.f === f); if (h) this.outT = h.o0;
        this.renderTl(); this.draw();
      };
      const up = (ev) => {
        block.removeEventListener('pointermove', mv);
        try { block.releasePointerCapture(ev.pointerId); } catch (_) { /* fine */ }
        this.tlDrag = false;
        if (moved) { this.p.scrubber.endDrag(); this.freezes.sort((a, b) => a.at - b.at); this.undoStack.push(before); this.redoStack = []; this.changed(); this.goFreeze(f); }
        else this.goFreeze(f);
      };
      block.addEventListener('pointermove', mv);
      block.addEventListener('pointerup', up, { once: true });
    });
    block.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); this.#freezeMenu(e, f, idx); });
    block.addEventListener('dblclick', (e) => { e.stopPropagation(); new TextPromptModal(this.p.app, 'Hold length (seconds)', String(f.dur), (v) => { if (Number(v) > 0) this.setDur(f, v); }).open(); });
  }

  // The transport's timeline while annotating (called by the player).
  tlPointer(kind, e) {
    const r = this.p.tlTrack.getBoundingClientRect(); const tl = this.tl();
    const o = (r.width ? clamp((e.clientX - r.left) / r.width, 0, 1) : 0) * tl.T;
    if (kind === 'down') { this.pause(); this.tlDrag = true; this.seekOut(o, true); return; }
    if (kind === 'move') {
      if (this.tlDrag) { this.seekOut(o, true); return; }
      const h = this.holdAt(o, tl);
      const i = h ? tl.segs.filter((g) => g.kind === 'hold').indexOf(h) + 1 : 0;
      this.p.tlTip.setText(h ? `${fmtRel(o)}  Freeze ${i} (${h.f.dur}s)` : fmtRel(o));
      this.p.tlTip.style.left = (100 * o / tl.T) + '%';
      return;
    }
    if (kind === 'up') { if (!this.tlDrag) return; this.tlDrag = false; this.p.scrubber.endDrag(); this.seekOut(o); }
  }

  // Everything that changes marks goes through here: one undo step, a redraw,
  // and a (debounced) save of the clips note.
  #mutate(fn, opts = {}) {
    this.#pushUndo();
    fn();
    this.changed(opts);
  }
  // One undo step = the freezes plus the clip's In / Out (a freeze can stretch them).
  #snap() { return JSON.stringify({ f: this.freezes, i: this.tag.in ?? null, o: this.tag.out ?? null }); }
  #applySnap(json) {
    const o = JSON.parse(json);
    this.tag.freezes = Array.isArray(o) ? o : o.f; // (older in-session snapshots were bare arrays)
    if (!Array.isArray(o)) { this.tag.in = o.i; this.tag.out = o.o; }
  }
  #pushUndo() {
    this.undoStack.push(this.#snap());
    if (this.undoStack.length > 100) this.undoStack.shift();
    this.redoStack = [];
  }
  changed(opts = {}) {
    // holds may have moved or changed length: keep the playhead on the same frame
    if (!this.playing && !this.tlDrag) { const tl = this.tl(); const h = this.freeze && tl.segs.find((g) => g.f === this.freeze); this.outT = h ? h.o0 : this.outAt(this.p.time, tl); this.tlKey = null; this.renderTl(tl); }
    this.draw();
    this.#syncBar();
    if (!opts.light) { this.renderStrip(); this.p.renderTimelineNow(); }
    this.p.scheduleSave();
    if (!opts.light) this.#rowsSoon();
  }
  #rowsSoon() { clearTimeout(this.rowsTimer); this.rowsTimer = setTimeout(() => { if (!this.dead) this.p.render(); }, 120); }
  flush() { if (this.editor) this.closeEditor(); }

  #restore(json) {
    const ids = this.freeze ? this.freeze.id : null;
    const selId = this.sel && this.sel.id;
    this.#applySnap(json);
    this.freeze = this.freezes.find((f) => f.id === ids) || this.freezeAt(this.p.time);
    this.sel = this.freeze ? this.freeze.marks.find((m) => m.id === selId) || null : null;
    this.changed();
  }
  undo() {
    if (this.editor) this.closeEditor();
    const prev = this.undoStack.pop();
    if (!prev) { this.p.flash('Nothing to undo'); return; }
    this.redoStack.push(this.#snap());
    this.#restore(prev);
  }
  redo() {
    const next = this.redoStack.pop();
    if (!next) { this.p.flash('Nothing to redo'); return; }
    this.undoStack.push(this.#snap());
    this.#restore(next);
  }

  // A freeze at the current frame, made on demand. Adding one outside the
  // clip's range stretches In / Out to include it.
  ensureFreeze() {
    if (this.freeze) return this.freeze;
    const t = this.p.time;
    this.p.video.pause();
    const f = { id: uid(), at: Math.round(t * 1000) / 1000, dur: Number(this.s.annoFreezeDur) || 3, marks: [] };
    this.freezes.push(f);
    this.freezes.sort((a, b) => a.at - b.at);
    this.freeze = f;
    const { a, b } = this.p.clipRange(this.tag);
    if (f.at < a || f.at > b) {
      // pin both ends first, so stretching one never slides the other
      if (this.tag.in == null) this.tag.in = a;
      if (this.tag.out == null) this.tag.out = b;
      if (f.at < a) this.tag.in = Math.max(0, f.at - 1);
      if (f.at > b) this.tag.out = Math.min(this.p.duration || f.at + 1, f.at + 1);
      this.p.flash(f.at < a ? 'In moved to include the freeze' : 'Out moved to include the freeze');
    }
    const h = this.tl().segs.find((g) => g.f === f); if (h) this.outT = h.o0;
    this.tlKey = null;
    return f;
  }

  toggleFreeze() {
    this.pause();
    const f = this.freeze || this.freezeAt(this.p.time);
    if (f) {
      if (f.marks.length) { new ConfirmModal(this.p.app, 'Remove this freeze?', `It has ${f.marks.length} mark${f.marks.length === 1 ? '' : 's'}. Cmd+Z brings it back.`, 'Remove', () => this.removeFreeze(f)).open(); return; }
      this.removeFreeze(f); return;
    }
    this.#mutate(() => this.ensureFreeze());
    this.p.flash(`Freeze ${this.freeze.dur}s`);
  }

  removeFreeze(f) {
    this.#mutate(() => { const i = this.freezes.indexOf(f); if (i >= 0) this.freezes.splice(i, 1); if (this.freeze === f) { this.freeze = null; this.sel = null; } });
  }

  goFreeze(f) {
    this.pause();
    const h = this.tl().segs.find((g) => g.f === f);
    if (h) { this.seekOut(h.o0); return; }
    // outside In / Out: show the frame, say why it is not on the timeline
    this.p.seek(f.at); this.p.flash('That freeze is outside In / Out (right-click its chip to stretch the clip)');
  }

  stepFreeze(dir) {
    const tl = this.tl(); const holds = tl.segs.filter((g) => g.kind === 'hold');
    const g = dir > 0 ? holds.find((x) => x.o0 > this.outT + 0.01) : holds.reverse().find((x) => x.o0 < this.outT - 0.01);
    if (g) this.seekOut(g.o0); else this.p.flash(dir > 0 ? 'Last freeze' : 'First freeze');
  }

  // Frame step on the clip timeline; a hold is stepped over in one press.
  stepFrame(dir) {
    this.pause();
    const tl = this.tl(); const fr = this.p.frameSec; const h = this.holdAt(this.outT, tl);
    if (h) this.seekOut(dir > 0 ? h.o1 + fr : h.o0 - fr);
    else this.seekOut(this.outT + dir * fr);
  }

  setDur(f, d) {
    const v = Math.round(clamp(Number(d) || 3, 0.2, 60) * 10) / 10;
    this.#mutate(() => { f.dur = v; });
  }

  // ---- tools ----------------------------------------------------------------

  setTool(id) {
    this.tool = id;
    if (id !== 'select' && this.sel) this.sel = null;
    this.closeEditor();
    this.#syncBar(); this.draw();
  }
  // Per-tool defaults (color, size, dash, note style, spotlight radius ...),
  // kept in settings.annoToolDefaults. With nothing selected, the swatches,
  // sizes and dash edit the active tool's defaults.
  #def(tool) { return ((this.s.annoToolDefaults || {})[tool]) || {}; }
  #setDef(tool, patch) {
    const all = Object.assign({}, this.s.annoToolDefaults || {});
    all[tool] = Object.assign({}, all[tool] || {}, patch);
    for (const k of Object.keys(all[tool])) if (all[tool][k] === undefined) delete all[tool][k];
    this.s.annoToolDefaults = all; this.p.plugin.saveSoon();
  }
  #defTool() { return this.tool === 'select' || this.tool === 'zoom' ? null : this.tool; }
  setColor(k) {
    if (this.sel) { this.#mutate(() => { this.sel.color = k; if (this.sel.kind === 'player') this.sel.fill = ANNO_COLORS[k].base; }, { light: true }); }
    else if (this.#defTool()) this.#setDef(this.#defTool(), { color: k });
    else { this.s.annoMarkColor = k; this.p.plugin.saveSoon(); }
    this.#syncBar();
  }
  setSize(v) {
    if (this.sel) this.#mutate(() => { this.sel.w = v; }, { light: true });
    else if (this.#defTool()) this.#setDef(this.#defTool(), { w: v });
    this.#syncBar();
  }
  setDash(v) {
    if (this.sel) { if (['arrow', 'line', 'pen', 'zone'].includes(this.sel.kind)) this.#mutate(() => { this.sel.dash = v; if (this.sel.kind === 'arrow') this.sel.motion = v ? 'pass' : undefined; }, { light: true }); }
    else if (this.#defTool()) this.#setDef(this.#defTool(), { dash: v || undefined });
    this.#syncBar();
  }
  // The properties of a mark that make sense as its tool's defaults.
  makeDefault(m) {
    const tool = toolOfMark(m);
    const keep = {};
    for (const k of ['color', 'w', 'dash', 'fs', 'style', 'bw', 'r', 'h', 'head', 'anim', 'amount']) if (m[k] !== undefined) keep[k] = m[k];
    if (m.kind === 'player') { const slots = (this.s.annoSlots = Array.isArray(this.s.annoSlots) ? this.s.annoSlots : []); const i = this.#slot(1).fill === m.fill ? 1 : 0; slots[i] = { fill: m.fill }; this.#setDef('p' + i, keep); }
    else this.#setDef(tool, keep);
    this.#syncBar();
    this.p.flash(`New ${(TOOL_OF[tool] || { label: tool }).label.split(' (')[0].split(':')[0].toLowerCase()} marks start like this`);
  }
  #renderTools() {
    const tools = this.toolsEl; tools.empty(); this.toolEls = {};
    let n = 0;
    for (const id of annoToolOrder(this.s)) {
      if (id === '|') { tools.createDiv({ cls: 'cvs-anno-sep', attr: { 'data-sort-item': `sep-${n++}` } }); continue; }
      const t = TOOL_OF[id];
      const b = tools.createEl('button', { cls: 'cvs-anno-tool has-kbd', attr: { 'aria-label': `${t.label}  (${t.key.toUpperCase()})`, 'data-sort-item': t.id } });
      if (t.player != null) { const disc = b.createSpan({ cls: 'cvs-anno-disc' }); this.#paintSlot(t.player, disc); b.oncontextmenu = (e) => { e.preventDefault(); this.#slotMenu(t.player, b); }; }
      else if (t.svg) b.createSpan({ cls: 'cvs-anno-svg' }).innerHTML = t.svg;
      else setIcon(b, t.icon);
      b.createSpan({ cls: 'cvs-anno-kbd', text: t.key.toUpperCase() });
      if (t.player == null) b.oncontextmenu = (e) => { e.preventDefault(); this.#toolMenu(t, b); };
      b.onclick = (e) => { e.preventDefault(); if (t.player != null && this.tool === t.id) { this.#slotMenu(t.player, b); return; } this.setTool(t.id); };
      this.toolEls[t.id] = b;
    }
  }
  resetToolOrder() { this.s.annoToolOrder = []; this.p.plugin.saveSoon(); this.#renderTools(); this.#syncBar(); }

  #toolMenu(t, anchor) {
    const m = new CvsMenu(this.p);
    const d = this.#def(t.id);
    m.addItem((x) => x.setTitle(`${t.label.split(':')[0]}  (${t.key.toUpperCase()})`).setDisabled(true));
    if (t.id === 'select') { m.addItem((x) => x.setTitle('Reset the tool order').setIcon('rotate-ccw').setDisabled(!(this.s.annoToolOrder || []).length).onClick(() => this.resetToolOrder())); m.addItem((x) => x.setTitle('Tip: drag tools to reorder them').setDisabled(true)); m.showBelow(anchor, 'start'); return; }
    m.addItem((x) => x.setTitle(Object.keys(d).length ? 'Reset its defaults' : 'No custom defaults yet').setIcon('rotate-ccw').setDisabled(!Object.keys(d).length).onClick(() => { const all = Object.assign({}, this.s.annoToolDefaults || {}); delete all[t.id]; this.s.annoToolDefaults = all; this.p.plugin.saveSoon(); this.#syncBar(); }));
    m.addItem((x) => x.setTitle('Tip: pick it, then a color or size, to set its default').setDisabled(true));
    m.showBelow(anchor, 'start');
  }

  #syncBar() {
    if (this.dead) return;
    for (const [id, b] of Object.entries(this.toolEls)) b.toggleClass('is-on', this.tool === id);
    for (let i = 0; i < 2; i++) { const d = this.toolEls['p' + i] && this.toolEls['p' + i].querySelector('.cvs-anno-disc'); if (d) this.#paintSlot(i, d); }
    const def = this.#def(this.tool);
    const col = this.sel && this.sel.color ? this.sel.color : (def.color || this.s.annoMarkColor || 'cyan');
    for (const [k, b] of Object.entries(this.colorEls)) b.toggleClass('is-on', k === col);
    const w = this.sel ? (this.sel.w || 1) : (def.w || 1);
    for (const [v, b] of Object.entries(this.sizeEls)) b.toggleClass('is-on', Math.abs(Number(v) - w) < 0.01);
    this.dashEl.toggleClass('is-on', this.sel ? !!this.sel.dash : !!def.dash);
    this.delEl.toggleClass('is-disabled', !this.sel);
    this.undoEl.toggleClass('is-disabled', !this.undoStack.length);
    this.redoEl.toggleClass('is-disabled', !this.redoStack.length);
    this.previewEl.empty();
    setIcon(this.previewEl.createSpan(), this.playing ? 'square' : 'clapperboard');
    this.previewEl.createSpan({ cls: 'cvs-anno-kbd', text: 'P' });
    this.cv.toggleClass('is-drawing', this.tool !== 'select');
    this.cv.toggleClass('is-zoomtool', this.tool === 'zoom');
  }

  // ---- freeze strip ------------------------------------------------------------

  renderStrip() {
    if (this.dead) return;
    const st = this.strip; st.empty();
    const { a, b } = this.p.clipRange(this.tag);
    const holds = this.freezes.filter((f) => f.at >= a - 1e-3 && f.at <= b + 1e-3).reduce((n, f) => n + (f.dur || 0), 0);
    const info = st.createDiv({ cls: 'cvs-anno-info' });
    info.createSpan({ cls: 'cvs-anno-name', text: this.tag.title || 'untitled clip' });
    const rg = info.createSpan({ cls: 'cvs-anno-range', text: `${fmtTag(a, 0)} to ${fmtTag(b, 0)}  ·  ${(b - a + holds).toFixed(1)}s`, attr: { 'aria-label': `Exports ${(b - a).toFixed(1)}s of video plus ${holds.toFixed(1)}s of freezes. I / O set In and Out at the current frame${this.tag.out == null ? '; Out is blank, so it runs ' + (Number(this.s.exportDefaultLength) || 10) + 's' : ''}.` } });
    rg.toggleClass('is-default', this.tag.out == null);
    const list = st.createDiv({ cls: 'cvs-anno-chips' });
    const sorted = this.freezes.slice().sort((x, y) => x.at - y.at);
    sorted.forEach((f, i) => {
      const out = f.at < a - 1e-3 || f.at > b + 1e-3;
      const c = list.createDiv({ cls: 'cvs-anno-chip' + (f === this.freeze ? ' is-on' : '') + (out ? ' is-out' : ''), attr: out ? { 'aria-label': 'Outside In / Out: not in the export. Right-click to stretch the clip to it.' } : {} });
      setIcon(c.createSpan({ cls: 'cvs-anno-chip-icon' }), 'snowflake');
      c.createSpan({ cls: 'cvs-anno-chip-t', text: fmtTag(f.at - a < 0 ? f.at : f.at - a, 1).replace(/^00:/, '') });
      const d = c.createSpan({ cls: 'cvs-anno-chip-d', text: `${f.dur}s`, attr: { 'aria-label': 'Hold length. Click to change.' } });
      d.onclick = (e) => { e.stopPropagation(); this.#durMenu(f, d); };
      if (f.marks.length) c.createSpan({ cls: 'cvs-anno-chip-n', text: String(f.marks.length) });
      c.onclick = () => this.goFreeze(f);
      c.oncontextmenu = (e) => { e.preventDefault(); this.#freezeMenu(e, f, i); };
    });
    const add = list.createEl('button', { cls: 'cvs-anno-add', attr: { 'aria-label': 'Freeze the current frame (F)' } });
    setIcon(add.createSpan(), 'plus'); add.createSpan({ text: this.freeze ? 'Freeze' : `Freeze ${Number(this.s.annoFreezeDur) || 3}s` });
    add.toggleClass('is-disabled', !!this.freeze);
    add.onclick = (e) => { e.preventDefault(); if (!this.freeze) this.toggleFreeze(); };
    if (!sorted.length) st.createDiv({ cls: 'cvs-anno-tip', text: 'Pause on a frame and draw: it freezes there automatically. F freezes without drawing.' });
  }

  #durMenu(f, anchor) {
    const m = new CvsMenu(this.p);
    for (const v of [1, 1.5, 2, 3, 4, 5, 6, 8, 10]) m.addItem((x) => x.setTitle(`${v} seconds`).setChecked(f.dur === v).onClick(() => this.setDur(f, v)));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Custom...').setIcon('timer').onClick(() => new TextPromptModal(this.p.app, 'Hold length (seconds)', String(f.dur), (v) => { if (Number(v) > 0) this.setDur(f, v); }).open()));
    m.addItem((x) => x.setTitle('Use for every freeze in this clip').setIcon('copy').onClick(() => this.#mutate(() => { for (const g of this.freezes) g.dur = f.dur; })));
    m.addItem((x) => x.setTitle(`Make ${f.dur}s the default for new freezes`).setIcon('pin').setDisabled(Number(this.s.annoFreezeDur) === f.dur).onClick(() => { this.s.annoFreezeDur = f.dur; this.p.plugin.saveSoon(); this.renderStrip(); this.p.flash(`New freezes hold ${f.dur}s`); }));
    m.showBelow(anchor, 'start');
  }

  #freezeMenu(e, f, i) {
    const m = new CvsMenu(this.p);
    m.addItem((x) => x.setTitle('Go to this freeze').setIcon('locate').onClick(() => this.goFreeze(f)));
    const { a: ra, b: rb } = this.p.clipRange(this.tag);
    if (f.at < ra - 1e-3 || f.at > rb + 1e-3) m.addItem((x) => x.setTitle('Stretch the clip to include it').setIcon('move-horizontal').onClick(() => this.#mutate(() => { if (this.tag.in == null) this.tag.in = ra; if (this.tag.out == null) this.tag.out = rb; if (f.at < ra) this.tag.in = Math.max(0, f.at - 1); else this.tag.out = Math.min(this.p.duration || f.at + 1, f.at + 1); })));
    m.addItem((x) => x.setTitle('Hold length...').setIcon('timer').onClick(() => this.#durMenu(f, e.currentTarget || this.strip)));
    m.addItem((x) => x.setTitle('Move to the current frame').setIcon('move-horizontal').setDisabled(!!this.freezeAt(this.p.time)).onClick(() => this.#mutate(() => { f.at = Math.round(this.p.time * 1000) / 1000; this.freezes.sort((a, b) => a.at - b.at); this.freeze = f; })));
    const prev = this.freezes.slice().sort((a, b) => a.at - b.at)[i - 1];
    m.addItem((x) => x.setTitle('Copy marks from the previous freeze').setIcon('copy-plus').setDisabled(!prev || !prev.marks.length).onClick(() => this.#mutate(() => { for (const mk of prev.marks) f.marks.push({ ...JSON.parse(JSON.stringify(mk)), id: uid() }); })));
    m.addItem((x) => x.setTitle('Clear its marks').setIcon('eraser').setDisabled(!f.marks.length).onClick(() => this.#mutate(() => { f.marks = []; if (this.freeze === f) this.sel = null; })));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Remove freeze').setIcon('trash-2').onClick(() => this.removeFreeze(f)));
    m.showAtMouseEvent(e);
  }

  #moreMenu(anchor) {
    const m = new CvsMenu(this.p);
    const f = this.freeze;
    m.addItem((x) => x.setTitle('Freeze the current frame').setIcon('snowflake').setShortcut('F').setDisabled(!!f).onClick(() => this.toggleFreeze()));
    m.addItem((x) => x.setTitle('Previous freeze').setIcon('chevron-up').setShortcut('Up').onClick(() => this.stepFreeze(-1)));
    m.addItem((x) => x.setTitle('Next freeze').setIcon('chevron-down').setShortcut('Down').onClick(() => this.stepFreeze(1)));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Duplicate mark').setIcon('copy').setShortcut('⌘D').setDisabled(!this.sel).onClick(() => this.duplicate()));
    m.addItem((x) => x.setTitle('Copy marks of this freeze').setIcon('clipboard-copy').setShortcut('⌘C').setDisabled(!f || !f.marks.length).onClick(() => this.copy(true)));
    m.addItem((x) => x.setTitle('Paste marks').setIcon('clipboard-paste').setShortcut('⌘V').setDisabled(!(this.p.plugin.annoClipboard || []).length).onClick(() => this.paste()));
    m.addItem((x) => x.setTitle('Clear this freeze').setIcon('eraser').setDisabled(!f || !f.marks.length).onClick(() => this.#mutate(() => { f.marks = []; this.sel = null; })));
    m.addSeparator();
    const rev = this.s.annoReveal || 'sequence';
    m.addItem((x) => x.setTitle('Marks appear one by one').setChecked(rev === 'sequence').onClick(() => { this.s.annoReveal = 'sequence'; this.p.plugin.saveSoon(); }));
    m.addItem((x) => x.setTitle('Marks appear together').setChecked(rev === 'together').onClick(() => { this.s.annoReveal = 'together'; this.p.plugin.saveSoon(); }));
    m.addItem((x) => x.setTitle('Label a new player right away').setChecked(this.s.annoLabelOnPlace !== false).onClick(() => { this.s.annoLabelOnPlace = this.s.annoLabelOnPlace === false; this.p.plugin.saveSoon(); }));
    m.addSeparator();
    m.addItem((x) => x.setTitle('Annotate shortcuts').setIcon('keyboard').onClick(() => new ShortcutsModal(this.p.app, this.p.plugin).open()));
    m.showBelow(anchor, 'end');
  }

  // ---- canvas -----------------------------------------------------------------

  draw() {
    if (this.dead || !this.ctx) return;
    const ctx = this.ctx; const v = this.view;
    ctx.clearRect(0, 0, this.cv.width, this.cv.height);
    const zoomed = this.zoom && this.zoom.z > 1.001;
    if (this.playing) {
      const g = this.playing.seg;
      this.hud.setText(this.loopClip ? 'Playing (loop)  ·  Space to pause' : 'Playing  ·  Space to pause');
      this.hud.addClass('is-on', 'is-preview');
      this.cv.toggleClass('is-live', zoomed);
      if (g && g.kind === 'hold') paintFreeze(ctx, g.f, this.outT - g.o0, v, this.s, false);
      return;
    }
    this.hud.removeClass('is-preview');
    const f = this.freeze;
    this.hud.toggleClass('is-on', !!f);
    this.cv.toggleClass('is-live', !!f || zoomed);
    if (!f) return;
    const holds = this.tl().segs.filter((g) => g.kind === 'hold');
    this.hud.setText(`Freeze ${holds.findIndex((g) => g.f === f) + 1} of ${holds.length}  ·  ${f.dur}s`);
    for (const m of paintOrder(f.marks)) drawMark(ctx, m, 0, v, { solid: true });
    if (this.draft) drawMark(ctx, this.draft, 0, v, { solid: true });
    if (this.sel && f.marks.includes(this.sel)) this.#drawHandles(ctx, this.sel, v);
  }

  #handles(m, v) {
    const pts = m.pts.map((q) => ({ x: v.toX(q.x), y: v.toY(q.y) }));
    const hs = [];
    if (['arrow', 'line', 'zone', 'shade', 'barrier', 'circle'].includes(m.kind) && pts.length === 2) {
      hs.push({ k: 'p', i: 0, ...pts[0] }, { k: 'p', i: 1, ...pts[1] });
      if (m.kind === 'arrow' || m.kind === 'line') {
        const c = m.curve || 0; const a = pts[0]; const b = pts[1];
        const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2;
        // the curve's midpoint: control point pulled halfway
        hs.push({ k: 'curve', x: mx - (b.y - a.y) * c * 0.5, y: my + (b.x - a.x) * c * 0.5 });
      }
    } else if ((m.kind === 'spot' || m.kind === 'beam') && pts.length) {
      const pt = { ...pts[0], yN: m.tilt ?? m.pts[0].y };
      const { rx } = ellipseFor(m, pt, v.scale);
      hs.push({ k: 'r', x: pts[0].x + rx, y: pts[0].y });
      if (m.kind === 'beam') hs.push({ k: 'h', x: pts[0].x, y: pts[0].y - (m.h ?? MDEF.beamH) * v.scale });
    } else if (m.kind === 'note') {
      const L = noteLayout(this.ctx, m, pts, v.scale, v);
      hs.push({ k: 'bw', x: L.x + L.w, y: L.y + L.h / 2 });
      hs.push({ k: 'scale', x: L.x + L.w, y: L.y + L.h, ax: L.x, ay: L.y });
      if (pts[1]) hs.push({ k: 'p', i: 1, ...pts[1] });
    } else if (m.kind === 'label' || m.kind === 'player' || m.kind === 'item' || m.kind === 'pen') {
      const bb = this.#bbox(m, v);
      if (bb) hs.push({ k: 'scale', x: bb.x + bb.w, y: bb.y + bb.h, ax: m.kind === 'pen' ? bb.x : bb.x + bb.w / 2, ay: m.kind === 'pen' ? bb.y : bb.y + bb.h / 2 });
      if (m.kind === 'label' && pts[1]) hs.push({ k: 'p', i: 1, ...pts[1] });
    }
    return hs;
  }

  #drawHandles(ctx, m, v) {
    const d = this.dpr || 1;
    const pts = m.pts.map((q) => ({ x: v.toX(q.x), y: v.toY(q.y) }));
    // selection outline
    ctx.save();
    ctx.setLineDash([4 * d, 3 * d]); ctx.lineWidth = 1 * d; ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    const bb = this.#bbox(m, v);
    if (bb) { ctx.strokeRect(bb.x - 4 * d, bb.y - 4 * d, bb.w + 8 * d, bb.h + 8 * d); ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.lineDashOffset = 3.5 * d; ctx.strokeRect(bb.x - 4 * d, bb.y - 4 * d, bb.w + 8 * d, bb.h + 8 * d); }
    ctx.restore();
    void pts;
    for (const h of this.#handles(m, v)) {
      if (h.k === 'scale') { // resize: a square corner handle
        ctx.fillStyle = '#ffffff'; ctx.fillRect(h.x - 5 * d, h.y - 5 * d, 10 * d, 10 * d);
        ctx.lineWidth = 1.5 * d; ctx.strokeStyle = '#111111'; ctx.strokeRect(h.x - 5 * d, h.y - 5 * d, 10 * d, 10 * d);
        continue;
      }
      ctx.beginPath(); ctx.arc(h.x, h.y, 5.5 * d, 0, Math.PI * 2); ctx.fillStyle = '#ffffff'; ctx.fill();
      ctx.lineWidth = 1.5 * d; ctx.strokeStyle = '#111111'; ctx.stroke();
      if (h.k === 'curve') { ctx.beginPath(); ctx.arc(h.x, h.y, 2 * d, 0, Math.PI * 2); ctx.fillStyle = '#111111'; ctx.fill(); }
    }
  }

  #bbox(m, v) {
    const pts = m.pts.map((q) => ({ x: v.toX(q.x), y: v.toY(q.y) }));
    if (!pts.length) return null;
    const S = v.scale;
    if (m.kind === 'note') { const L = noteLayout(this.ctx, m, pts, S, v); return { x: L.x, y: L.y, w: L.w, h: L.h }; }
    if (m.kind === 'player') { const r = (m.r ?? MDEF.player) * S * (m.w || 1); return { x: pts[0].x - r, y: pts[0].y - r, w: r * 2, h: r * 2 }; }
    if (m.kind === 'item') { const b = itemBox(m, pts[0], S); return { x: pts[0].x - b.w / 2, y: pts[0].y - b.h / 2, w: b.w, h: b.h }; }
    if (m.kind === 'spot' || m.kind === 'beam') { const { rx, ry } = ellipseFor(m, { ...pts[0], yN: m.tilt ?? m.pts[0].y }, S); const h = m.kind === 'beam' ? (m.h ?? MDEF.beamH) * S : 0; return { x: pts[0].x - rx, y: pts[0].y - ry - h, w: rx * 2, h: ry * 2 + h }; }
    if (m.kind === 'label') { const sz = chipSize(S, (m.w || 1) * LABEL_SCALE); this.ctx.save(); this.ctx.font = `800 ${sz}px ${ANNO_FONT}`; const w = this.ctx.measureText(String(m.text || 'LABEL').toUpperCase()).width + sz * 1.1; this.ctx.restore(); return { x: pts[0].x - w / 2, y: pts[0].y - sz * 0.88, w, h: sz * 1.76 }; }
    const path = m.kind === 'arrow' || m.kind === 'line' ? curvePoints(pts, m.curve || 0) : pts;
    const xs = path.map((p) => p.x); const ys = path.map((p) => p.y);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }

  // Pointer position from the canvas's displayed box, so it stays right even
  // before the drawing buffer catches up with a resize.
  #pt(e) {
    const r = this.cv.getBoundingClientRect();
    if (Math.abs(this.cv.offsetWidth * (this.dpr || 1) - this.cv.width) > 2 || Math.abs(this.cv.offsetHeight * (this.dpr || 1) - this.cv.height) > 2) this.layout();
    const nx = r.width ? (e.clientX - r.left) / r.width : 0; const ny = r.height ? (e.clientY - r.top) / r.height : 0;
    return { px: nx * this.cv.width, py: ny * this.cv.height, nx: clamp01(nx), ny: clamp01(ny) };
  }

  #hit(px, py) {
    const f = this.freeze; if (!f) return null;
    const v = this.view; const d = this.dpr || 1;
    const order = paintOrder(f.marks);
    for (let i = order.length - 1; i >= 0; i--) if (hitMark(this.ctx, order[i], v, px, py, 8 * d)) return order[i];
    return null;
  }

  #bindCanvas() {
    const cv = this.cv;
    cv.addEventListener('pointerdown', (e) => this.#down(e));
    cv.addEventListener('pointermove', (e) => this.#move(e));
    cv.addEventListener('pointerup', (e) => this.#up(e));
    cv.addEventListener('pointercancel', (e) => this.#up(e));
    cv.addEventListener('dblclick', (e) => { e.stopPropagation(); if (this.tool === 'zoom') { this.resetZoom(); return; } const { px, py } = this.#pt(e); const m = this.#hit(px, py); if (m) { this.sel = m; this.editText(m); } });
    cv.addEventListener('click', (e) => e.stopPropagation());
    cv.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); const { px, py } = this.#pt(e); const m = this.#hit(px, py); if (m) { this.sel = m; this.draw(); this.#markMenu(e, m); } else this.#canvasMenu(e); });
  }

  #down(e) {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const pt = this.#pt(e); // read where the click landed before anything can shift the layout
    this.pause();
    this.closeEditor();
    this.p.stageEl.focus({ preventScroll: true });
    if (this.tool === 'zoom') { this.drag = { kind: 'zoom', sx: e.clientX, sy: e.clientY, ox: this.zoom.x, oy: this.zoom.y, moved: false, out: e.altKey }; try { this.cv.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ } return; }
    try { this.cv.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
    const v = this.view; const d = this.dpr || 1;
    // handles of the selected mark win over everything
    if (this.sel && this.freeze && this.freeze.marks.includes(this.sel)) {
      const h = this.#handles(this.sel, v).find((q) => Math.hypot(q.x - pt.px, q.y - pt.py) < 10 * d);
      if (h) {
        const m0 = this.sel;
        this.drag = { kind: 'handle', h, m: m0, before: this.#snap(), moved: false, origW: m0.w || 1, origPts: m0.pts.map((q) => ({ ...q })), d0: Math.max(4, Math.hypot(h.x - (h.ax ?? h.x), h.y - (h.ay ?? h.y))) };
        return;
      }
    }
    const tool = this.tool;
    if (tool === 'select' || e.altKey) {
      const m = this.#hit(pt.px, pt.py);
      this.sel = m;
      if (m) this.drag = { kind: 'move', m, start: pt, orig: m.pts.map((q) => ({ ...q })), before: this.#snap(), moved: false, dup: e.altKey };
      else if (this.zoom.z > 1.001) this.drag = { kind: 'zoom', sx: e.clientX, sy: e.clientY, ox: this.zoom.x, oy: this.zoom.y, moved: false, panOnly: true };
      this.#syncBar(); this.draw();
      return;
    }
    const T0 = TOOL_OF[tool];
    // a double-click with a stamp tool must not stack two players on one spot
    const lp = this.lastPlace;
    if (lp && performance.now() - lp.t < 450 && Math.hypot(lp.x - pt.px, lp.y - pt.py) < 8 * d) return;
    // stamp tools (players, items, notes, labels) pick up a mark they land on instead of stacking a new one
    if (T0 && (T0.player != null || T0.item || tool === 'note' || tool === 'label')) {
      const hit = this.#hit(pt.px, pt.py);
      if (hit) { this.sel = hit; this.drag = { kind: 'move', m: hit, start: pt, orig: hit.pts.map((q) => ({ ...q })), before: this.#snap(), moved: false }; this.#syncBar(); this.draw(); return; }
    }
    // a drawing tool: a freeze at this frame is made on demand
    const created = !this.freeze;
    const before = this.#snap();
    this.p.video.pause();
    this.ensureFreeze();
    if (created) { this.renderStrip(); this.p.renderTimelineNow(); }
    const T = TOOL_OF[tool];
    const def = this.#def(tool);
    const base = { id: uid(), color: def.color || this.s.annoMarkColor || 'cyan', w: def.w || 1 };
    if (def.anim) base.anim = def.anim;
    const at = { x: pt.nx, y: pt.ny };
    let m;
    if (T.player != null) m = { ...base, kind: 'player', pts: [at], fill: this.#slot(T.player).fill, label: this.pending[T.player] || '' };
    else if (T.item) m = { ...base, kind: 'item', item: T.item, pts: [at] };
    else if (tool === 'spot' || tool === 'beam') { m = { ...base, kind: tool, pts: [at], r: def.r ?? MDEF.spot, tilt: at.y }; if (tool === 'beam' && def.h != null) m.h = def.h; }
    else if (tool === 'pen') m = { ...base, kind: 'pen', pts: [at], dash: def.dash || undefined };
    else if (tool === 'circle') m = { ...base, kind: 'zone', shape: 'ellipse', pts: [at, { ...at }], dash: def.dash || undefined };
    else if (tool === 'label') m = { ...base, kind: 'label', pts: [at], text: '' };
    else if (tool === 'note') m = { ...base, kind: 'note', pts: [at], text: '', style: def.style || this.s.annoNoteStyle || 'dark', fs: def.fs || this.s.annoNoteSize || 'm', bw: def.bw ?? (v.w > v.h ? 0.3 : 0.62), head: def.head };
    else if (T.motion) m = { ...base, kind: 'arrow', motion: T.motion, pts: [at, { ...at }], dash: T.motion === 'pass' ? true : undefined };
    else m = { ...base, kind: tool, pts: [at, { ...at }], dash: (tool === 'arrow' || tool === 'line' || tool === 'zone') && def.dash ? true : undefined };
    if (tool === 'shade' && def.amount != null) m.amount = def.amount;
    for (const k of Object.keys(m)) if (m[k] === undefined) delete m[k];
    this.draft = m;
    this.drag = { kind: 'draw', m, start: pt, before, created, moved: false };
    this.draw();
  }

  #move(e) {
    const dg = this.drag;
    const pt = this.#pt(e);
    if (!dg) {
      // hover cursor: move over marks in select mode
      const d = this.dpr || 1;
      const onHandle = !!(this.sel && this.freeze && this.freeze.marks.includes(this.sel) && this.#handles(this.sel, this.view).some((q) => Math.hypot(q.x - pt.px, q.y - pt.py) < 10 * d));
      this.cv.toggleClass('is-handle', onHandle);
      this.cv.toggleClass('is-over', !onHandle && (this.tool === 'select' || !!TOOL_OF[this.tool].player || !!TOOL_OF[this.tool].item) && !!this.#hit(pt.px, pt.py));
      return;
    }
    e.preventDefault();
    if (dg.kind === 'zoom') {
      const dx = e.clientX - dg.sx; const dy = e.clientY - dg.sy;
      if (!dg.moved && Math.hypot(dx, dy) < 4) return;
      dg.moved = true;
      if (this.zoom.z > 1.001) { this.zoom.x = dg.ox + dx; this.zoom.y = dg.oy + dy; this.applyZoom(false); }
      return;
    }
    const m = dg.m; const v = this.view;
    const dist = Math.hypot(pt.px - dg.start?.px, pt.py - dg.start?.py);
    if (dg.kind === 'draw') {
      if (dist > 3 * (this.dpr || 1)) dg.moved = true;
      const at = { x: pt.nx, y: pt.ny };
      if (m.kind === 'pen') { const last = m.pts[m.pts.length - 1]; if (Math.hypot(at.x - last.x, at.y - last.y) > 0.004) m.pts.push(at); }
      else if (m.kind === 'spot' || m.kind === 'beam') { const dx = (at.x - m.pts[0].x) * v.w; const dy = (at.y - m.pts[0].y) * v.h; m.r = Math.max(0.02, Math.hypot(dx, dy * 1.9) / v.scale / (m.w || 1)); }
      else if (m.kind === 'player' || m.kind === 'item') m.pts[0] = at;
      else if (m.kind === 'label' || m.kind === 'note') { if (dg.moved) m.pts = [at, { x: dg.start.nx, y: dg.start.ny }]; }
      else m.pts[1] = at;
    } else if (dg.kind === 'move') {
      if (dist > 2 * (this.dpr || 1) && !dg.moved) {
        dg.moved = true;
        if (dg.dup) { const copy = { ...JSON.parse(JSON.stringify(m)), id: uid() }; this.freeze.marks.push(copy); dg.m = copy; this.sel = copy; }
      }
      if (!dg.moved) return;
      const dx = pt.nx - dg.start.nx; const dy = pt.ny - dg.start.ny;
      dg.m.pts = dg.orig.map((q) => ({ x: clamp01(q.x + dx), y: clamp01(q.y + dy) }));
      if (dg.m.tilt != null && (dg.m.kind === 'spot' || dg.m.kind === 'beam')) dg.m.tilt = dg.m.pts[0].y;
    } else if (dg.kind === 'handle') {
      dg.moved = true;
      const h = dg.h;
      if (h.k === 'p') m.pts[h.i] = { x: pt.nx, y: pt.ny };
      else if (h.k === 'r') { const cx = v.toX(m.pts[0].x); m.r = Math.max(0.015, Math.abs(pt.px - cx) / v.scale / (m.w || 1)); }
      else if (h.k === 'bw') { const x0 = v.toX(m.pts[0].x); m.bw = clamp((pt.px - x0) / v.w, 0.08, 0.95); }
      else if (h.k === 'h') { const y0 = v.toY(m.pts[0].y); m.h = clamp((y0 - pt.py) / v.scale, 0.05, 1.2); }
      else if (h.k === 'scale') {
        const f = clamp(Math.hypot(pt.px - h.ax, pt.py - h.ay) / dg.d0, 0.2, 8);
        if (m.kind === 'pen') m.pts = dg.origPts.map((q) => ({ x: clamp01((h.ax + (q.x * v.w - h.ax) * f) / v.w), y: clamp01((h.ay + (q.y * v.h - h.ay) * f) / v.h) }));
        else m.w = clamp(Math.round(dg.origW * f * 100) / 100, 0.3, 6);
      }
      else if (h.k === 'curve') {
        const a = { x: v.toX(m.pts[0].x), y: v.toY(m.pts[0].y) }; const b = { x: v.toX(m.pts[1].x), y: v.toY(m.pts[1].y) };
        const dx = b.x - a.x; const dy = b.y - a.y; const L2 = dx * dx + dy * dy || 1;
        const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2;
        // signed distance of the pointer from the chord, as a chord fraction (x2 for the control point)
        const cross = ((pt.px - mx) * -dy + (pt.py - my) * dx) / L2;
        m.curve = clamp(Math.round(cross * 2 * 100) / 100, -0.8, 0.8);
        if (Math.abs(m.curve) < 0.03) m.curve = 0;
      }
    }
    this.draw();
  }

  #up(e) {
    const dg = this.drag; this.drag = null;
    try { this.cv.releasePointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
    if (!dg) return;
    if (dg.kind === 'zoom') {
      if (!dg.moved && !dg.panOnly) this.zoomAt(e.clientX, e.clientY, dg.out || e.altKey ? 0.5 : 2, true);
      if (!dg.moved && dg.panOnly) { this.sel = null; this.#syncBar(); this.draw(); }
      return;
    }
    if (dg.kind === 'draw') {
      const m = dg.m; this.draft = null;
      const v = this.view;
      let keep = true;
      const span = m.pts.length > 1 ? Math.hypot((m.pts[1].x - m.pts[0].x) * v.w, (m.pts[1].y - m.pts[0].y) * v.h) / v.h : 0;
      if (['arrow', 'line', 'zone', 'shade', 'barrier'].includes(m.kind) && span < 0.015) keep = false;
      if (m.kind === 'pen' && m.pts.length < 3) keep = false;
      if (m.kind === 'note' && m.pts.length > 1) { const box = m.pts[0]; m.pts = [box, m.pts[1]]; }
      if (!keep) {
        // a click with a line tool: nothing drawn; a freeze made only for it goes too
        if (dg.created && this.freeze && !this.freeze.marks.length) { const i = this.freezes.indexOf(this.freeze); if (i >= 0) this.freezes.splice(i, 1); this.freeze = null; this.renderStrip(); this.p.renderTimelineNow(); }
        this.draw(); return;
      }
      this.undoStack.push(dg.before); if (this.undoStack.length > 100) this.undoStack.shift(); this.redoStack = [];
      this.freeze.marks.push(m);
      this.sel = m;
      this.createdFor = dg.created ? m : null;
      this.lastPlace = { t: performance.now(), x: dg.start.px, y: dg.start.py, kind: m.kind };
      if (m.kind === 'pen') m.pts = simplifyPath(m.pts, 0.0012);
      this.changed();
      if (m.kind === 'note' || m.kind === 'label' || (m.kind === 'player' && this.s.annoLabelOnPlace !== false && !m.label)) this.editText(m, true);
      // one-shot tools hand back to Select so the next click can move what you made
      if (m.kind === 'note' || m.kind === 'label') this.tool = 'select';
      this.#syncBar();
      return;
    }
    if (dg.moved) { this.undoStack.push(dg.before); if (this.undoStack.length > 100) this.undoStack.shift(); this.redoStack = []; this.changed({ light: true }); this.#rowsSoon(); }
  }

  // ---- text editing: a shadcn popover under the mark ----------------------------

  editText(m, fresh = false, field = 'label') {
    this.closeEditor();
    if (!HAS_TEXT.has(m.kind)) return;
    const v = this.view; const d = this.dpr || 1;
    const bb = this.#bbox(m, v) || { x: 0, y: 0, w: 0, h: 0 };
    const cvr = this.cv.getBoundingClientRect(); const fr = this.p.stageEl.getBoundingClientRect();
    const pop = this.p.stageEl.createDiv({ cls: 'cvs-anno-pop' });
    const before = this.#snap();
    const isNote = m.kind === 'note'; const isPlayer = m.kind === 'player' && field !== 'text';
    let input;
    if (isNote) {
      input = pop.createEl('textarea', { cls: 'cvs-anno-textarea', attr: { rows: '3', spellcheck: 'true', placeholder: 'First line is the heading, like "#18 Avery"' } });
      input.value = m.text || '';
    } else {
      input = pop.createEl('input', { type: 'text', cls: 'cvs-anno-input', attr: { spellcheck: 'false', maxlength: isPlayer ? '3' : '60', placeholder: isPlayer ? 'Number or letter' : 'Label text' } });
      input.value = isPlayer ? (m.label || '') : (m.text || '');
    }
    const row = pop.createDiv({ cls: 'cvs-anno-pop-row' });
    if (isNote) {
      const seg = (vals, key, cur, labels) => { const g = row.createDiv({ cls: 'cvs-anno-seg' }); vals.forEach((val, i) => { const b = g.createEl('button', { text: labels[i], cls: val === cur ? 'is-on' : '' }); b.onmousedown = (e) => e.preventDefault(); b.onclick = (e) => { e.preventDefault(); m[key] = val; if (key === 'style') this.s.annoNoteStyle = val; if (key === 'fs') this.s.annoNoteSize = val; this.p.plugin.saveSoon(); g.querySelectorAll('button').forEach((x, k) => x.toggleClass('is-on', vals[k] === val)); this.draw(); }; }); };
      seg(['s', 'm', 'l', 'xl'], 'fs', m.fs || 'm', ['S', 'M', 'L', 'XL']);
      seg(['dark', 'light', 'color', 'clear'], 'style', m.style || 'dark', ['Dark', 'Light', 'Color', 'Clear']);
      const hd = row.createEl('button', { cls: 'cvs-anno-pop-btn' + (m.head !== false ? ' is-on' : ''), attr: { 'aria-label': 'First line is a bold heading' } });
      setIcon(hd, 'heading'); hd.onmousedown = (e) => e.preventDefault();
      hd.onclick = (e) => { e.preventDefault(); m.head = m.head === false; hd.toggleClass('is-on', m.head !== false); this.draw(); };
      const presets = row.createEl('button', { cls: 'cvs-anno-pop-btn', attr: { 'aria-label': 'Quick notes' } });
      setIcon(presets, 'list-plus'); presets.onmousedown = (e) => e.preventDefault();
      presets.onclick = (e) => {
        e.preventDefault();
        const menu = new CvsMenu(this.p);
        const list = String(this.s.annoNotePresets || '').split('\n').map((x) => x.trim()).filter(Boolean);
        if (!list.length) menu.addItem((x) => x.setTitle('Add quick notes in Settings').setDisabled(true));
        for (const q of list) menu.addItem((x) => x.setTitle(q).onClick(() => { const cur = input.value.trim(); input.value = cur ? cur + '\n' + q : q; m.text = input.value; this.draw(); setTimeout(() => input.focus(), 0); }));
        menu.showBelow(presets, 'end');
      };
    } else if (isPlayer) {
      const g = row.createDiv({ cls: 'cvs-anno-chiprow' });
      for (const lab of ['C', 'LW', 'RW', 'LD', 'RD', 'G', 'X', 'O']) { const b = g.createEl('button', { text: lab }); b.onmousedown = (e) => e.preventDefault(); b.onclick = (e) => { e.preventDefault(); input.value = lab; m.label = lab; this.draw(); finish(true); }; }
    }
    const hint = pop.createDiv({ cls: 'cvs-anno-pop-hint', text: isNote ? 'Enter for a new line  ·  Cmd+Enter or Esc to finish' : 'Enter to finish' });
    void hint;
    // place under (or above) the mark, inside the frame
    // canvas px to screen, through any zoom
    const sx = (x) => cvr.left + (x / this.cv.width) * cvr.width - fr.left; const sy = (y) => cvr.top + (y / this.cv.height) * cvr.height - fr.top;
    void d;
    pop.style.left = clamp(sx(bb.x), 6, Math.max(6, fr.width - 330)) + 'px';
    pop.style.top = clamp(sy(bb.y + bb.h) + 8, 4, Math.max(4, fr.height - 60)) + 'px';
    requestAnimationFrame(() => { const pr = pop.getBoundingClientRect(); if (pr.bottom > fr.bottom - 4) pop.style.top = Math.max(4, sy(bb.y) - pr.height - 8) + 'px'; });
    const apply = () => { if (isPlayer) m.label = input.value.trim().slice(0, 3).toUpperCase(); else m.text = input.value; this.draw(); };
    input.addEventListener('input', apply);
    const finish = (commit) => {
      if (!this.editor || this.editor.pop !== pop) return;
      this.editor = null;
      pop.remove();
      if (!commit) { this.#applySnap(before); this.freeze = this.freezeAt(this.p.time); this.sel = null; this.changed(); return; }
      apply();
      const empty = isPlayer ? false : !String(m.text || '').trim();
      if (empty && (m.kind === 'note' || m.kind === 'label')) {
        // an empty note or label is not worth keeping
        const f = this.freeze; if (f) { const i = f.marks.indexOf(m); if (i >= 0) f.marks.splice(i, 1); }
        this.sel = null;
        if (fresh) {
          this.undoStack.pop(); // the step that added it
          // a freeze made only for this note goes too
          if (f && !f.marks.length && this.createdFor === m) { const i = this.freezes.indexOf(f); if (i >= 0) this.freezes.splice(i, 1); this.freeze = null; }
        }
      } else if (!fresh && this.#snap() !== before) { this.undoStack.push(before); this.redoStack = []; }
      this.changed();
      this.p.stageEl.focus({ preventScroll: true });
    };
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); finish(true); }
      else if (e.key === 'Enter' && (!isNote || e.metaKey || e.ctrlKey)) { e.preventDefault(); finish(true); }
    });
    input.addEventListener('blur', () => setTimeout(() => { if (this.editor && this.editor.pop === pop && !pop.contains(document.activeElement)) finish(true); }, 120));
    pop.addEventListener('pointerdown', (e) => e.stopPropagation());
    pop.addEventListener('click', (e) => e.stopPropagation());
    this.editor = { pop, mark: m, finish };
    setTimeout(() => { input.focus(); if (!fresh) input.select(); }, 20);
  }

  closeEditor() { if (this.editor) this.editor.finish(true); }

  // ---- mark ops -------------------------------------------------------------------

  deleteSel() {
    if (!this.sel || !this.freeze) return;
    const m = this.sel;
    this.#mutate(() => { const i = this.freeze.marks.indexOf(m); if (i >= 0) this.freeze.marks.splice(i, 1); this.sel = null; });
  }
  duplicate() {
    if (!this.sel || !this.freeze) return;
    const src = this.sel;
    this.#mutate(() => { const c = { ...JSON.parse(JSON.stringify(src)), id: uid() }; c.pts = c.pts.map((q) => ({ x: clamp01(q.x + 0.025), y: clamp01(q.y + 0.025) })); this.freeze.marks.push(c); this.sel = c; });
  }
  copy(all = false) {
    const f = this.freeze; if (!f) return;
    const list = all || !this.sel ? f.marks : [this.sel];
    if (!list.length) return;
    this.p.plugin.annoClipboard = JSON.parse(JSON.stringify(list));
    this.p.flash(`Copied ${list.length} mark${list.length === 1 ? '' : 's'}`);
  }
  paste() {
    const clip = this.p.plugin.annoClipboard; if (!clip || !clip.length) return;
    this.#mutate(() => { const f = this.ensureFreeze(); for (const mk of clip) f.marks.push({ ...JSON.parse(JSON.stringify(mk)), id: uid() }); this.sel = f.marks[f.marks.length - 1]; });
    this.p.flash('Pasted');
  }
  order(m, front) {
    const f = this.freeze; if (!f) return;
    this.#mutate(() => { const i = f.marks.indexOf(m); if (i < 0) return; f.marks.splice(i, 1); if (front) f.marks.push(m); else f.marks.unshift(m); });
  }
  nudge(dx, dy) {
    const m = this.sel; if (!m) return;
    this.#mutate(() => { m.pts = m.pts.map((q) => ({ x: clamp01(q.x + dx), y: clamp01(q.y + dy) })); if (m.tilt != null) m.tilt = m.pts[0].y; }, { light: true });
  }

  #markMenu(e, m) {
    const menu = new CvsMenu(this.p);
    if (HAS_TEXT.has(m.kind)) menu.addItem((x) => x.setTitle(m.kind === 'player' ? 'Edit number / letter' : m.kind === 'note' ? 'Edit note' : m.text ? 'Edit label' : 'Add a label').setIcon('type').setShortcut('Enter').onClick(() => this.editText(m)));
    if (m.kind === 'player') menu.addItem((x) => x.setTitle(m.text ? 'Edit caption' : 'Add a caption under it').setIcon('text').onClick(() => this.editText(m, false, 'text')));
    menu.addItem((x) => x.setTitle('Duplicate').setIcon('copy').setShortcut('⌘D').onClick(() => this.duplicate()));
    menu.addItem((x) => x.setTitle('Copy').setIcon('clipboard-copy').setShortcut('⌘C').onClick(() => this.copy()));
    menu.addSeparator();
    const anim = m.anim || defaultAnim(m.kind);
    for (const [k, lab] of [['draw', 'Appears: draws on'], ['pop', 'Appears: pops in'], ['fade', 'Appears: fades in'], ['none', 'Appears: instantly']]) {
      if (k === 'draw' && !['arrow', 'line', 'pen', 'barrier'].includes(m.kind)) continue;
      menu.addItem((x) => x.setTitle(lab).setChecked(anim === k).onClick(() => this.#mutate(() => { m.anim = k; }, { light: true })));
    }
    if (m.kind === 'zone' || m.kind === 'shade') menu.addItem((x) => x.setTitle(m.shape === 'ellipse' ? 'Make it a rectangle' : 'Make it an ellipse').setIcon('shapes').onClick(() => this.#mutate(() => { m.shape = m.shape === 'ellipse' ? undefined : 'ellipse'; if (!m.shape) delete m.shape; })));
    if (m.kind === 'item' && m.item === 'net') menu.addItem((x) => x.setTitle('Flip').setIcon('flip-horizontal').onClick(() => this.#mutate(() => { m.flip = !m.flip; })));
    if (m.kind === 'arrow') {
      menu.addSeparator();
      for (const [mo, lab] of [[undefined, 'Arrow'], ['pass', 'Pass (dashed)'], ['shoot', 'Shoot (double line)'], ['puck', 'Skate with the puck']]) {
        menu.addItem((x) => x.setTitle(lab).setChecked((m.motion || undefined) === mo).onClick(() => this.#mutate(() => { if (mo) m.motion = mo; else delete m.motion; if (mo === 'pass') m.dash = true; else delete m.dash; })));
      }
    }
    if (m.kind === 'arrow' || m.kind === 'line') menu.addItem((x) => x.setTitle('Straighten').setIcon('move-diagonal').setDisabled(!m.curve).onClick(() => this.#mutate(() => { m.curve = 0; })));
    if (m.w && m.w !== 1) menu.addItem((x) => x.setTitle('Reset size').setIcon('scaling').onClick(() => this.#mutate(() => { m.w = 1; })));
    menu.addItem((x) => x.setTitle('Make this the default for new ones').setIcon('pin').onClick(() => this.makeDefault(m)));
    if ((m.kind === 'note' || m.kind === 'label') && m.pts[1]) menu.addItem((x) => x.setTitle('Remove the pointer line').setIcon('unlink').onClick(() => this.#mutate(() => { m.pts = [m.pts[0]]; })));
    menu.addSeparator();
    menu.addItem((x) => x.setTitle('Bring to front').setIcon('bring-to-front').onClick(() => this.order(m, true)));
    menu.addItem((x) => x.setTitle('Send to back').setIcon('send-to-back').onClick(() => this.order(m, false)));
    menu.addSeparator();
    menu.addItem((x) => x.setTitle('Delete').setIcon('trash-2').setShortcut('⌫').onClick(() => this.deleteSel()));
    menu.showAtMouseEvent(e);
  }

  #canvasMenu(e) {
    const menu = new CvsMenu(this.p);
    const f = this.freeze;
    menu.addItem((x) => x.setTitle(f ? 'Remove this freeze' : 'Freeze this frame').setIcon('snowflake').setShortcut('F').onClick(() => this.toggleFreeze()));
    menu.addItem((x) => x.setTitle('Paste marks').setIcon('clipboard-paste').setShortcut('⌘V').setDisabled(!(this.p.plugin.annoClipboard || []).length).onClick(() => this.paste()));
    menu.addItem((x) => x.setTitle('Clear this freeze').setIcon('eraser').setDisabled(!f || !f.marks.length).onClick(() => this.#mutate(() => { f.marks = []; this.sel = null; })));
    menu.showAtMouseEvent(e);
  }

  // ---- playing the clip timeline: video, then each hold for its length --------------

  togglePreview() { if (this.playing) this.pause(); else this.play(true); }
  togglePlay() { if (this.playing) this.pause(); else this.play(false); }
  toggleLoop() { this.loopClip = !this.loopClip; this.p.flash(this.loopClip ? 'Loop the clip: on' : 'Loop the clip: off'); this.p.syncLoopIcon(); this.draw(); }

  play(fromStart = false) {
    if (this.dead) return;
    this.closeEditor();
    this.sel = null;
    const tl = this.tl(); this.playTl = tl;
    if (fromStart || this.outT >= tl.T - 0.02) this.outT = 0;
    const v = this.p.video;
    this.playing = { rate: v.playbackRate, loop: v.loop, seg: null, holdStart: 0 };
    v.playbackRate = 1; v.loop = false; // the export plays at 1x
    this.#enter();
    const step = (now) => {
      if (!this.playing || this.dead) return;
      const pv = this.playing; const g = pv.seg; const T = this.playTl.T;
      if (!g) { this.pause(); return; }
      let done = false;
      if (g.kind === 'hold') { this.outT = g.o0 + (now - pv.holdStart) / 1000; if (this.outT >= g.o1) { this.outT = g.o1; done = true; } }
      else {
        const t = v.currentTime; this.outT = Math.min(g.o1, g.o0 + Math.max(0, t - g.s0));
        if (t >= g.s1 - 1e-3 || v.ended || this.outT >= g.o1 - 1e-4) { this.outT = g.o1; done = true; }
      }
      if (done) {
        if (this.outT >= T - 1e-4) {
          if (this.loopClip) { this.outT = 0; this.#enter(); }
          else { this.pause(); return; }
        } else this.#enter();
      }
      this.renderTl(this.playTl); this.draw();
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
    this.#syncBar(); this.p.syncPlayIcon(); this.draw();
  }

  #enter() {
    const g = this.segAt(this.outT, this.playTl); const pv = this.playing; const v = this.p.video;
    pv.seg = g; if (!g) return;
    if (g.kind === 'hold') {
      v.pause(); this.syncing = true; this.p.seek(g.s0); this.syncing = false;
      pv.holdStart = performance.now() - (this.outT - g.o0) * 1000;
    } else {
      this.syncing = true; this.p.seek(g.s0 + (this.outT - g.o0)); this.syncing = false;
      v.play().catch(() => {});
    }
  }

  pause() {
    if (!this.playing) return;
    const pv = this.playing; this.playing = null;
    cancelAnimationFrame(this.raf);
    const v = this.p.video; v.pause(); v.playbackRate = pv.rate || 1; v.loop = !!pv.loop;
    this.freeze = undefined; // refresh for wherever we stopped
    const tl = this.tl();
    const src = this.srcAt(this.outT, tl);
    this.syncing = true; this.p.seek(src); this.syncing = false;
    this.syncFromOut(tl);
    this.#syncBar(); this.p.syncPlayIcon();
  }
  // (kept for callers from 3.8: preview was the old name)
  stopPreview() { this.pause(); }

  // Transport buttons while annotating.
  go(cmd) {
    const tl = this.tl();
    if (cmd === 'start') this.seekOut(0);
    else if (cmd === 'end') this.seekOut(tl.T);
    else if (cmd === 'prev') this.stepFreeze(-1);
    else if (cmd === 'next') this.stepFreeze(1);
    else if (cmd === 'back') this.stepFrame(-1);
    else if (cmd === 'fwd') this.stepFrame(1);
  }

  // ---- zoom: pinch / Cmd+scroll anywhere on the picture, the Zoom tool, pan by
  // two-finger scroll or drag while zoomed (hold Option to scrub instead) --------

  applyZoom(animate) {
    if (!this.zoom || !this.cv) return;
    const fr = this.p.frameEl; const W = fr.clientWidth; const H = fr.clientHeight; const z = this.zoom;
    z.x = clamp(z.x, W - W * z.z, 0); z.y = clamp(z.y, H - H * z.z, 0);
    const t = z.z > 1.001 ? `translate(${z.x}px, ${z.y}px) scale(${z.z})` : '';
    const tr = animate ? 'transform 200ms cubic-bezier(0.2, 0, 0, 1)' : 'none';
    const v = this.p.video;
    v.style.transformOrigin = '0 0'; v.style.transition = tr; v.style.transform = t;
    this.cv.style.transformOrigin = `${-(parseFloat(this.cv.style.left) || 0)}px ${-(parseFloat(this.cv.style.top) || 0)}px`;
    this.cv.style.transition = tr; this.cv.style.transform = t;
    const on = z.z > 1.001;
    this.zoomChip.toggleClass('is-on', on); this.zoomChip.setText(`${Math.round(z.z * 100)}%`);
    this.p.stageEl.toggleClass('is-zoomed', on);
    // a scrub while zoomed shows its frames clipped to the card
    this.p.scrubber.clipEl = on ? this.p.frameEl : null;
    this.draw();
  }
  zoomAt(clientX, clientY, factor, animate = false) {
    const fr = this.p.frameEl.getBoundingClientRect(); const z = this.zoom;
    const px = clientX - fr.left - this.p.frameEl.clientLeft; const py = clientY - fr.top - this.p.frameEl.clientTop;
    const z1 = clamp(z.z * factor, 1, 8);
    z.x = px - (px - z.x) * (z1 / z.z); z.y = py - (py - z.y) * (z1 / z.z); z.z = z1;
    if (z1 <= 1.001) { z.x = 0; z.y = 0; z.z = 1; }
    this.applyZoom(animate);
  }
  resetZoom() { this.zoom = { z: 1, x: 0, y: 0 }; this.applyZoom(true); }
  #wheel(e) {
    if (this.dead) return;
    if (e.ctrlKey || e.metaKey) {
      // trackpad pinch arrives as ctrl + wheel
      e.preventDefault(); e.stopPropagation();
      this.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * (e.ctrlKey ? 0.012 : 0.004)));
      return;
    }
    if (this.zoom.z > 1.001 && !e.altKey) {
      e.preventDefault(); e.stopPropagation();
      this.zoom.x -= e.deltaX; this.zoom.y -= e.deltaY; this.applyZoom(false);
    }
  }

  exportAndClose() {
    const tag = this.tag;
    this.close();
    void this.p.exportClips([tag]);
  }

  // ---- keys (annotate mode first, then the player's own) ----------------------------

  handleKey(e) {
    const k = e.key; const mod = e.metaKey || e.ctrlKey;
    if (mod && (k === 'z' || k === 'Z')) { if (e.shiftKey) this.redo(); else this.undo(); return true; }
    if (mod && (k === 'y' || k === 'Y')) { this.redo(); return true; }
    if (mod && (k === 'd' || k === 'D')) { this.duplicate(); return true; }
    if (mod && (k === 'c' || k === 'C')) { if (this.freeze) { this.copy(); return true; } return false; }
    if (mod && (k === 'v' || k === 'V')) { this.paste(); return true; }
    if (mod && (k === 'e' || k === 'E')) { this.exportAndClose(); return true; }
    if (mod && k === '0') { this.resetZoom(); return true; }
    if (mod || e.altKey) return false;
    if (k === 'Escape') {
      if (this.playing) { this.pause(); return true; }
      if (this.tool !== 'select') { this.setTool('select'); return true; }
      if (this.sel) { this.sel = null; this.draw(); this.#syncBar(); return true; }
      if (this.zoom.z > 1.001) { this.resetZoom(); return true; }
      this.close(); return true;
    }
    if (k === ' ' || k === 'k' || k === 'K') { this.togglePlay(); return true; }
    if (k === 'Backspace' || k === 'Delete') { if (this.sel) { this.deleteSel(); return true; } return true; }
    if (k === 'Enter') { if (this.sel) { this.editText(this.sel); return true; } return true; }
    if (this.sel && k.startsWith('Arrow')) {
      const st = (e.shiftKey ? 10 : 1) / 1000;
      if (k === 'ArrowLeft') this.nudge(-st, 0); else if (k === 'ArrowRight') this.nudge(st, 0); else if (k === 'ArrowUp') this.nudge(0, -st); else this.nudge(0, st);
      return true;
    }
    if (k === 'ArrowLeft') { if (e.shiftKey) this.seekOut(this.outT - this.p.frameSec * (this.s.stepFramesShift || 10)); else this.stepFrame(-1); return true; }
    if (k === 'ArrowRight') { if (e.shiftKey) this.seekOut(this.outT + this.p.frameSec * (this.s.stepFramesShift || 10)); else this.stepFrame(1); return true; }
    if (k === 'ArrowUp') { this.stepFreeze(-1); return true; }
    if (k === 'ArrowDown') { this.stepFreeze(1); return true; }
    if (k === 'Home') { this.seekOut(0); return true; }
    if (k === 'End') { this.seekOut(this.tl().T); return true; }
    if (k === 'j' || k === 'J' || k === 'l' || k === 'L') { const d = (k === 'j' || k === 'J' ? -1 : 1) * (e.shiftKey ? this.s.skipLarge : this.s.skipSmall); this.pause(); this.seekOut(this.outT + d); return true; }
    if (k === 'f' || k === 'F') { this.toggleFreeze(); return true; }
    if (k === 'p' || k === 'P') { this.togglePreview(); return true; }
    const lk = k.toLowerCase();
    const t = ANNO_TOOLS.find((x) => x && x.key === lk);
    if (t) { this.setTool(t.id); return true; }
    // keys that would leave the clip or add one are swallowed while annotating
    if (['x', 'g', 'u', 'y'].includes(lk)) return true;
    return false;
  }

  close(silent = false) {
    if (this.dead) return;
    this.closeEditor();
    this.pause();
    this.zoom = { z: 1, x: 0, y: 0 }; this.applyZoom(false);
    const v = this.p.video; v.style.transform = ''; v.style.transformOrigin = ''; v.style.transition = '';
    this.p.stageEl.removeClass('is-zoomed');
    this.dead = true;
    clearTimeout(this.rowsTimer);
    if (this.ro) this.ro.disconnect();
    this.p.video.removeEventListener('loadedmetadata', this.onMeta);
    this.p.stageEl.removeEventListener('wheel', this.onWheel, { capture: true });
    this.cv.remove(); this.hud.remove(); this.host.remove(); this.zoomChip.remove(); this.tlLayer.remove();
    this.p.view.contentEl.removeClass('cvs-annotating');
    if (Array.isArray(this.tag.freezes) && !this.tag.freezes.length) delete this.tag.freezes;
    if (!silent) this.p.onAnnotatorClosed(this.returnT); else this.p.anno = null;
  }
}

// ============================================================================
// the player: one per open video. Custom chrome over the page's <video>.
// ============================================================================

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
// Sidebar columns. `title` fills the remaining width unless the user sized it.
// The three timecode columns collapse to their icon on a header click.
const COLUMNS = [
  { key: 'title', label: 'Clips', icon: null, w: 0, min: 60, auto: true, max: 320 }, // auto-fits its contents until sized by hand
  { key: 'tags', label: 'Tags', icon: 'tag', w: 0, min: 48, auto: true, max: 240 }, // the Tags property: "#good #d-zone"
  // timecode columns default to exactly one "00:00:00" (or "00:00:00.0" at
  // one decimal) in the cell font, measured, and cannot go narrower than that
  { key: 't', label: 'Start', icon: 'clock', w: 0, min: 40, time: true },
  { key: 'in', label: 'In', icon: 'log-in', w: 0, min: 40, time: true },
  { key: 'out', label: 'Out', icon: 'log-out', w: 0, min: 40, time: true },
];
const FIELD_ORDER = ['title', 'tags', 'time', 'in', 'out'];
const CHECK_W = 22; // px track for the checkbox column
const TAGS_AUTO_MAX = 280;
const SIDEBAR_MIN = 28;   // px; below this use S to hide it outright
const STAGE_MIN = 160;    // px of video pane that must remain
const CELL_OF = { title: 'cvs-title', tags: 'cvs-tags', time: 'cvs-t', in: 'cvs-in', out: 'cvs-out' };
const fieldOfCell = (cell) => cell.classList.contains('cvs-tags') ? 'tags' : cell.classList.contains('cvs-t') ? 'time' : cell.classList.contains('cvs-in') ? 'in' : cell.classList.contains('cvs-out') ? 'out' : 'title';

class Player {
  constructor(plugin, view, video, absPath) {
    this.plugin = plugin;
    this.app = plugin.app;
    this.view = view;
    this.video = video;
    this.absPath = absPath;
    this.vaultPath = this.#vaultPathOf(absPath);
    this.tags = [];
    this.checked = new Set(); // clip objects ticked for export (per tab, not saved)
    this.selected = -1;
    this.current = -1;
    this.editing = null;
    this.undo = [];
    this.dead = false;
    this.saveTimer = 0;
    this.posTimer = 0;
    this.unbinders = [];
    this.raf = 0;
    // a #/i/<id>&t=..&clip=.. link: where to start (wins over the resume position)
    this.link = HOST.takeLink ? HOST.takeLink(this.vaultPath) : null;
    this.#build();
    this.scrubber = new Scrubber(plugin, video, absPath, this.stageEl);
    plugin.scrubbers.set(video, this.scrubber);
    void this.load();
  }

  get s() { return this.plugin.settings; }
  get duration() { const d = this.video.duration; return Number.isFinite(d) ? d : 0; }
  get time() { return this.scrubber && this.scrubber.pos != null ? this.scrubber.pos : this.video.currentTime; }
  get frameSec() { return this.scrubber ? this.scrubber.frameSec : 1 / 30; }

  #vaultPathOf(abs) {
    const base = this.app.vault.adapter.getBasePath ? this.app.vault.adapter.getBasePath() : '';
    if (base && abs.startsWith(base)) return normalizePath(abs.slice(base.length).replace(/^\/+/, ''));
    return null;
  }

  notePath() {
    if (!this.vaultPath) return null;
    const stem = this.vaultPath.replace(/\.[^./]+$/, '');
    const suffix = this.s.tagNoteSuffix || '.clips';
    if (this.s.tagFolder) return normalizePath(`${this.s.tagFolder}/${baseName(stem)}${suffix}.md`);
    return normalizePath(`${stem}${suffix}.md`);
  }

  #on(node, ev, fn, opts) { node.addEventListener(ev, fn, opts); this.unbinders.push(() => node.removeEventListener(ev, fn, opts)); }

  // ---- DOM -----------------------------------------------------------------

  #build() {
    const v = this.video;
    const content = this.view.contentEl;
    content.addClass('cvs-content');
    v.controls = false;
    v.removeAttribute('controls');
    v.addClass('cvs-video');

    const root = content.createDiv({ cls: 'cvs-root' });
    this.rootEl = root;

    // sidebar
    const side = root.createDiv({ cls: 'cvs-side' });
    this.sideEl = side;
    // one scroll container (both axes) with a sticky header row
    // the action row on top (the same 44px header row as the file tree's; Add clip first)
    const bar = side.createDiv({ cls: 'cvs-side-bar' });
    this.addBtn = this.#btn(bar, 'plus', 'Add clip at current time (T)', () => this.addAtCurrent());
    this.delBtn = this.#btn(bar, 'minus', 'Delete selected clip (Backspace)', () => this.removeSelected());
    this.delBtn.addClass('cvs-del-btn');
    this.countEl = bar.createSpan({ cls: 'cvs-count' });
    this.annoBtn = this.#btn(bar, 'pencil-ruler', 'Annotate the selected clip (A)', () => this.annotate());
    this.exportBtn = bar.createEl('button', { cls: 'cvs-export-btn', attr: { 'aria-label': 'Export the checked clips as mp4 (E)' } });
    this.exportBtn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); void this.exportChecked(); };
    this.savedEl = bar.createSpan({ cls: 'cvs-saved', attr: { 'aria-label': 'Clips are saved to a note next to the video' } });
    this.moreBtn = this.#btn(bar, 'more-horizontal', 'More', () => this.#menu(this.moreBtn));
    this.moreBtn.addClass('is-right');
    // filter bar: two Select-style dropdowns (Clip names, Tags) plus a clear
    this.filter = { names: new Set(), tags: new Set() };
    const fb = side.createDiv({ cls: 'cvs-filter' });
    this.nameSel = fb.createEl('button', { cls: 'cvs-select', attr: { 'aria-label': 'Filter by clip name' } });
    this.nameSel.onclick = () => this.#filterMenu('names', this.nameSel);
    this.tagSel = fb.createEl('button', { cls: 'cvs-select', attr: { 'aria-label': 'Filter by tags' } });
    this.tagSel.onclick = () => this.#filterMenu('tags', this.tagSel);
    this.clearFilterBtn = this.#btn(fb, 'x', 'Clear filters', () => this.clearFilter());
    this.clearFilterBtn.addClass('cvs-filter-clear');
    this.sortBtn = this.#btn(fb, 'arrow-up-down', 'Sort', () => this.#sortMenu(this.sortBtn));
    this.sortBtn.addClass('cvs-filter-sort');
    this.#syncFilterUi();
    this.tableEl = side.createDiv({ cls: 'cvs-table', attr: { tabindex: '0' } });
    const cols = this.tableEl.createDiv({ cls: 'cvs-cols' });
    this.heads = {};
    this.checkAllEl = cols.createDiv({ cls: 'cvs-col cvs-col-check' }).createSpan({ cls: 'cvs-cb', attr: { role: 'checkbox', 'aria-label': 'Check all shown clips' } });
    this.checkAllEl.onclick = (e) => { e.stopPropagation(); this.checkAll(); };
    for (const col of COLUMNS) {
      const h = cols.createDiv({ cls: `cvs-col cvs-col-${col.key}` });
      h.createSpan({ cls: 'cvs-col-label', text: col.label });
      if (col.icon) { const ic = h.createSpan({ cls: 'cvs-col-icon' }); setIcon(ic, col.icon); h.onclick = (e) => { if (e.target.closest('.cvs-grip')) return; this.toggleColumn(col.key); }; }
      const grip = h.createDiv({ cls: 'cvs-grip' });
      this.#bindColumnResize(grip, col.key);
      this.heads[col.key] = h;
    }
    this.listEl = this.tableEl.createDiv({ cls: 'cvs-list' });
    this.emptyEl = this.listEl.createDiv({ cls: 'cvs-empty', text: 'No clips. Press T (or +) to clip the current frame.' });
    this.handleEl = side.createDiv({ cls: 'cvs-handle' });
    this.#bindResize();

    // main
    const main = root.createDiv({ cls: 'cvs-main' });
    const stage = main.createDiv({ cls: 'cvs-stage', attr: { tabindex: '0' } });
    this.stageEl = stage;
    const holder = v.closest('.video-container');
    // The picture sits in a card sized to its own aspect ratio (border, radius,
    // shadow); the space around it is the app background, not black.
    this.frameEl = stage.createDiv({ cls: 'cvs-frame' });
    this.frameEl.appendChild(v);
    this.frameRO = new ResizeObserver(() => this.#layoutFrame());
    this.narrowRO = new ResizeObserver(() => this.#checkNarrow());
    this.narrowRO.observe(content);
    this.frameRO.observe(stage);
    if (holder && holder !== stage && !holder.childElementCount) holder.remove();
    this.flashEl = stage.createDiv({ cls: 'cvs-flash' });
    const fillExit = stage.createEl('button', { cls: 'cvs-fill-exit', attr: { type: 'button', 'aria-label': 'Exit full screen (Esc)' } });
    setIcon(fillExit, 'minimize');
    fillExit.onclick = (e) => { e.stopPropagation(); this.setFilled(false); };
    this.#keyHint(stage);

    // transport
    this.panelHost = main.createDiv({ cls: 'cvs-panel-host' });
    this.panel = new ButtonPanel(this, this.panelHost);
    const tr = main.createDiv({ cls: 'cvs-transport' });
    const row1 = tr.createDiv({ cls: 'cvs-row1' });
    this.pillEl = row1.createDiv({ cls: 'cvs-pill', attr: { 'aria-label': 'Current clip. Click for the list.' } });
    this.pillEl.onclick = () => this.#chapterMenu(this.pillEl);
    this.timelineEl = row1.createDiv({ cls: 'cvs-timeline' });
    this.tlTrack = this.timelineEl.createDiv({ cls: 'cvs-tl-track' });
    this.tlFill = this.tlTrack.createDiv({ cls: 'cvs-tl-fill' });
    this.tlRange = this.tlTrack.createDiv({ cls: 'cvs-tl-range' });
    this.tlMarks = this.timelineEl.createDiv({ cls: 'cvs-tl-marks' });
    this.tlHead = this.timelineEl.createDiv({ cls: 'cvs-tl-head' });
    this.tlTip = this.timelineEl.createDiv({ cls: 'cvs-tl-tip' });
    const row2 = tr.createDiv({ cls: 'cvs-row2' });
    this.curEl = row2.createSpan({ cls: 'cvs-time cvs-cur', attr: { 'aria-label': 'Click to type a time' } });
    this.curEl.onclick = () => this.#editClock();
    this.durEl = row2.createSpan({ cls: 'cvs-time cvs-dur' });
    const row3 = tr.createDiv({ cls: 'cvs-row3' });
    const left = row3.createDiv({ cls: 'cvs-left' });
    this.loopBtn = this.#btn(left, 'repeat', 'Loop (R)', () => (this.anno ? this.anno.toggleLoop() : this.toggleLoop()));
    const vol = left.createDiv({ cls: 'cvs-vol' });
    this.muteBtn = this.#btn(vol, 'volume-2', 'Mute (M)', () => this.toggleMute());
    this.volEl = vol.createEl('input', { type: 'range', cls: 'cvs-slider', attr: { min: '0', max: '1', step: '0.02' } });
    this.volEl.value = String(this.s.volume);
    this.volEl.oninput = () => { this.video.volume = Number(this.volEl.value); this.video.muted = false; this.s.volume = this.video.volume; this.plugin.saveSoon(); this.#syncVolume(); };
    const sp = left.createDiv({ cls: 'cvs-speed' });
    this.#btn(sp, 'chevron-down', 'Slower (-)', () => this.speedStep(-1)).addClass('cvs-speed-btn');
    this.speedEl = sp.createSpan({ cls: 'cvs-speed-val', attr: { 'aria-label': 'Reset to 1x (0)' } });
    this.speedEl.onclick = () => this.setSpeed(1);
    this.#btn(sp, 'chevron-up', 'Faster (=)', () => this.speedStep(1)).addClass('cvs-speed-btn');
    const center = row3.createDiv({ cls: 'cvs-center' });
    // while annotating these drive the clip's own timeline (prev / next = freezes)
    this.#btn(center, 'skip-back', 'Go to start (Home)', () => (this.anno ? this.anno.go('start') : this.seek(0))).addClass('cvs-b-skip');
    this.#btn(center, 'chevrons-left', 'Previous clip (Up)', () => (this.anno ? this.anno.go('prev') : this.jump(-1))).addClass('cvs-b-jump');
    this.#btn(center, 'chevron-left', 'Back one frame (Left)', () => (this.anno ? this.anno.go('back') : this.step(-this.s.stepFrames)));
    this.playBtn = this.#btn(center, 'play', 'Play / pause (Space)', () => this.togglePlay());
    this.playBtn.addClass('cvs-play');
    this.#btn(center, 'chevron-right', 'Forward one frame (Right)', () => (this.anno ? this.anno.go('fwd') : this.step(this.s.stepFrames)));
    this.#btn(center, 'chevrons-right', 'Next clip (Down)', () => (this.anno ? this.anno.go('next') : this.jump(1))).addClass('cvs-b-jump');
    this.#btn(center, 'skip-forward', 'Go to end (End)', () => (this.anno ? this.anno.go('end') : this.seek(this.duration))).addClass('cvs-b-skip');
    const right = row3.createDiv({ cls: 'cvs-right' });
    this.#btn(right, 'keyboard', 'Shortcuts (?)', () => new ShortcutsModal(this.app, this.plugin).open()).addClass('cvs-keys-btn');
    this.fsBtn = this.#btn(right, 'maximize', 'Full screen (F)', () => this.toggleFullscreen());
    this.fsBtn.addClass('cvs-fs-btn');

    // wire the element
    this.#on(v, 'timeupdate', () => { this.#tick(); if (!this.posTimer) this.posTimer = setTimeout(() => { this.posTimer = 0; this.#savePosition(); }, 5000); });
    this.#on(v, 'seeked', () => this.#tick());
    this.#on(v, 'play', () => this.#syncPlay());
    this.#on(v, 'pause', () => { this.#syncPlay(); this.#savePosition(); });
    this.#on(v, 'ratechange', () => this.#syncSpeed());
    this.#on(v, 'volumechange', () => this.#syncVolume());
    this.#on(v, 'loadedmetadata', () => { this.#onMeta(); this.#layoutFrame(); });
    const onFs = () => { this.stageEl.toggleClass('is-fullscreen', fsElement() === this.stageEl || this.stageEl.hasClass('is-filled')); this.#syncFsBtn(); this.#layoutFrame(); };
    this.#on(document, 'fullscreenchange', onFs);
    this.#on(document, 'webkitfullscreenchange', onFs);
    this.#on(v, 'durationchange', () => this.#renderTimeline());
    this.#on(v, 'ended', () => this.#syncPlay());
    this.#on(stage, 'click', (e) => { if (this.narrow && this.narrowOpen) { this.toggleSidebar(false); return; } if (e.target === v || e.target === stage || e.target === this.frameEl) { if (this.s.clickToPlay) this.togglePlay(); stage.focus({ preventScroll: true }); } });
    this.#on(stage, 'dblclick', (e) => { if (e.target === v || e.target === stage || e.target === this.frameEl) this.toggleFullscreen(); });
    this.#on(this.tableEl, 'click', (e) => { if (e.target === this.listEl || e.target === this.emptyEl || e.target === this.tableEl) { this.select(-1); } });
    this.#on(this.tableEl, 'keydown', (e) => this.#listKey(e));
    this.#bindTimeline();
    this.#applyLayout();
    if (v.readyState >= 1) this.#onMeta();
    this.#syncSpeed(); this.#syncVolume(); this.#syncPlay(); this.#syncLoop();
    v.volume = this.s.volume;
    setTimeout(() => { if (!this.dead) stage.focus({ preventScroll: true }); }, 50);
  }

  // In a Notion embed the keys reach the player only once its frame has focus:
  // a small hint says so until that happens once on this device (or it is closed).
  #keyHint(stage) {
    const KEY = 'cthvw.keyhint.v1';
    let seen = false;
    try { seen = localStorage.getItem(KEY) === '1'; } catch (_) { /* private window */ }
    let coarse = false;
    try { coarse = matchMedia('(pointer: coarse)').matches; } catch (_) { /* old browser */ }
    if (!HOST.embedded || this.s.embedKeyHint === false || seen || coarse || document.hasFocus()) return;
    const hint = stage.createDiv({ cls: 'cvs-keyhint', attr: { role: 'status' } });
    hint.createSpan({ text: 'Click the video to use keyboard shortcuts' });
    const x = hint.createEl('button', { cls: 'cvs-keyhint-x', attr: { type: 'button', 'aria-label': 'Hide this hint' } });
    setIcon(x, 'x');
    const done = () => { try { localStorage.setItem(KEY, '1'); } catch (_) { /* private window */ } hint.remove(); };
    x.onclick = (e) => { e.stopPropagation(); done(); };
    this.#on(window, 'focus', done);
  }

  #btn(parent, icon, tip, fn) {
    const b = parent.createEl('button', { cls: 'cvs-btn clickable-icon', attr: { 'aria-label': tip } });
    setIcon(b, icon);
    b.onclick = (e) => { e.preventDefault(); e.stopPropagation(); fn(e); this.stageEl.focus({ preventScroll: true }); };
    return b;
  }

  refreshChrome() {
    this.narrow = null; this.short = null; this.#checkNarrow();
    if (this.panel) this.panel.render();
    this.#applyLayout();
    this.durEl.setText(fmtClock(this.duration, this.s.clock, this.frameSec));
    this.#tick(true);
    this.render();
  }

  applyTheme() {
    const t = this.s.theme || 'auto';
    const dark = t === 'dark' || (t === 'auto' && document.body.classList.contains('theme-dark'));
    this.view.contentEl.toggleClass('is-dark', dark);
  }

  // Size the frame to the largest box of the video's aspect ratio that fits
  // inside the stage padding, centered by the stage's flex layout.
  #layoutFrame() {
    if (this.dead || !this.frameEl) return;
    const st = this.stageEl;
    const cs = getComputedStyle(st);
    const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const W = st.clientWidth - pad; const H = st.clientHeight - padY;
    // before the video's own size is known, the host's hint (the thumbnail's size) gives the frame its final shape at once
    const vw = this.video.videoWidth || Number(this.video.dataset.cvsW) || 16; const vh = this.video.videoHeight || Number(this.video.dataset.cvsH) || 9;
    if (W <= 0 || H <= 0) return;
    const scale = Math.min(W / vw, H / vh);
    this.frameEl.style.width = Math.floor(vw * scale) + 'px';
    this.frameEl.style.height = Math.floor(vh * scale) + 'px';
  }

  #applyLayout() {
    const c = this.view.contentEl;
    this.applyTheme();
    c.toggleClass('cvs-grid', !!this.s.gridlines);
    c.style.setProperty('--cvs-row-h', (this.s.rowHeight || 28) + 'px');
    c.toggleClass('cvs-stage-edge', this.s.stageStyle === 'edge');
    this.#layoutFrame();
    c.toggleClass('cvs-side-right', this.s.sidebarSide === 'right');
    c.style.setProperty('--cvs-side-w', (this.s.sidebarWidth || 300) + 'px');
    c.toggleClass('cvs-narrow', !!this.narrow);
    const sideShown = this.narrow ? !!this.narrowOpen : !!this.s.sidebarOpen;
    this.sideEl.toggleClass('is-hidden', !sideShown);
    this.#applyColumns();
    if (sideShown !== this.lastSideShown) { this.lastSideShown = sideShown; HOST.onSideChange?.(this, sideShown); } // the top bar's clip log toggle
    this.pillEl.toggleClass('is-hidden', !this.s.showPill || !this.tags.length);
    this.tlMarks.toggleClass('is-hidden', !this.s.showMarks);
  }

  // Column widths and collapse state live in settings.cols; the grid template
  // is built here so the header and every row share one definition.
  #applyColumns() {
    const cols = this.s.cols;
    const editingKey = this.editing && this.editing.field;
    const parts = []; let total = 0;
    const checks = this.s.showCheckboxes !== false;
    this.sideEl.toggleClass('no-checks', !checks);
    if (checks) { parts.push(CHECK_W + 'px'); total += CHECK_W + 8; }
    for (const col of COLUMNS) {
      const c = cols[col.key] || {};
      const collapsed = col.icon && c.show === false && editingKey !== col.key;
      this.sideEl.toggleClass(`col-${col.key}-collapsed`, !!collapsed);
      if (collapsed) { parts.push('20px'); total += 20; continue; }
      let w = Math.max(col.min, c.w || col.w);
      if (col.auto && !c.w) w = this.#autoWidth(col);
      if (col.time) { const tw = this.#timeWidth(); w = Math.max(tw, c.w || tw); }
      parts.push(w + 'px'); total += w;
    }
    this.sideEl.style.setProperty('--cvs-cols', parts.join(' '));
    this.sideEl.style.setProperty('--cvs-table-min', (total + 24 + 8 * (COLUMNS.length - 1)) + 'px');
  }

  // Width that fits the widest cell in the column (or its header), measured
  // with the cell font so it is exact, capped so one long tag list cannot eat
  // the sidebar.
  #autoWidth(col) {
    if (!this.measureCtx) this.measureCtx = document.createElement('canvas').getContext('2d');
    const sample = this.listEl.querySelector('.cvs-cell.cvs-' + col.key) || this.listEl;
    const cs = getComputedStyle(sample);
    this.measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    let w = 0;
    for (const tag of this.tags) {
      const text = col.key === 'tags' ? fmtTagsProp(clipTags(tag)) : (tag.title || 'untitled');
      // annotated clips carry a small freeze badge after the name
      if (text) w = Math.max(w, this.measureCtx.measureText(text).width + (tag.freezes && tag.freezes.length ? 26 : 0));
    }
    const head = this.heads[col.key] && this.heads[col.key].querySelector('.cvs-col-label');
    const hcs = head ? getComputedStyle(head) : cs;
    this.measureCtx.font = `${hcs.fontWeight} ${hcs.fontSize} ${hcs.fontFamily}`;
    w = Math.max(w, this.measureCtx.measureText(col.label).width);
    return Math.round(Math.max(col.min, Math.min(col.max || TAGS_AUTO_MAX, w + 2)));
  }

  // Width of one full timecode ("00:00:00", plus decimals if shown) in the
  // mono cell font; the floor for Start, In and Out.
  #timeWidth() {
    if (!this.measureCtx) this.measureCtx = document.createElement('canvas').getContext('2d');
    const sample = this.listEl.querySelector('.cvs-cell.cvs-t') || this.listEl;
    const cs = getComputedStyle(sample);
    this.measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const text = fmtTag(0, this.s.sidebarDecimals);
    return Math.ceil(this.measureCtx.measureText(text).width) + 2;
  }

  toggleColumn(key, force) {
    const c = this.s.cols[key] || (this.s.cols[key] = {});
    c.show = force == null ? c.show === false : !!force;
    this.plugin.saveSoon();
    for (const p of this.plugin.players.values()) p.#applyLayout();
  }

  #bindColumnResize(grip, key) {
    const col = COLUMNS.find((c) => c.key === key);
    let start = 0; let base = 0;
    const onMove = (e) => {
      const c = this.s.cols[key] || (this.s.cols[key] = {});
      c.w = Math.max(col.time ? this.#timeWidth() : col.min, Math.round(base + (e.clientX - start)));
      this.#applyColumns();
    };
    const onUp = (e) => {
      try { grip.releasePointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      grip.removeEventListener('pointermove', onMove);
      document.body.removeClass('cvs-col-resizing');
      this.plugin.saveSoon();
      for (const p of this.plugin.players.values()) if (p !== this) p.#applyLayout();
    };
    grip.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault(); e.stopPropagation();
      const c = this.s.cols[key] || {};
      if (col.icon && c.show === false) return; // collapsed columns are not resizable
      start = e.clientX; base = this.heads[key].getBoundingClientRect().width;
      try { grip.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      grip.addEventListener('pointermove', onMove);
      grip.addEventListener('pointerup', onUp, { once: true });
      document.body.addClass('cvs-col-resizing');
    });
    grip.addEventListener('click', (e) => e.stopPropagation());
    grip.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      const c = this.s.cols[key] || (this.s.cols[key] = {});
      delete c.w; // back to the default width (title: fill)
      this.plugin.saveSoon();
      for (const p of this.plugin.players.values()) p.#applyLayout();
    });
  }

  // ---- filter -----------------------------------------------------------------

  get filterActive() { return this.filter.names.size > 0 || this.filter.tags.size > 0; }

  // A clip passes when its name is one of the chosen names (if any are chosen)
  // AND it carries at least one of the chosen tags (if any are chosen).
  passes(tag) {
    const f = this.filter;
    if (f.names.size && !f.names.has(clipBase(tag.title))) return false;
    if (f.tags.size && !clipTags(tag).some((x) => f.tags.has(x))) return false;
    return true;
  }

  clearFilter() { this.filter.names.clear(); this.filter.tags.clear(); this.#syncFilterUi(); this.render(); }

  #syncFilterUi() {
    const label = (base, set) => set.size ? `${base}: ${set.size === 1 ? [...set][0] : set.size}` : base;
    const setSel = (el, text, active) => { el.empty(); el.createSpan({ cls: 'cvs-select-label', text }); const ic = el.createSpan({ cls: 'cvs-select-icon' }); setIcon(ic, 'chevron-down'); el.toggleClass('is-active', active); };
    setSel(this.nameSel, label('Clip', this.filter.names), this.filter.names.size > 0);
    setSel(this.tagSel, label('Tags', this.filter.tags), this.filter.tags.size > 0);
    this.clearFilterBtn.toggleClass('is-hidden', !this.filterActive);
  }

  #filterMenu(kind, anchor) {
    const set = this.filter[kind];
    const values = new Map(); // value -> count
    for (const tag of this.tags) {
      if (kind === 'names') { const n = clipBase(tag.title); if (n) values.set(n, (values.get(n) || 0) + 1); }
      else for (const x of new Set(clipTags(tag))) values.set(x, (values.get(x) || 0) + 1);
    }
    const menu = new CvsMenu(this);
    menu.addItem((m) => m.setTitle(kind === 'names' ? 'All clips' : 'All tags').setChecked(set.size === 0).onClick(() => { set.clear(); this.#syncFilterUi(); this.render(); }));
    if (values.size) menu.addSeparator();
    const keys = [...values.keys()].sort((a, b) => a.localeCompare(b));
    if (!values.size) menu.addItem((m) => m.setTitle(kind === 'names' ? 'No named clips yet' : 'No tags yet').setDisabled(true));
    for (const k of keys) {
      menu.addItem((m) => m.setTitle(kind === 'tags' ? `#${k}` : k).setShortcut(String(values.get(k))).setChecked(set.has(k)).onClick(() => { if (set.has(k)) set.delete(k); else set.add(k); this.#syncFilterUi(); this.render(); }));
    }
    menu.showBelow(anchor, 'start');
  }

  // In / Out for a tag (null clears). Selected tag first, else the current one.
  setInOut(field, t) {
    let i = this.selected;
    if (i < 0) i = this.current;
    if (i < 0 || !this.tags[i]) { this.flash('Select a clip first'); return; }
    this.#snapshot();
    this.tags[i][field] = t == null ? null : clamp(t, 0, this.duration || t);
    this.render(); this.scheduleSave();
    this.flash(t == null ? `${field === 'in' ? 'In' : 'Out'} cleared` : `${field === 'in' ? 'In' : 'Out'} ${fmtTag(t)}`);
  }

  // ---- sort --------------------------------------------------------------------
  // The note stays in time order; only the view is sorted.
  sortedIndexes() {
    const idx = this.tags.map((_, i) => i);
    const k = this.s.sortKey || 'time'; const dir = this.s.sortDir === 'desc' ? -1 : 1;
    const cmp = (a, b) => {
      const A = this.tags[a], B = this.tags[b];
      let r = 0;
      if (k === 'title') r = (A.title || '').localeCompare(B.title || '', undefined, { sensitivity: 'base' });
      else if (k === 'tags') r = fmtTagsProp(clipTags(A)).localeCompare(fmtTagsProp(clipTags(B)));
      else if (k === 'in') r = (A.in == null) - (B.in == null) || (A.in || 0) - (B.in || 0);
      else if (k === 'out') r = (A.out == null) - (B.out == null) || (A.out || 0) - (B.out || 0);
      else if (k === 'length') r = ((A.out ?? A.t) - (A.in ?? A.t)) - ((B.out ?? B.t) - (B.in ?? B.t));
      if (r === 0) r = A.t - B.t;
      return r * dir;
    };
    return idx.sort(cmp);
  }

  #sortMenu(anchor) {
    const m = new CvsMenu(this);
    const k = this.s.sortKey || 'time';
    for (const [key, label, icon] of [['time', 'Start time', 'clock'], ['title', 'Clip name', 'type'], ['tags', 'Tags', 'tag'], ['in', 'In', 'log-in'], ['out', 'Out', 'log-out'], ['length', 'Length (Out minus In)', 'ruler']]) {
      m.addItem((x) => x.setTitle(label).setIcon(icon).setChecked(k === key).onClick(() => { this.s.sortKey = key; this.plugin.saveSoon(); this.render(); }));
    }
    m.addSeparator();
    m.addItem((x) => x.setTitle('Ascending').setChecked(this.s.sortDir !== 'desc').onClick(() => { this.s.sortDir = 'asc'; this.plugin.saveSoon(); this.render(); }));
    m.addItem((x) => x.setTitle('Descending').setChecked(this.s.sortDir === 'desc').onClick(() => { this.s.sortDir = 'desc'; this.plugin.saveSoon(); this.render(); }));
    m.showBelow(anchor, 'end');
  }

  // ---- button panel entry points ---------------------------------------------------

  // A clip from a panel button: title and tags preset, In/Out from buffers.
  addClipFromButton({ title, tags, inBuf, outBuf, edit }) {
    if (this.editing) this.#commitEdit(true);
    const t = this.time;
    if (this.s.pauseOnAdd && !this.video.paused) this.video.pause();
    if (this.filterActive) { this.filter.names.clear(); this.filter.tags.clear(); this.#syncFilterUi(); }
    this.#snapshot();
    const base = this.s.slugNames ? slugName(title || '') : String(title || '').trim();
    const day = this.s.clipDate !== false ? mmddyy() : '';
    const tag = { t, title: [base, day].filter(Boolean).join(' '), tags: [...new Set((tags || []).map((x) => slugName(String(x).replace(/^#/, ''))).filter(Boolean))], in: null, out: null };
    if (inBuf != null) tag.in = clamp(t - inBuf, 0, this.duration || t);
    if (outBuf != null) tag.out = clamp(t + outBuf, 0, this.duration || t + outBuf);
    this.tags.push(tag);
    this.tags.sort((a, b) => a.t - b.t);
    if (base) this.#renumber(base, day);
    this.selected = this.tags.indexOf(tag);
    this.lastAdded = tag;
    this.render(); this.scheduleSave(); this.#scrollSelected();
    if (edit) this.#edit(this.selected, 'title');
    return tag;
  }

  // Clips that share a name and day are numbered in time order: "Breakout 100226",
  // "Breakout 2 100226", ... Only names in that exact pattern are touched (a hand-typed name stays).
  #renumber(base, day) {
    const re = new RegExp(`^${escRe(base)}(?: (\\d+))?${day ? ` ${day}` : ''}$`);
    const same = this.tags.filter((x) => re.test(x.title || '')).sort((a, b) => a.t - b.t);
    same.forEach((x, k) => { x.title = [base, k ? String(k + 1) : '', day].filter(Boolean).join(' '); });
  }

  // Tags from a panel button go into the Tags property of the selected clip,
  // else the last one added, else the current one (Alt or Shift-click takes them out).
  addTagsToTarget(list, remove = false) {
    if (this.editing) this.#commitEdit(true);
    let tag = this.selected >= 0 ? this.tags[this.selected] : null;
    if (!tag && this.lastAdded && this.tags.includes(this.lastAdded)) tag = this.lastAdded;
    if (!tag && this.current >= 0) tag = this.tags[this.current];
    if (!tag) { this.flash('Add or select a clip first'); return null; }
    const words = list.map((x) => slugName(String(x).replace(/^#/, ''))).filter(Boolean);
    const cur = clipTags(tag);
    const next = remove ? cur.filter((x) => !words.includes(x)) : [...cur, ...words.filter((x) => !cur.includes(x))];
    if (next.join(' ') === cur.join(' ')) return tag;
    this.#snapshot();
    tag.tags = next;
    const i = this.tags.indexOf(tag); if (i >= 0) this.selected = i;
    this.render(); this.scheduleSave();
    return tag;
  }

  /** Is the clip log showing right now (beside the video, or as a sheet in a narrow pane)? */
  sideShown() { return this.narrow ? !!this.narrowOpen : !!this.s.sidebarOpen; }

  toggleSidebar(force) {
    if (this.narrow) {
      // in a narrow pane the sidebar is a slide-over; this is per tab, not a setting
      this.narrowOpen = force == null ? !this.narrowOpen : !!force;
      this.#applyLayout();
      return;
    }
    this.s.sidebarOpen = force == null ? !this.s.sidebarOpen : !!force;
    this.plugin.saveSoon();
    // it slides open and closed like the file tree (styles.css .cvs-side-sliding)
    for (const p of this.plugin.players.values()) {
      const c = p.view.contentEl; clearTimeout(p.slideT);
      c.addClass('cvs-side-sliding'); p.#applyLayout();
      p.slideT = setTimeout(() => c.removeClass('cvs-side-sliding'), 240);
    }
  }

  // A pane narrower than sidebar + STAGE_MIN cannot show both side by side,
  // so the sidebar becomes a sheet over the stage, closed by default.
  #checkNarrow() {
    if (this.dead) return;
    const w = this.view.contentEl.clientWidth;
    if (!w) return;
    // short (a low embed or window): the button panel hides, the controls fold into one row
    const ht = this.view.contentEl.clientHeight;
    const short = this.s.shortBelow > 0 && ht > 0 && ht < this.s.shortBelow;
    if (short !== this.short) {
      this.short = short;
      this.view.contentEl.toggleClass('cvs-short', short);
      if (!short) { this.shortPanel = false; this.view.contentEl.removeClass('cvs-short-panel'); }
      requestAnimationFrame(() => this.#layoutFrame());
    }
    const limit = this.s.sidebarSheetBelow > 0 ? this.s.sidebarSheetBelow : (this.s.sidebarWidth || 300) + STAGE_MIN + 24;
    const narrow = w < limit;
    if (narrow === !!this.narrow) return;
    this.narrow = narrow;
    if (narrow) this.narrowOpen = false;
    this.#applyLayout();
  }

  #bindResize() {
    const h = this.handleEl;
    let start = 0; let base = 0;
    const onMove = (e) => {
      const sign = this.s.sidebarSide === 'right' ? -1 : 1;
      // Any width from a sliver (the sidebar nearly gone) to most of the pane
      // (the video nearly gone); the table scrolls and the toolbar clips.
      const next = clamp(base + sign * (e.clientX - start), SIDEBAR_MIN, Math.max(SIDEBAR_MIN, this.view.contentEl.clientWidth - STAGE_MIN));
      this.view.contentEl.style.setProperty('--cvs-side-w', next + 'px');
    };
    const onUp = (e) => {
      try { h.releasePointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      h.removeEventListener('pointermove', onMove);
      document.body.removeClass('cvs-resizing');
      this.s.sidebarWidth = Math.round(this.sideEl.getBoundingClientRect().width);
      this.plugin.saveSoon();
      for (const p of this.plugin.players.values()) if (p !== this) p.#applyLayout();
    };
    h.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      start = e.clientX; base = this.sideEl.getBoundingClientRect().width;
      try { h.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
      h.addEventListener('pointermove', onMove);
      h.addEventListener('pointerup', onUp, { once: true });
      document.body.addClass('cvs-resizing');
    });
    h.addEventListener('dblclick', () => { this.s.sidebarWidth = DEFAULT_SETTINGS.sidebarWidth; this.plugin.saveSoon(); for (const p of this.plugin.players.values()) p.#applyLayout(); });
  }

  // ---- timeline ------------------------------------------------------------

  #bindTimeline() {
    const tl = this.timelineEl;
    const frac = (e) => { const r = this.tlTrack.getBoundingClientRect(); return r.width ? clamp((e.clientX - r.left) / r.width, 0, 1) : 0; };
    let dragging = false;
    // annotating: the timeline is the clip's own (freezes as holds)
    const annoDown = (e) => { e.preventDefault(); try { tl.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ } tl.addClass('is-dragging'); this.anno.tlPointer('down', e); };
    // (freeze blocks handle their own drags, so events on them pass through)
    const onHold = (e) => e.target instanceof Element && !!e.target.closest('.cvs-tl-hold');
    tl.addEventListener('pointerdown', (e) => { if (this.anno && e.button === 0 && !onHold(e)) { e.stopImmediatePropagation(); annoDown(e); } }, true);
    tl.addEventListener('pointermove', (e) => { if (!this.anno) return; if (onHold(e) && !this.anno.tlDrag) { this.anno.tlPointer('move', e); tl.addClass('is-hover'); return; } e.stopImmediatePropagation(); this.anno.tlPointer('move', e); tl.addClass('is-hover'); }, true);
    const annoUp = (e) => { if (!this.anno || (onHold(e) && !this.anno.tlDrag)) return; e.stopImmediatePropagation(); try { tl.releasePointerCapture(e.pointerId); } catch (_) { /* fine */ } tl.removeClass('is-dragging'); this.anno.tlPointer('up', e); this.stageEl.focus({ preventScroll: true }); };
    tl.addEventListener('pointerup', annoUp, true);
    tl.addEventListener('pointercancel', annoUp, true);
    tl.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || !this.duration) return;
      e.preventDefault();
      dragging = true;
      try { tl.setPointerCapture(e.pointerId); } catch (_) { /* synthetic event */ }
      tl.addClass('is-dragging');
      this.scrubber.dragTo(frac(e) * this.duration);
    });
    tl.addEventListener('pointermove', (e) => {
      if (dragging) { this.scrubber.dragTo(frac(e) * this.duration); return; }
      if (!this.duration) return;
      const t = frac(e) * this.duration;
      const ch = this.#chapterAt(t);
      this.tlTip.setText(ch && ch.title ? `${fmtClock(t, this.s.clock, this.frameSec)}  ${ch.title}` : fmtClock(t, this.s.clock, this.frameSec));
      this.tlTip.style.left = (frac(e) * 100) + '%';
      tl.addClass('is-hover');
    });
    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      try { tl.releasePointerCapture(e.pointerId); } catch (_) { /* fine */ }
      tl.removeClass('is-dragging');
      this.scrubber.endDrag();
      this.stageEl.focus({ preventScroll: true });
    };
    tl.addEventListener('pointerup', end);
    tl.addEventListener('pointercancel', end);
    tl.addEventListener('pointerleave', () => tl.removeClass('is-hover'));
  }

  #renderRange() {
    const dur = this.duration;
    if (this.anno) { this.tlRange.addClass('is-hidden'); return; } // the whole timeline is the clip
    const tag = this.tags[this.selected >= 0 ? this.selected : this.current];
    const has = dur && tag && tag.in != null && tag.out != null && tag.out > tag.in;
    this.tlRange.toggleClass('is-hidden', !has);
    if (!has) return;
    this.tlRange.style.left = (100 * tag.in / dur) + '%';
    this.tlRange.style.width = (100 * (tag.out - tag.in) / dur) + '%';
  }

  #renderTimeline() {
    const dur = this.duration;
    this.tlMarks.empty();
    if (this.anno) { this.#renderRange(); this.anno.renderTl(); return; }
    this.#renderRange();
    if (!dur) return;
    this.tags.forEach((tag, i) => {
      if (!this.passes(tag)) return;
      const m = this.tlMarks.createDiv({ cls: 'cvs-mark', attr: { 'aria-label': `${fmtTag(tag.t)}  ${tag.title || ''}` } });
      m.style.left = (100 * tag.t / dur) + '%';
      m.dataset.i = String(i);
      m.toggleClass('is-current', i === this.current);
      m.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); };
      m.onclick = (e) => { e.stopPropagation(); this.seekTo(i); };
    });
  }
  renderTimelineNow() { this.#renderTimeline(); }

  // ---- state sync ----------------------------------------------------------

  #onMeta() {
    this.durEl.setText(fmtClock(this.duration, this.s.clock, this.frameSec));
    this.#renderTimeline();
    this.#tick(true);
    if (this.link) this.resumed = true;
    this.#applyLink();
    if (this.s.resume && !this.resumed) {
      this.resumed = true;
      const t = (this.plugin.positions || {})[this.vaultPath || this.absPath];
      if (t && t > 1 && t < this.duration - 2) { this.video.currentTime = t; this.flash(`Resumed at ${fmtClock(t, this.s.clock, this.frameSec)}`); }
    }
  }

  #tick(force) {
    if (this.dead) return;
    if (this.anno) { this.anno.onTime(this.time); if (!this.video.paused && !this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.#tick(); }); return; }
    const t = this.time;
    if (!force && t === this.lastT) return;
    this.lastT = t;
    this.curEl.setText(fmtClock(t, this.s.clock, this.frameSec));
    const dur = this.duration;
    const f = dur ? clamp(t / dur, 0, 1) : 0;
    this.tlFill.style.width = (f * 100) + '%';
    this.tlHead.style.left = (f * 100) + '%';
    this.setTime(t);
    if (!this.video.paused && !this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.#tick(); });
  }

  onScrubTime(t) { this.#tick(); }

  syncPlayIcon() { this.#syncPlay(); }
  syncLoopIcon() { this.#syncLoop(); }
  #syncPlay() {
    const playing = this.anno ? !!this.anno.playing : !this.video.paused && !this.video.ended;
    setIcon(this.playBtn, playing ? 'pause' : 'play');
    this.stageEl.toggleClass('is-playing', playing);
    if (playing) this.#tick(true);
  }
  #syncSpeed() { this.speedEl.setText(`${this.video.playbackRate}x`); this.speedEl.toggleClass('is-off', this.video.playbackRate !== 1); }
  #syncVolume() {
    const m = this.video.muted || this.video.volume === 0;
    setIcon(this.muteBtn, m ? 'volume-x' : this.video.volume < 0.5 ? 'volume-1' : 'volume-2');
    this.volEl.value = String(m ? 0 : this.video.volume);
    this.volEl.style.setProperty('--f', (m ? 0 : this.video.volume) * 100 + '%');
  }
  #syncLoop() { this.loopBtn.toggleClass('is-on', this.anno ? !!this.anno.loopClip : !!this.video.loop); }

  flash(text) {
    this.flashEl.setText(text);
    this.flashEl.addClass('is-on');
    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => this.flashEl.removeClass('is-on'), 900);
  }

  #savePosition() {
    if (!this.plugin.positions) this.plugin.positions = {};
    this.plugin.positions[this.vaultPath || this.absPath] = this.video.currentTime;
    this.plugin.saveSoon();
  }

  // ---- transport actions ---------------------------------------------------

  togglePlay() { if (this.anno) { this.anno.togglePlay(); return; } const v = this.video; if (v.paused || v.ended) v.play().catch(() => {}); else v.pause(); }
  seek(t) { this.scrubber.stopGesture(); this.scrubber.seek(clamp(t, 0, this.duration), true); this.#tick(true); }
  skip(secs) { if (!this.video.paused) { this.video.currentTime = clamp(this.video.currentTime + secs, 0, this.duration); } else this.seek(this.time + secs); this.flash(`${secs > 0 ? '+' : ''}${secs} s`); }
  step(frames) { this.video.pause(); this.scrubber.step(frames); this.#tick(true); }
  setSpeed(r) { this.video.playbackRate = clamp(r, 0.25, 4); this.flash(`${this.video.playbackRate}x`); }
  speedStep(dir) {
    const cur = this.video.playbackRate;
    let i = SPEEDS.findIndex((x) => Math.abs(x - cur) < 1e-6);
    if (i < 0) i = SPEEDS.findIndex((x) => x > cur) - (dir > 0 ? 1 : 0);
    this.setSpeed(SPEEDS[clamp(i + dir, 0, SPEEDS.length - 1)]);
  }
  toggleLoop() { this.video.loop = !this.video.loop; this.#syncLoop(); this.flash(this.video.loop ? 'Loop on' : 'Loop off'); }
  toggleMute() { this.video.muted = !this.video.muted; this.#syncVolume(); }
  // F, double-click or the button. Real fullscreen where the browser allows it;
  // on an iPhone (no element fullscreen) the video's own fullscreen player unless
  // annotating; otherwise, or when it is refused (an embed without permission),
  // the stage fills the window or frame. Setting fullscreenMode 'fill' always fills.
  toggleFullscreen() {
    if (this.stageEl.hasClass('is-filled')) { this.setFilled(false); return; }
    const cur = fsElement();
    if (cur) { try { const r = (document.exitFullscreen || document.webkitExitFullscreen).call(document); if (r && r.catch) r.catch(() => {}); } catch (_) { /* already out */ } return; }
    const st = this.stageEl;
    const req = st.requestFullscreen || st.webkitRequestFullscreen;
    const allowed = document.fullscreenEnabled || document.webkitFullscreenEnabled;
    const mode = this.s.fullscreenMode || 'auto';
    if (mode !== 'fill' && req && allowed) {
      try { const r = req.call(st); if (r && r.catch) r.catch(() => this.setFilled(true)); } catch (_) { this.setFilled(true); }
      return;
    }
    if (mode !== 'fill' && !req && !this.anno && typeof this.video.webkitEnterFullscreen === 'function' && this.video.readyState >= 1) {
      try { this.video.webkitEnterFullscreen(); return; } catch (_) { /* fill instead */ }
    }
    this.setFilled(true);
  }

  setFilled(on) {
    if (this.dead) return;
    this.stageEl.toggleClass('is-filled', !!on);
    this.stageEl.toggleClass('is-fullscreen', !!on || fsElement() === this.stageEl);
    this.#syncFsBtn();
    this.#layoutFrame();
    if (on) { this.stageEl.focus({ preventScroll: true }); let touch = false; try { touch = matchMedia('(pointer: coarse)').matches; } catch (_) { /* old browser */ } if (!touch) this.flash('Esc to exit'); }
  }

  #syncFsBtn() {
    const on = this.stageEl.hasClass('is-filled') || fsElement() === this.stageEl;
    if (this.fsBtn) { setIcon(this.fsBtn, on ? 'minimize' : 'maximize'); this.fsBtn.setAttribute('aria-label', on ? 'Exit full screen (F)' : 'Full screen (F)'); }
  }

  #editClock() {
    if (this.clockInput || this.anno) return;
    const input = createEl('input', { type: 'text', cls: 'cvs-clock-input', value: fmtTag(this.time), attr: { spellcheck: 'false' } });
    this.curEl.empty(); this.curEl.appendChild(input);
    this.clockInput = input;
    input.focus(); input.select();
    const done = (commit) => {
      if (!this.clockInput) return;
      this.clockInput = null;
      if (commit) { const t = parseTagTime(input.value); if (t == null) new Notice('Time must look like 00:06:58.700'); else this.seek(t); }
      this.#tick(true);
      this.stageEl.focus({ preventScroll: true });
    };
    input.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); done(true); } else if (e.key === 'Escape') { e.preventDefault(); done(false); } };
    input.onblur = () => done(true);
  }

  // ---- chapters: data -------------------------------------------------------

  // The panel's clip names and tag words (for splitting pre-format-2 names).
  #knownNames() {
    const clips = new Set(); const tags = new Set(); const labels = new Map();
    for (const row of (this.s.panel && this.s.panel.rows) || []) for (const it of row.items || []) {
      if (it.divider) continue;
      if (row.kind === 'clips') { const sl = slugName(it.title || it.label); if (sl) { clips.add(sl); if (it.label && !labels.has(sl)) labels.set(sl, String(it.label).trim()); } }
      for (const w of btnWords(it)) tags.add(w);
    }
    return { clips, tags, labels };
  }

  async load() {
    const p = this.notePath();
    if (!p) { this.notesLoaded = true; this.#applyLink(); return; }
    const f = this.app.vault.getAbstractFileByPath(p);
    if (f) {
      let text = '';
      try { text = await this.app.vault.read(f); this.tags = parseTagsNote(text, { known: this.#knownNames() }); } catch (_) { this.tags = []; }
      this.lastText = text;
      this.orphans = this.tags.orphans || [];
      // notes before format 2 kept tags in the name ("breakout-good"); split them once
      if (this.tags.migrated) { this.dirty = true; setTimeout(() => { if (!this.dead) void this.save(); }, 50); }
      const fm = /^---\r?\n[\s\S]*?^video:\s*"?([^"\n]*)"?\s*$/m.exec(text);
      if (fm && this.vaultPath && fm[1].trim() !== this.vaultPath) {
        try { await this.app.fileManager.processFrontMatter(f, (y) => { y.video = this.vaultPath; y.type = 'video-clips'; }); } catch (_) { /* fine */ }
      }
    }
    else this.tags = [];
    if (this.dead) return;
    this.render();
    this.notesLoaded = true;
    this.#applyLink();
  }

  // The clips note changed under us (another pane, a sync, a hand edit): reload
  // when this pane has nothing unsaved and nothing open, so no one's work is lost.
  reloadIfIdle(text) {
    if (this.dead || text === this.lastText) return;
    if (this.dirty || this.editing || this.anno || this.exporting) { this.staleNote = true; return; }
    const sel = this.tags[this.selected]; const selT = sel && sel.t; const selName = sel && sel.title;
    const checked = new Set([...this.checked].map((c) => `${c.t}|${c.title}`));
    void this.load().then(() => {
      this.checked = new Set(this.tags.filter((c) => checked.has(`${c.t}|${c.title}`)));
      const i = this.tags.findIndex((c) => c.t === selT && c.title === selName); this.selected = i;
      this.render();
    });
  }

  scheduleSave() {
    clearTimeout(this.saveTimer);
    this.dirty = true;
    this.#savedState('pending');
    this.saveTimer = setTimeout(() => void this.save(), 250);
  }

  #savedState(state) {
    if (!this.savedEl) return;
    this.savedEl.className = 'cvs-saved is-' + state;
    this.savedEl.setText(state === 'saved' ? 'Saved' : state === 'error' ? 'Not saved' : '');
    clearTimeout(this.savedTimer);
    if (state === 'saved') this.savedTimer = setTimeout(() => { if (this.savedEl) this.savedEl.className = 'cvs-saved'; }, 1600);
  }

  async save() {
    clearTimeout(this.saveTimer);
    const p = this.notePath();
    if (!p) return;
    const f = this.app.vault.getAbstractFileByPath(p);
    let existing = '';
    if (f) { try { existing = await this.app.vault.read(f); } catch (_) { existing = ''; } }
    // the note changed since we last read or wrote it (another pane, a sync):
    // keep clips that appeared there, rather than overwriting them
    if (f && this.lastText != null && existing !== this.lastText) {
      const ours = new Set(this.tags.map((c) => `${fmtFull(c.t)}|${c.title}`));
      const known = new Set(parseTagsNote(this.lastText, { slug: false }).map((c) => `${fmtFull(c.t)}|${c.title}`));
      const theirs = parseTagsNote(existing, { slug: false });
      const added = theirs.filter((c) => { const k = `${fmtFull(c.t)}|${c.title}`; return !ours.has(k) && !known.has(k); });
      if (added.length) { this.tags.push(...added); this.tags.sort((a, b) => a.t - b.t); this.render(); }
    }
    this.staleNote = false;
    const body = serializeTagsNote(this.vaultPath, this.tags, existing, this.orphans);
    this.lastText = body;
    try {
      if (f) { if (existing !== body) await this.app.vault.modify(f, body); }
      else if (this.tags.length) {
        const dir = dirName(p);
        if (dir && !this.app.vault.getAbstractFileByPath(dir)) await this.app.vault.createFolder(dir);
        await this.app.vault.create(p, body);
      }
      this.dirty = false;
      if (this.tags.length || f) this.#savedState('saved');
      // the same video open in another pane picks this up (unless it is mid-edit)
      for (const q of this.plugin.players.values()) if (q !== this && q.notePath() === p) q.reloadIfIdle(body);
    } catch (e) { this.#savedState('error'); new Notice('Could not save clips: ' + (e && e.message || e)); }
  }

  // The host saved this note's text on the player's behalf (the web app retrying
  // a save that failed offline): clear the dirty flag and the "Not saved" badge.
  savedElsewhere(text) {
    if (this.dead || text !== this.lastText) return;
    this.dirty = false;
    this.#savedState('saved');
  }

  #snapshot() { this.undo.push(this.tags.map((t) => ({ ...t }))); if (this.undo.length > 50) this.undo.shift(); }

  undoLast() {
    if (this.editing) { this.#commitEdit(false); return; }
    const prev = this.undo.pop();
    if (!prev) { this.flash('Nothing to undo'); return; }
    this.tags = prev;
    this.selected = Math.min(this.selected, this.tags.length - 1);
    this.render(); this.scheduleSave();
    this.flash('Undone');
  }

  #chapterAt(t) { let cur = null; for (const tag of this.tags) { if (tag.t <= t + 0.001) cur = tag; else break; } return cur; }

  // ---- chapters: edits ------------------------------------------------------

  addAtCurrent() {
    if (this.editing) this.#commitEdit(true);
    const t = this.time;
    const dup = this.tags.findIndex((x) => Math.abs(x.t - t) < this.frameSec / 2);
    if (dup >= 0) { this.select(dup); this.#edit(dup, 'title'); return; }
    if (this.s.pauseOnAdd && !this.video.paused) this.video.pause();
    if (this.filterActive) { this.filter.names.clear(); this.filter.tags.clear(); this.#syncFilterUi(); }
    this.#snapshot();
    const tag = { t, title: '', fresh: true, tags: [] };
    this.tags.push(tag);
    this.tags.sort((a, b) => a.t - b.t);
    this.selected = this.tags.indexOf(tag);
    this.lastAdded = tag;
    this.render();
    this.scheduleSave();
    this.#scrollSelected();
    this.#edit(this.selected, this.s.tagAddStartsIn === 'title' ? 'title' : 'time');
  }

  removeSelected() {
    if (this.editing) this.#commitEdit(false);
    if (this.selected < 0 || this.selected >= this.tags.length) { this.flash('Select a clip first'); return; }
    this.removeAt(this.selected);
  }

  removeAt(i) {
    this.#snapshot();
    const gone = this.tags.splice(i, 1)[0];
    this.selected = Math.min(i, this.tags.length - 1);
    this.render(); this.scheduleSave();
    const nf = gone && gone.freezes ? gone.freezes.length : 0;
    this.flash(`Deleted ${gone && gone.title ? '"' + gone.title + '"' : 'clip'}${nf ? ` and its ${nf} freeze${nf === 1 ? '' : 's'}` : ''} (Cmd+Z to undo)`);
  }

  setTagTime(i, t) {
    const tag = this.tags[i];
    if (!tag) return;
    this.#snapshot();
    tag.t = clamp(t, 0, this.duration || t);
    this.tags.sort((a, b) => a.t - b.t);
    this.selected = this.tags.indexOf(tag);
    this.render(); this.scheduleSave();
  }

  select(i) { if (this.editing) this.#commitEdit(true); this.selected = i; this.render(); }

  // A clip's link key: its Start in seconds to the millisecond ("12.345"), with
  // "~2", "~3" for later clips sharing that Start (the note's own identity rule).
  clipKey(i) {
    const k = (j) => (Math.round(this.tags[j].t * 1000) / 1000).toFixed(3);
    if (!this.tags[i]) return '';
    const key = k(i);
    let n = 1;
    for (let j = 0; j < i; j++) if (k(j) === key) n++;
    return n > 1 ? `${key}~${n}` : key;
  }

  // Go where a link says: { t } seconds, { clip } key (selects it), or both
  // (the clip selected, the time given). Waits for the clips and the duration.
  openLink(link) { this.link = link; this.#applyLink(); }
  #applyLink() {
    const l = this.link;
    if (!l || this.dead || !this.notesLoaded || !(this.duration > 0)) return;
    this.link = null;
    let i = -1;
    if (l.clip) {
      for (let j = 0; j < this.tags.length; j++) if (this.clipKey(j) === l.clip) { i = j; break; }
      if (i < 0) new Notice(`That clip is not in this video any more${l.t != null ? ', so it opened at the time in the link' : ''}.`);
    }
    const t = l.t != null && Number.isFinite(l.t) ? l.t : i >= 0 ? this.tags[i].t : null;
    if (i >= 0) { if (this.editing) this.#commitEdit(true); this.selected = i; this.render(); this.#scrollSelected(); }
    if (t != null) this.seek(t);
  }

  seekTo(i) {
    const tag = this.tags[i];
    if (!tag) return;
    if (this.editing && this.editing.i !== i) this.#commitEdit(true);
    this.selected = i;
    this.seek(tag.t);
    if (!this.editing) this.render();
    this.#scrollSelected();
  }

  jump(dir) {
    if (!this.tags.length) { this.flash('No clips'); return; }
    const t = this.time;
    const eps = 0.25;
    let idx = -1;
    if (dir > 0) idx = this.tags.findIndex((x) => x.t > t + eps && this.passes(x));
    else { for (let i = this.tags.length - 1; i >= 0; i--) if (this.tags[i].t < t - eps && this.passes(this.tags[i])) { idx = i; break; } }
    if (idx >= 0) this.seekTo(idx); else this.flash(dir > 0 ? 'Last clip' : 'First clip');
  }

  #scrollSelected() { const row = this.listEl.querySelector('.cvs-row.is-selected'); if (row) row.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }

  // ---- chapters: render -----------------------------------------------------

  render() {
    if (this.dead) return;
    const list = this.listEl;
    list.querySelectorAll('.cvs-row').forEach((r) => r.remove());
    this.countEl.setText(this.tags.length ? `${this.tags.length}` : '');
    const shown = this.tags.filter((tag) => this.passes(tag)).length;
    this.emptyEl.setText(this.tags.length && !shown ? 'No clips match the filter.' : 'No clips. Press T (or +) to clip the current frame.');
    this.emptyEl.toggleClass('is-hidden', shown > 0);
    this.countEl.setText(this.tags.length ? (this.filterActive ? `${shown} of ${this.tags.length}` : `${this.tags.length}`) : '');
    for (const c of [...this.checked]) if (!this.tags.includes(c)) this.checked.delete(c);
    const shownIdx = [];
    for (const i of this.sortedIndexes()) {
      const tag = this.tags[i];
      if (!this.passes(tag)) continue;
      shownIdx.push(i);
      const row = list.createDiv({ cls: 'cvs-row' });
      row.dataset.i = String(i);
      row.toggleClass('is-selected', i === this.selected);
      row.toggleClass('is-current', i === this.current);
      row.toggleClass('is-checked', this.checked.has(tag));
      row.toggleClass('is-annotating', !!(this.anno && this.anno.tag === tag));
      if (this.s.showCheckboxes !== false) {
        const cc = row.createSpan({ cls: 'cvs-cell cvs-check' });
        const cb = cc.createSpan({ cls: 'cvs-cb' + (this.checked.has(tag) ? ' is-checked' : ''), attr: { role: 'checkbox', 'aria-checked': String(this.checked.has(tag)) } });
        if (this.checked.has(tag)) setIcon(cb, 'check');
        cc.onmousedown = (e) => e.stopPropagation();
        cc.onclick = (e) => { e.stopPropagation(); this.toggleCheck(i, e.shiftKey); };
        cc.ondblclick = (e) => e.stopPropagation();
      }
      const ti = row.createSpan({ cls: 'cvs-cell cvs-title' });
      ti.createSpan({ cls: 'cvs-title-text', text: tag.title || '' });
      if (!tag.title) ti.addClass('is-blank');
      if (tag.freezes && tag.freezes.length) {
        const bd = ti.createSpan({ cls: 'cvs-anno-badge', attr: { 'aria-label': `${tag.freezes.length} freeze frame${tag.freezes.length === 1 ? '' : 's'}. Click to annotate.` } });
        setIcon(bd, 'snowflake'); bd.createSpan({ text: String(tag.freezes.length) });
        bd.onmousedown = (e) => e.stopPropagation();
        bd.onclick = (e) => { e.stopPropagation(); this.selected = i; this.annotate(); };
      }
      row.createSpan({ cls: 'cvs-cell cvs-tags' + (clipTags(tag).length ? '' : ' is-blank'), text: fmtTagsProp(clipTags(tag)) });
      const d = this.s.sidebarDecimals;
      row.createSpan({ cls: 'cvs-cell cvs-t', text: fmtTag(tag.t, d) });
      row.createSpan({ cls: 'cvs-cell cvs-in' + (tag.in == null ? ' is-blank' : ''), text: tag.in == null ? '' : fmtTag(tag.in, d) });
      row.createSpan({ cls: 'cvs-cell cvs-out' + (tag.out == null ? ' is-blank' : ''), text: tag.out == null ? '' : fmtTag(tag.out, d) });
      let wasSelected = false;
      row.addEventListener('mousedown', () => { wasSelected = i === this.selected && !this.editing; });
      row.onclick = (e) => {
        if (this.editing && this.editing.i === i) return;
        const cell = e.target.closest('.cvs-cell');
        if (wasSelected && cell && this.s.tagClickToEdit) { this.#edit(i, fieldOfCell(cell)); return; }
        // annotating: picking another clip moves the annotate mode to it
        if (this.anno && this.anno.tag !== tag) { this.annotate(tag); return; }
        this.seekTo(i);
        this.tableEl.focus({ preventScroll: true });
      };
      row.ondblclick = (e) => { const cell = e.target.closest('.cvs-cell'); if (cell) { e.stopPropagation(); this.#edit(i, fieldOfCell(cell)); } };
      row.oncontextmenu = (e) => { e.preventDefault(); this.select(i); this.#rowMenu(e, i); };
    }
    this.shownIdx = shownIdx;
    this.#syncChecks();
    this.#syncButtons();
    this.#applyColumns();
    this.#renderTimeline();
    this.#renderPill();
    if (this.anno) this.anno.onClipsChanged();
  }

  // ---- checkboxes (export selection) ------------------------------------------

  toggleCheck(i, range = false) {
    const tag = this.tags[i]; if (!tag) return;
    const on = !this.checked.has(tag);
    if (range && this.lastCheck != null && this.shownIdx) {
      // Shift-click ticks (or unticks) everything shown between the two rows
      const a = this.shownIdx.indexOf(this.lastCheck); const b = this.shownIdx.indexOf(i);
      if (a >= 0 && b >= 0) { for (let k = Math.min(a, b); k <= Math.max(a, b); k++) { const t = this.tags[this.shownIdx[k]]; if (on) this.checked.add(t); else this.checked.delete(t); } }
    } else if (on) this.checked.add(tag); else this.checked.delete(tag);
    this.lastCheck = i;
    this.render();
  }

  // Header box: all shown rows on, or (when they already are) all off.
  checkAll(force) {
    const shown = (this.shownIdx || []).map((i) => this.tags[i]);
    const allOn = shown.length && shown.every((t) => this.checked.has(t));
    const on = force == null ? !allOn : !!force;
    for (const t of shown) { if (on) this.checked.add(t); else this.checked.delete(t); }
    this.render();
  }

  #syncChecks() {
    const shown = (this.shownIdx || []).map((i) => this.tags[i]);
    const n = shown.filter((t) => this.checked.has(t)).length;
    const el = this.checkAllEl;
    el.empty();
    el.toggleClass('is-checked', n > 0 && n === shown.length);
    el.toggleClass('is-mixed', n > 0 && n < shown.length);
    if (n > 0) setIcon(el, n === shown.length ? 'check' : 'minus');
    const total = this.checked.size;
    this.exportBtn.empty();
    setIcon(this.exportBtn.createSpan({ cls: 'cvs-export-icon' }), this.exporting ? 'loader' : 'download');
    this.exportBtn.createSpan({ cls: 'cvs-export-lbl', text: this.exporting ? 'Exporting' : 'Export' });
    if (!this.exporting) this.exportBtn.createSpan({ cls: 'cvs-export-n', text: String(total) });
    this.exportBtn.toggleClass('is-hidden', !total && !this.exporting);
    this.exportBtn.toggleClass('is-busy', !!this.exporting);
  }

  #renderPill() {
    const tag = this.tags[this.current];
    this.pillEl.setText(tag ? (tag.title || fmtTag(tag.t)) : '');
    this.pillEl.toggleClass('is-empty', !tag);
    this.pillEl.toggleClass('is-hidden', !this.s.showPill || !this.tags.length);
  }

  setTime(t) {
    let cur = -1;
    for (let i = 0; i < this.tags.length; i++) { if (this.tags[i].t <= t + 0.001) cur = i; else break; }
    if (cur === this.current) return;
    this.current = cur;
    this.listEl.querySelectorAll('.cvs-row').forEach((r) => r.toggleClass('is-current', Number(r.dataset.i) === cur));
    this.tlMarks.querySelectorAll('.cvs-mark').forEach((m) => m.toggleClass('is-current', Number(m.dataset.i) === cur));
    this.#renderPill();
    if (this.selected < 0) this.#renderRange();
    if (this.s.followPlayback && !this.editing) { const row = this.listEl.querySelector('.cvs-row.is-current'); if (row) row.scrollIntoView({ block: 'nearest' }); }
  }

  #syncButtons() {
    const editing = !!this.editing;
    this.addBtn.disabled = editing; this.addBtn.toggleClass('is-disabled', editing);
    const canDel = this.selected >= 0 && this.selected < this.tags.length && !editing;
    this.delBtn.disabled = !canDel; this.delBtn.toggleClass('is-disabled', !canDel);
  }

  #listKey(e) {
    if (this.editing) { e.stopPropagation(); return; }
    if (e.key === 'Enter' && this.selected >= 0) { e.preventDefault(); e.stopPropagation(); this.#edit(this.selected, 'title'); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.select(-1); this.stageEl.focus({ preventScroll: true }); }
    // everything else falls through to the plugin-level player keys
  }

  // ---- chapters: cell editing ----------------------------------------------

  #edit(i, field) {
    if (i < 0 || i >= this.tags.length) return;
    if (this.editing) this.#commitEdit(true);
    this.selected = i;
    this.render();
    const tag0 = this.tags[i];
    const cell = this.listEl.querySelector(`.cvs-row[data-i="${i}"] .${CELL_OF[field]}`);
    if (!cell) return;
    const d = this.s.sidebarDecimals;
    const value = field === 'time' ? fmtTag(tag0.t, d) : field === 'in' || field === 'out' ? (tag0[field] == null ? '' : fmtTag(tag0[field], d)) : field === 'tags' ? fmtTagsProp(clipTags(tag0)) : (tag0.title || '');
    const input = createEl('input', { type: 'text', cls: 'cvs-input', value, attr: { spellcheck: 'false', placeholder: '' } });
    if (field === 'title' && this.s.slugNames === true) {
      // live clean-up: "Breakout Good #low" reads "breakout-good-low" as you type
      input.addEventListener('input', () => {
        const pos = input.selectionStart; const atEnd = pos === input.value.length;
        const next = liveSlug(input.value);
        if (next !== input.value) { const delta = next.length - input.value.length; input.value = next; const at = atEnd ? next.length : Math.max(0, pos + delta); input.setSelectionRange(at, at); }
      });
    }
    cell.empty(); cell.appendChild(input); cell.addClass('is-editing');
    this.editing = { i, field, input, before: value };
    this.#applyColumns(); // a collapsed column opens for the edit
    this.#syncButtons();
    input.focus(); input.select();
    if (field !== 'title') input.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        const goTitle = field === 'time' && !(this.tags[i].title || '').trim() && this.s.tagEnterAdvances;
        this.#commitEdit(true, goTitle ? 'title' : null);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const order = FIELD_ORDER.filter((f) => f === 'title' || f === 'time' || !(this.s.cols[f] && this.s.cols[f].show === false) || f === field);
        const at = order.indexOf(field);
        this.#commitEdit(true, e.shiftKey ? (order[at - 1] || null) : (order[at + 1] || null));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        const tag = this.tags[i];
        // Esc on a chapter you just added and never titled: take it back out
        // rather than leaving an "untitled" row behind.
        if (tag && tag.fresh && !(tag.title || '').trim() && this.s.discardBlankOnEscape) {
          this.editing = null;
          const at = this.tags.indexOf(tag);
          this.tags.splice(at, 1);
          this.selected = -1;
          this.render(); this.scheduleSave();
          this.flash('Clip discarded');
          this.stageEl.focus({ preventScroll: true });
          return;
        }
        this.#commitEdit(false);
      }
    });
    input.addEventListener('blur', () => { if (this.editing && this.editing.input === input) this.#commitEdit(true); });
    for (const ev of ['mousedown', 'click', 'dblclick']) input.addEventListener(ev, (e) => e.stopPropagation());
  }

  #commitEdit(commit, next = null) {
    const ed = this.editing;
    if (!ed) return;
    this.editing = null;
    const tag = this.tags[ed.i];
    let idx = ed.i;
    if (commit && tag) {
      const v = ed.input.value;
      if (ed.field === 'time') {
        const t = parseTagTime(v);
        if (t == null) new Notice('Time must look like 00:06:58.7');
        else if (v.trim() !== ed.before.trim() && Math.abs(t - tag.t) > 1e-6) {
          this.#snapshot();
          tag.t = clamp(t, 0, this.duration || t);
          this.tags.sort((a, b) => a.t - b.t);
          idx = this.tags.indexOf(tag);
        }
      } else if (ed.field === 'in' || ed.field === 'out') {
        const raw = v.trim();
        const t = raw ? parseTagTime(raw) : null;
        if (raw && t == null) new Notice('Time must look like 00:06:58.7');
        else if (raw !== ed.before.trim()) { this.#snapshot(); tag[ed.field] = t == null ? null : clamp(t, 0, this.duration || t); }
      } else if (ed.field === 'title') {
        const name = this.s.slugNames === true ? slugName(v) : v.trim();
        if (name !== (tag.title || '')) { this.#snapshot(); tag.title = name; }
      }
      if (ed.field === 'tags') {
        const next = parseWords(v);
        if (next.join(' ') !== clipTags(tag).join(' ')) { this.#snapshot(); tag.tags = next; }
      }
      if (ed.field === 'title' && (tag.title || '').trim()) delete tag.fresh;
      this.scheduleSave();
    }
    if (tag && !next) delete tag.fresh;
    this.selected = idx;
    this.render();
    this.#applyColumns();
    if (next && tag) { this.#edit(idx, next); return; }
    this.tableEl.focus({ preventScroll: true });
  }

  // ---- menus ----------------------------------------------------------------

  #chapterMenu(anchor) {
    const menu = new CvsMenu(this);
    if (!this.tags.length) menu.addItem((m) => m.setTitle('No clips yet').setDisabled(true));
    this.tags.forEach((tag, i) => menu.addItem((m) => m.setTitle(`${fmtTag(tag.t)}   ${tag.title || 'untitled'}`).setChecked(i === this.current).onClick(() => this.seekTo(i))));
    menu.showBelow(anchor, 'start');
  }

  #rowMenu(e, i) {
    const menu = new CvsMenu(this);
    const tg = this.tags[i];
    menu.addItem((m) => m.setTitle('Rename').setIcon('pencil').setShortcut('Enter').onClick(() => this.#edit(i, 'title')));
    menu.addItem((m) => m.setTitle('Edit start').setIcon('clock').onClick(() => this.#edit(i, 'time')));
    menu.addSeparator();
    menu.addItem((m) => m.setTitle(tg.freezes && tg.freezes.length ? `Annotate (${tg.freezes.length} freeze${tg.freezes.length === 1 ? '' : 's'})` : 'Annotate').setIcon('pencil-ruler').setShortcut('A').onClick(() => { this.selected = i; this.annotate(); }));
    menu.addItem((m) => m.setTitle('Export as mp4').setIcon('download').onClick(() => void this.exportClips([tg])));
    menu.addItem((m) => m.setTitle('Duplicate (with its freezes)').setIcon('copy-plus').onClick(() => this.duplicateClip(i)));
    menu.addItem((m) => m.setTitle(this.checked.has(tg) ? 'Uncheck' : 'Check for export').setIcon('square-check').setShortcut('X').onClick(() => this.toggleCheck(i)));
    menu.addSeparator();
    menu.addItem((m) => m.setTitle('Set In to current frame').setIcon('log-in').setShortcut('I').onClick(() => { this.selected = i; this.setInOut('in', this.time); }));
    menu.addItem((m) => m.setTitle('Set Out to current frame').setIcon('log-out').setShortcut('O').onClick(() => { this.selected = i; this.setInOut('out', this.time); }));
    menu.addItem((m) => m.setTitle('Clear In and Out').setIcon('eraser').setDisabled(this.tags[i].in == null && this.tags[i].out == null).onClick(() => { this.#snapshot(); this.tags[i].in = null; this.tags[i].out = null; this.render(); this.scheduleSave(); }));
    menu.addSeparator();
    menu.addItem((m) => m.setTitle('Move to current frame').setIcon('locate').onClick(() => this.setTagTime(i, this.time)));
    menu.addItem((m) => m.setTitle('Copy name').setIcon('copy').onClick(() => void copyText(this.tags[i].title || '', { title: 'Copy the clip name', done: 'Name copied.' })));
    menu.addItem((m) => m.setTitle('Copy as "time | name"').setIcon('copy').onClick(() => void copyText(`${fmtTag(this.tags[i].t)} | ${this.tags[i].title || ''}`, { title: 'Copy the clip', done: 'Copied.' })));
    if (HOST.copyLinkAt) menu.addItem((m) => m.setTitle('Copy link to this clip').setIcon('link').onClick(() => void HOST.copyLinkAt(this.vaultPath, { clip: this.clipKey(i) })));
    menu.addSeparator();
    menu.addItem((m) => m.setTitle('Delete').setIcon('trash').setShortcut('⌫').onClick(() => this.removeAt(i)));
    menu.showAtMouseEvent(e);
  }

  #menu(anchor) {
    const menu = new CvsMenu(this);
    const np = this.notePath();
    menu.addItem((m) => m.setTitle('Copy all clips').setIcon('copy').setDisabled(!this.tags.length).onClick(() => { const n = this.tags.length; void copyText(this.tags.map((t) => `${fmtTag(t.t)} | ${t.title || ''}`).join('\n'), { title: `Copy ${n} clip${n === 1 ? '' : 's'}`, done: `Copied ${n} clip${n === 1 ? '' : 's'}.` }); }));
    if (HOST.copyLinkAt) menu.addItem((m) => m.setTitle('Copy link at this time').setIcon('link').onClick(() => void HOST.copyLinkAt(this.vaultPath, { t: this.time })));
    menu.addSeparator();
    const nChecked = this.checked.size;
    menu.addItem((m) => m.setTitle(nChecked ? `Export ${nChecked} checked clip${nChecked === 1 ? '' : 's'}` : 'Export checked clips').setIcon('download').setShortcut('E').setDisabled(!nChecked || this.exporting).onClick(() => void this.exportChecked()));
    menu.addItem((m) => m.setTitle('Check all shown clips').setIcon('check-check').setDisabled(!this.tags.length).onClick(() => this.checkAll(true)));
    menu.addItem((m) => m.setTitle('Uncheck all').setIcon('square').setDisabled(!nChecked).onClick(() => { this.checked.clear(); this.render(); }));
    menu.addItem((m) => m.setTitle('Open the export folder').setIcon('folder-open').onClick(() => this.#revealExportFolder()));
    menu.addItem((m) => m.setTitle('Checkbox column').setIcon('square-check').setChecked(this.s.showCheckboxes !== false).onClick(async () => { this.s.showCheckboxes = this.s.showCheckboxes === false; await this.plugin.saveSettings(); }));
    menu.addSeparator();
    menu.addItem((m) => m.setTitle('Undo last change').setIcon('undo').setShortcut('⌘Z').setDisabled(!this.undo.length).onClick(() => this.undoLast()));
    menu.addItem((m) => m.setTitle('Delete all clips').setIcon('trash').setDisabled(!this.tags.length).onClick(() => {
      new ConfirmModal(this.app, 'Delete all clips?', `Removes all ${this.tags.length} clips for this video. Cmd+Z brings them back while this tab is open.`, 'Delete all', () => { this.#snapshot(); this.tags = []; this.selected = -1; this.render(); this.scheduleSave(); }).open();
    }));
    menu.addSeparator();
    for (const col of COLUMNS) {
      if (!col.icon) continue;
      const shown = !(this.s.cols[col.key] && this.s.cols[col.key].show === false);
      menu.addItem((m) => m.setTitle(`${col.label} column`).setIcon(col.icon).setChecked(shown).onClick(() => this.toggleColumn(col.key)));
    }
    menu.addItem((m) => m.setTitle('Reset column widths').setIcon('columns').onClick(() => { for (const col of COLUMNS) if (this.s.cols[col.key]) delete this.s.cols[col.key].w; this.plugin.saveSoon(); for (const p of this.plugin.players.values()) p.#applyLayout(); }));
    menu.addSeparator();
    const theme = this.s.theme || 'auto';
    for (const [k, label] of [['light', 'Light'], ['dark', 'Dark'], ['auto', 'Follow the system']]) {
      menu.addItem((m) => m.setTitle(`Appearance: ${label}`).setIcon(k === 'light' ? 'sun' : k === 'dark' ? 'moon' : 'monitor').setChecked(theme === k).onClick(async () => { this.s.theme = k; await this.plugin.saveSettings(); }));
    }
    menu.addSeparator();
    menu.addItem((m) => m.setTitle('Shortcuts').setIcon('keyboard').setShortcut('?').onClick(() => new ShortcutsModal(this.app, this.plugin).open()));
    menu.addItem((m) => m.setTitle('Player settings').setIcon('settings').onClick(() => { this.app.setting.open(); this.app.setting.openTabById(this.plugin.manifest.id); }));
    menu.showBelow(anchor, 'end');
  }

  // ---- annotate + export --------------------------------------------------------

  // The stretch of video a clip covers: In..Out, with a blank In falling back
  // to Start and a blank Out to In + the default length (setting).
  clipRange(tag) {
    const len = Math.max(1, Number(this.s.exportDefaultLength) || 10);
    const a = tag.in != null ? tag.in : tag.t;
    let b = tag.out != null ? tag.out : a + len;
    if (this.duration) b = Math.min(b, this.duration);
    return { a: Math.max(0, a), b: Math.max(a + 0.1, b) };
  }

  // A copy of a clip right after it, freezes included (fresh ids), so one play
  // can be annotated separately for two players.
  duplicateClip(i) {
    const src = this.tags[i]; if (!src) return;
    this.#snapshot();
    const copy = JSON.parse(JSON.stringify(src)); delete copy.fresh;
    if (copy.freezes) copy.freezes = copy.freezes.map((f) => ({ ...f, id: uid(), marks: f.marks.map((m) => ({ ...m, id: uid() })) }));
    this.tags.splice(i + 1, 0, copy);
    this.selected = i + 1;
    this.render(); this.scheduleSave();
    this.flash('Duplicated');
  }

  // Open the annotate mode on the selected clip (else the current one).
  annotate(tag) {
    if (this.editing) this.#commitEdit(true);
    if (!tag) { const i = this.selected >= 0 ? this.selected : this.current; tag = this.tags[i]; }
    if (!tag) { this.flash('Select a clip to annotate'); return; }
    if (this.anno && this.anno.tag === tag) return;
    // switching clips keeps the spot to come back to from the first one
    this.annoReturnT = this.anno ? this.anno.returnT : this.time;
    if (this.anno) this.anno.close(true);
    if (this.filterActive && !this.passes(tag)) this.clearFilter();
    this.selected = this.tags.indexOf(tag);
    this.anno = new Annotator(this, tag);
    this.annoReturnT = null;
    this.render();
  }

  // Done / Export: back to the whole video, where you were before annotating.
  onAnnotatorClosed(returnT) {
    this.anno = null;
    this.durEl.setText(fmtClock(this.duration, this.s.clock, this.frameSec));
    this.render();
    this.#syncPlay(); this.#syncLoop();
    this.seek(returnT != null ? returnT : this.time);
    this.stageEl.focus({ preventScroll: true });
  }

  // Export button / E: the checked clips; with none checked (and fromKey) the
  // selected clip, so one keypress exports what you are looking at.
  async exportChecked(fromKey = false) {
    let list = this.tags.filter((t) => this.checked.has(t));
    if (!list.length && fromKey) { const t = this.tags[this.selected >= 0 ? this.selected : -1]; if (t) list = [t]; }
    if (!list.length) { this.flash('Check clips to export (X or the boxes)'); return; }
    await this.exportClips(list);
  }

  async exportClips(list) {
    if (this.exporting) { this.flash('An export is already running'); return; }
    if (this.editing) this.#commitEdit(true);
    if (this.anno) this.anno.flush();
    this.exporting = true; this.exportCancel = false; this.#syncChecks();
    const ex = new HOST.ClipExporter(this);
    let msgEl;
    const notice = new Notice(createFragment((f) => {
      msgEl = f.createDiv({ text: `Exporting ${list.length} clip${list.length === 1 ? '' : 's'}...` });
      const c = f.createEl('button', { text: 'Cancel', cls: 'cvs-notice-btn' });
      c.onclick = (e) => { e.stopPropagation(); this.cancelExport(); msgEl.setText('Cancelling...'); };
    }), 0);
    const say = (t) => { if (msgEl) msgEl.setText(t); };
    const done = []; const failed = []; let skippedFreezes = 0; let cancelled = false;
    try {
      for (let k = 0; k < list.length; k++) {
        if (this.exportCancel) { cancelled = true; break; }
        const tag = list[k];
        const head = `Exporting ${k + 1} of ${list.length}: ${tag.title || 'clip'}`;
        say(head);
        const { a, b } = this.clipRange(tag);
        skippedFreezes += (tag.freezes || []).filter((f) => f.at < a - 1e-3 || f.at > b + 1e-3).length;
        try { done.push(await ex.exportClip(tag, (msg) => say(`${head}  (${msg})`))); }
        catch (e) {
          if (this.exportCancel) { cancelled = true; break; }
          failed.push(`${tag.title || fmtTag(tag.t)}: ${e && e.message || e}`); console.error('[cth-video] export', e);
        }
      }
    } finally {
      notice.hide();
      this.exporting = false; this.exportCancel = false; this.#syncChecks();
    }
    if (cancelled) new Notice(`Export cancelled${done.length ? ` after ${done.length} clip${done.length === 1 ? '' : 's'}` : ''}.`);
    if (done.length) {
      const frag = createFragment((f) => {
        f.createDiv({ text: done.length === 1 ? `Exported ${done[0].name}` : `Exported ${done.length} clips to ${ex.folderVault || 'videos'}/` });
        if (skippedFreezes) f.createDiv({ cls: 'cvs-notice-warn', text: `${skippedFreezes} freeze${skippedFreezes === 1 ? ' sits' : 's sit'} outside In / Out and ${skippedFreezes === 1 ? 'was' : 'were'} left out.` });
        const row = f.createDiv({ cls: 'cvs-notice-row' });
        const b = row.createEl('button', { text: 'Show in folder', cls: 'mod-cta cvs-notice-btn' });
        b.onclick = () => HOST.reveal(done[done.length - 1].abs);
        if (done.length === 1) {
          const o = row.createEl('button', { text: 'Open', cls: 'cvs-notice-btn' });
          o.onclick = () => { const vf = this.app.vault.getAbstractFileByPath(normalizePath(`${ex.folderVault}/${done[0].name}`)); if (vf) this.app.workspace.getLeaf('tab').openFile(vf); };
        }
      });
      new Notice(frag, 8000);
      if (this.s.exportUncheck !== false) { for (const t of list) this.checked.delete(t); this.render(); }
    }
    if (failed.length) new Notice(`Could not export ${failed.length} clip${failed.length === 1 ? '' : 's'}:\n${failed.join('\n')}`, 12000);
  }

  #revealExportFolder() { HOST.revealFolder(this.s.exportFolder || 'videos'); }

  commitEditIfAny() { if (this.editing) this.#commitEdit(true); }
  tick() { this.#tick(true); }

  // The exporter checks exportCancel between steps and stops.
  cancelExport() { if (this.exporting) this.exportCancel = true; }

  // ---- keys (called by the plugin when this tab is active) -----------------

  handleKey(e) {
    if (this.editing || this.clockInput) return false;
    if (this.anno && this.anno.handleKey(e)) return true;
    const k = e.key;
    const s = this.s;
    const inList = document.activeElement === this.tableEl;
    if (e.metaKey && !e.shiftKey && (k === 'z' || k === 'Z')) { this.undoLast(); return true; }
    if (e.metaKey && !e.shiftKey && (k === 'e' || k === 'E')) { void this.exportChecked(true); return true; }
    if (e.metaKey || e.ctrlKey || e.altKey) return false;
    switch (k) {
      case ' ': this.togglePlay(); return true;
      case 'ArrowLeft': this.step(-(e.shiftKey ? s.stepFramesShift : s.stepFrames)); return true;
      case 'ArrowRight': this.step(e.shiftKey ? s.stepFramesShift : s.stepFrames); return true;
      case 'ArrowUp': this.jump(-1); return true;
      case 'ArrowDown': this.jump(1); return true;
      case 'Home': this.seek(0); return true;
      case 'End': this.seek(this.duration); return true;
      case 'j': this.skip(-s.skipSmall); return true;
      case 'J': this.skip(-s.skipLarge); return true;
      case 'l': this.skip(s.skipSmall); return true;
      case 'L': this.skip(s.skipLarge); return true;
      case 'k': this.togglePlay(); return true;
      case '-': case '_': this.speedStep(-1); return true;
      case '=': case '+': this.speedStep(1); return true;
      case '0': this.setSpeed(1); return true;
      case 'r': case 'R': this.toggleLoop(); return true;
      case 'm': case 'M': this.toggleMute(); return true;
      case 'f': case 'F': this.toggleFullscreen(); return true;
      case 's': case 'S': this.toggleSidebar(); return true;
      case 'i': this.setInOut('in', this.time); return true;
      case 'I': this.setInOut('in', null); return true;
      case 'o': this.setInOut('out', this.time); return true;
      case 'O': this.setInOut('out', null); return true;
      case '?': new ShortcutsModal(this.app, this.plugin).open(); return true;
      case 'b': case 'B': this.togglePanel(); return true;
      case 't': case 'T': if (!s.sidebarOpen) this.toggleSidebar(true); this.addAtCurrent(); return true;
      case 'a': case 'A': this.annotate(); return true;
      case 'e': case 'E': void this.exportChecked(true); return true;
      case 'x': case 'X': if (this.selected >= 0) { this.toggleCheck(this.selected); return true; } return false;
      case 'Enter': if (this.selected >= 0) { this.#edit(this.selected, 'title'); return true; } return false;
      case 'Backspace': case 'Delete': if (this.selected >= 0) { this.removeSelected(); return true; } return false;
      case 'Escape': if (this.stageEl.hasClass('is-filled')) { this.setFilled(false); return true; } if (this.narrow && this.narrowOpen) { this.toggleSidebar(false); return true; } if (inList || this.selected >= 0) { this.select(-1); this.stageEl.focus({ preventScroll: true }); return true; } return false;
      default: return this.panel ? this.panel.handleKey(e) : false;
    }
  }

  togglePanel(force) {
    // compact controls: B shows the panel for now without changing the setting
    if (this.short) { this.shortPanel = force == null ? !this.shortPanel : !!force; this.view.contentEl.toggleClass('cvs-short-panel', this.shortPanel); return; }
    this.s.panelOpen = force == null ? !this.s.panelOpen : !!force;
    this.plugin.saveSoon();
    for (const p of this.plugin.players.values()) if (p.panel) p.panel.render();
  }

  destroy() {
    if (this.dead) return;
    CvsMenu.closeOpen();
    if (this.anno) this.anno.close(true);
    if (this.editing) this.#commitEdit(true);
    if (this.dirty) { clearTimeout(this.saveTimer); void this.save(); }
    this.dead = true;
    clearTimeout(this.saveTimer); clearTimeout(this.flashTimer); clearTimeout(this.savedTimer); clearTimeout(this.posTimer);
    cancelAnimationFrame(this.raf);
    this.#savePosition();
    for (const u of this.unbinders) u();
    if (this.frameRO) this.frameRO.disconnect();
    if (this.narrowRO) this.narrowRO.disconnect();
    if (this.panel) this.panel.destroy();
    if (this.scrubber) { this.scrubber.destroy(); this.plugin.scrubbers.delete(this.video); }
    // Hand the element back as a plain native player, so whatever still
    // holds the view is left with a working video.
    const content = this.view.contentEl;
    if (this.video.isConnected || this.video.parentElement === this.frameEl) {
      const holder = createDiv({ cls: 'video-container' });
      this.video.removeClass('cvs-video');
      this.video.controls = true;
      holder.appendChild(this.video);
      content.appendChild(holder);
    }
    this.rootEl.remove();
    content.removeClass('cvs-content', 'cvs-side-right');
    content.style.removeProperty('--cvs-side-w');
  }
}

// ============================================================================
// plugin
// ============================================================================

const SHARED_DATA = 'videos/.cth-video.json'; // see CthVideoPlugin.loadData

const DEFAULT_SETTINGS = {
  // scrub engine
  enabled: true,
  sensitivity: 1,
  reverse: false,
  axis: 'horizontal',
  fineModifier: 'shift',
  useDecoder: true,
  frameCacheMB: 0,          // decoded frames kept for scrubbing; 0 = automatic (640 MB, 128 MB on phones, tablets and machines with 4 GB or less)
  // player
  theme: 'auto',            // 'light' | 'dark' | 'auto' (follow the system; the default since 2026-10-01, saved choices kept)
  clock: 'ms',              // 'ms' | 'frames'
  decimals: 1,              // fraction digits on the transport clock (0 to 3)
  sidebarDecimals: 0,       // fraction digits in the sidebar's Start / In / Out cells
  skipSmall: 5,
  skipLarge: 30,
  stepFrames: 1,
  stepFramesShift: 10,
  clickToPlay: true,
  stageStyle: 'card',       // 'card' (bordered picture on the app background) | 'edge' (black, edge to edge)
  resume: true,
  volume: 1,
  keysEnabled: true,
  showPill: true,
  showMarks: true,
  fullscreenMode: 'auto',   // 'auto' (the browser's fullscreen; an iPhone uses its own video player; fills the window where blocked) | 'fill' (always fill the window or frame)
  shortBelow: 420,          // player height (px) under which the button panel hides and the controls fold into one row; 0 = never
  embedKeyHint: true,       // in a Notion embed, say "Click the video to use keyboard shortcuts" until it has had focus once
  // sidebar
  sidebarOpen: true,
  sidebarSide: 'right',     // the clip log beside the video, on the right (since 2026-10-02: the file tree is on the left)
  layoutV: 2,               // 2 = the clip log moved to the right once (2026-10-02)
  sidebarWidth: 300,
  sidebarSheetBelow: 0,     // pane width (px) under which the sidebar becomes a slide-over; 0 = automatic (sidebar width + 184)
  cols: { title: {}, tags: { show: true }, t: { show: true }, in: { show: true }, out: { show: true } },
  slugNames: false,         // clip names lowercase-hyphenated ("breakout"); off: as typed ("Breakout 100226")
  clipDate: true,           // new clip names end with the day, MMDDYY ("Breakout 100226"); repeats are numbered ("Breakout 2 100226")
  clipsV: 2,                // settings version for the clip names change (2026-10-02)
  showCheckboxes: true,     // checkbox column for choosing clips to export
  followPlayback: true,
  tagAddStartsIn: 'title',
  tagEnterAdvances: true,
  tagClickToEdit: true,
  tagFolder: '',
  tagNoteSuffix: '.clips',
  // button panel
  panelOpen: true,
  panelTooltips: true,
  panelKeyBadges: true,     // a panel button's keyboard shortcut shown a little dimmed before its name      // hovering a panel button shows its name and keyboard shortcut
  panel: null,              // defaultPanel() on first use
  panelLibrary: [],
  panelDefaultIn: null,     // seconds before Start for In when a button/row sets none (null = leave empty)
  panelDefaultOut: null,
  gridlines: true,
  rowHeight: 28,
  sortKey: 'time',
  sortDir: 'asc',
  pauseOnAdd: true,
  discardBlankOnEscape: true,
  // export
  exportFolder: 'videos',   // folder the mp4s land in (inside videos/, the library)
  exportQuality: 'high',    // 'high' | 'standard' | 'small'
  exportDefaultLength: 10,  // seconds when a clip's Out is blank (a blank In starts at Start)
  exportNameChip: false,    // clip name in the lower-left corner for the first 3 seconds
  exportUncheck: true,      // untick clips after they export
  // annotate
  annoFreezeDur: 3,         // seconds a new freeze holds
  annoReveal: 'sequence',   // 'sequence' (marks appear one by one) | 'together'
  annoRevealGap: 0.35,      // seconds between marks in a sequence
  annoMarkColor: 'cyan',    // new marks (a tool's own default color wins); was annoColor, reset to cyan 2026-09-24
  annoLabelOnPlace: true,   // placing a player asks for its number / letter
  annoNoteStyle: 'dark',
  annoNoteSize: 'm',
  annoSlots: null,          // player button colors [{ fill }, x2]; null = black, blue
  annoToolOrder: [],        // the Annotate toolbar's order: tool ids and '|' separators ([] = as built)
  annoToolDefaults: {},     // per tool: { color, w, dash, fs, style, bw, r, h, head, anim, amount }
  // the app's folder table (web/app.js): its views per folder, { defaults: [view] | null, folders: { '<folder>': [view] } }
  tableViews: null,
  annoNotePresets: 'Stick on the ice\nHead up: scan before you get it\nSupport the puck carrier\nGreat read!\nShoulder check\nStop at the net\nGet to open ice\nInside body position\nMove your feet\nCommunicate: call for it',
};

// Known keys only, defaults for anything missing, nested cols merged per column.
// Keys this version does not know (older ones such as tabIcon or ffmpegDir,
// still in the shared file) are dropped here and never read.
function normalizeSettings(raw) {
  const out = Object.assign({}, DEFAULT_SETTINGS);
  for (const k of Object.keys(DEFAULT_SETTINGS)) if (k in raw) out[k] = raw[k];
  if (out.panel && !Array.isArray(out.panel.rows)) out.panel = null;
  // cols is nested: merge per column so a new column gets its default
  out.cols = Object.assign({}, DEFAULT_SETTINGS.cols, raw.cols || {});
  for (const k of Object.keys(DEFAULT_SETTINGS.cols)) out.cols[k] = Object.assign({}, DEFAULT_SETTINGS.cols[k], (raw.cols || {})[k] || {});
  if (typeof raw.showTimeColumn === 'boolean') out.cols.t.show = raw.showTimeColumn; // pre-2.2 key
  if (!(Number(raw.clipsV) >= 2)) { out.slugNames = false; out.clipsV = 2; } // 2026-10-02: names as typed, tags in their own property
  if (!(Number(raw.layoutV) >= 2)) { out.sidebarSide = 'right'; out.layoutV = 2; } // 2026-10-02: the clip log moves to the right once
  return out;
}

// Table views merge folder by folder (and the default set as one): mine when only I changed it since the
// last sync, theirs when only they did, mine when both did.
function mergeTableViews(base, mine, theirs) {
  const J = JSON.stringify; const pick = (b, m, t) => (J(m) === J(t) ? m : J(m) === J(b) ? t : m);
  const b = base || {}; const m = mine || {}; const t = theirs || {};
  const out = { defaults: pick(b.defaults ?? null, m.defaults ?? null, t.defaults ?? null), folders: {} };
  for (const k of new Set([...Object.keys(m.folders || {}), ...Object.keys(t.folders || {})])) {
    const v = pick((b.folders || {})[k], (m.folders || {})[k], (t.folders || {})[k]);
    if (v !== undefined) out.folders[k] = v;
  }
  out.statuses = {}; // the folders' status sets, the same way
  for (const k of new Set([...Object.keys(m.statuses || {}), ...Object.keys(t.statuses || {})])) {
    const v = pick((b.statuses || {})[k], (m.statuses || {})[k], (t.statuses || {})[k]);
    if (v !== undefined) out.statuses[k] = v;
  }
  return out;
}

class CthVideoPlugin extends Plugin {
  async onload() {
    const data = (await this.loadData()) || {};
    const raw = data.settings || data;
    this.settings = normalizeSettings(raw);
    this.sharedSeen = data.savedAt || 0; // the newest shared copy this host has taken in
    this.sharedBase = JSON.stringify(this.settings); // the settings as of the last sync (the merge base)
    // Pre-rebuild installs used ".tags"; one name, everywhere.
    if (this.settings.tagNoteSuffix === '.chapters' || this.settings.tagNoteSuffix === '.tags') { this.migrateSuffix = this.settings.tagNoteSuffix; this.settings.tagNoteSuffix = '.clips'; }
    this.positions = data.positions || {};
    DISPLAY_DECIMALS = this.settings.decimals;
    this.#applyFont();
    this.scrubbers = new Map(); // video element -> Scrubber (one per player)
    this.players = new Map();   // video element -> Player
    this.active = null;
    this.addSettingTab(new CthVideoSettingTab(this.app, this));

    this.observer = new MutationObserver(() => this.#scheduleScan());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.registerEvent(this.app.workspace.on('layout-change', () => { this.#reap(); this.#scheduleScan(); }));
    this.registerEvent(this.app.workspace.on('active-leaf-change', () => this.#scheduleScan()));
    this.registerEvent(this.app.workspace.on('css-change', () => { for (const p of this.players.values()) p.applyTheme(); }));
    this.app.workspace.onLayoutReady(() => { this.#scan(); if (this.migrateSuffix) void this.#migrateNotes(this.migrateSuffix); void this.#createSharedIfMissing(); });
    // Clip notes follow their video through renames and deletes.
    this.registerEvent(this.app.vault.on('rename', (file, oldPath) => void this.#followRename(file, oldPath)));
    this.registerEvent(this.app.vault.on('delete', (file) => void this.#followDelete(file)));
    this.registerEvent(this.app.vault.on('modify', (file) => {
      if (!file || !file.path || !file.path.endsWith((this.settings.tagNoteSuffix || '.clips') + '.md')) return;
      const ps = [...this.players.values()].filter((p) => p.notePath() === file.path);
      if (ps.length) void this.app.vault.read(file).then((text) => { for (const p of ps) p.reloadIfIdle(text); });
    }));
    this.registerDomEvent(window, 'keydown', (e) => this.#onKey(e), { capture: true });
    // settings changed in another browser while this window was in the background
    this.registerDomEvent(window, 'focus', () => { if (Date.now() - (this.pulledAt || 0) > 3000) { this.pulledAt = Date.now(); void this.pullShared(); } });

    const P = () => this.#player();
    this.addCommand({ id: 'toggle-sidebar', name: 'Toggle clip sidebar', callback: () => P()?.toggleSidebar() });
    this.addCommand({ id: 'toggle-time-column', name: 'Toggle Start column', callback: () => P()?.toggleColumn('t') });
    this.addCommand({ id: 'toggle-in-column', name: 'Toggle In column', callback: () => P()?.toggleColumn('in') });
    this.addCommand({ id: 'toggle-out-column', name: 'Toggle Out column', callback: () => P()?.toggleColumn('out') });
    this.addCommand({ id: 'set-in', name: 'Set In of selected clip to current frame', callback: () => { const p = P(); if (p) p.setInOut('in', p.time); } });
    this.addCommand({ id: 'set-out', name: 'Set Out of selected clip to current frame', callback: () => { const p = P(); if (p) p.setInOut('out', p.time); } });
    this.addCommand({ id: 'add-chapter', name: 'Add clip at current time', callback: () => P()?.addAtCurrent() });
    this.addCommand({ id: 'next-chapter', name: 'Next clip', callback: () => P()?.jump(1) });
    this.addCommand({ id: 'prev-chapter', name: 'Previous clip', callback: () => P()?.jump(-1) });
    this.addCommand({ id: 'step-back', name: 'Step back one frame', callback: () => P()?.step(-this.settings.stepFrames) });
    this.addCommand({ id: 'step-forward', name: 'Step forward one frame', callback: () => P()?.step(this.settings.stepFrames) });
    this.addCommand({ id: 'play-pause', name: 'Play / pause', callback: () => P()?.togglePlay() });
    this.addCommand({ id: 'toggle-theme', name: 'Toggle player light / dark', callback: async () => { this.settings.theme = this.settings.theme === 'dark' ? 'light' : 'dark'; await this.saveSettings(); } });
    this.addCommand({ id: 'shortcuts', name: 'Show player shortcuts', callback: () => new ShortcutsModal(this.app, this).open() });
    this.addCommand({ id: 'annotate', name: 'Annotate the selected clip', callback: () => P()?.annotate() });
    this.addCommand({ id: 'export-checked', name: 'Export checked clips as mp4', callback: () => void P()?.exportChecked(true) });
    this.addCommand({ id: 'toggle-scrub', name: 'Toggle trackpad scrubbing', callback: async () => { this.settings.enabled = !this.settings.enabled; await this.saveSettings(); } });
  }

  onunload() {
    if (this.fontStyle) this.fontStyle.remove();
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.scanTimer); clearTimeout(this.saveTimer);
    for (const p of this.players.values()) p.destroy();
    this.players.clear();
    for (const s of this.scrubbers.values()) s.destroy();
    this.scrubbers.clear();
    releaseAllScrubSources();
    void this.saveData({ settings: this.settings, positions: this.positions });
    void this.shareNow(); // quitting: share now rather than after the debounce
  }

  // Geist Variable, the same file CTH Calendar / Mail / Bookings bundle, served
  // next to the page (build-web.mjs copies it into dist/fonts).
  #applyFont() {
    if (this.fontStyle) return;
    this.fontStyle = document.head.createEl('style', { attr: { id: 'cvs-font-style' } });
    this.fontStyle.textContent = `@font-face { font-family: 'Geist Variable'; font-style: normal; font-display: swap; font-weight: 100 900; src: url("fonts/geist-latin-wght-normal.woff2") format('woff2-variations'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD; }`;
  }

  sidecarFor(videoPath) {
    const stem = videoPath.replace(/\.[^./]+$/, '');
    const suffix = this.settings.tagNoteSuffix || '.clips';
    if (this.settings.tagFolder) return normalizePath(`${this.settings.tagFolder}/${baseName(stem)}${suffix}.md`);
    return normalizePath(`${stem}${suffix}.md`);
  }

  #isVideo(path) { return /\.(mp4|m4v|mov|webm|mkv|ogv)$/i.test(path || ''); }

  async #migrateNotes(oldSuffix) {
    let n = 0;
    for (const f of this.app.vault.getMarkdownFiles()) {
      if (!f.path.endsWith(oldSuffix + '.md')) continue;
      const target = f.path.slice(0, -(oldSuffix + '.md').length) + this.settings.tagNoteSuffix + '.md';
      if (this.app.vault.getAbstractFileByPath(target)) continue;
      try { await this.app.fileManager.renameFile(f, target); n++; } catch (_) { /* leave it */ }
    }
    this.migrateSuffix = null;
    await this.saveData({ settings: this.settings, positions: this.positions });
    if (n) new Notice(`Renamed ${n} clip note${n === 1 ? '' : 's'} to "${this.settings.tagNoteSuffix}.md".`);
    for (const p of this.players.values()) void p.load();
  }

  async #followRename(file, oldPath) {
    if (!this.#isVideo(oldPath) || !file || !file.path) return;
    const old = this.sidecarFor(oldPath);
    const note = this.app.vault.getAbstractFileByPath(old);
    if (!note) return;
    const target = this.sidecarFor(file.path);
    if (this.app.vault.getAbstractFileByPath(target)) return;
    try { await this.app.fileManager.renameFile(note, target); } catch (_) { /* fine */ }
    const moved = this.app.vault.getAbstractFileByPath(target);
    if (moved) { try { await this.app.fileManager.processFrontMatter(moved, (y) => { y.video = file.path; y.type = 'video-clips'; }); } catch (_) { /* fine */ } }
    if (this.positions[oldPath] != null) { this.positions[file.path] = this.positions[oldPath]; delete this.positions[oldPath]; this.saveSoon(); }
  }

  async #followDelete(file) {
    if (!file || !this.#isVideo(file.path)) return;
    const note = this.app.vault.getAbstractFileByPath(this.sidecarFor(file.path));
    if (note) { try { await this.app.vault.trash(note, true); new Notice(`Moved ${note.name} to trash with its video.`); } catch (_) { /* fine */ } }
    delete this.positions[file.path];
    this.saveSoon();
  }

  // ---- shared settings (every browser and embed) -------------------------------
  // The same settings and resume positions in every browser, Notion embeds
  // included. Each browser keeps its own copy (localStorage) and also writes
  // SHARED_DATA next to the videos (vault videos/ = ~/CTH/videos on the Mac),
  // so the server carries it. At load the newer copy wins. Settings changes are
  // shared within a second; position-only saves (every 5 s while playing) at
  // most once a minute.
  async loadData() {
    const local = (await super.loadData()) || null;
    let shared = null;
    try { if (await this.app.vault.adapter.exists(SHARED_DATA)) shared = JSON.parse(await this.app.vault.adapter.read(SHARED_DATA)); } catch (_) { shared = null; }
    if (shared && shared.settings && (!local || (shared.savedAt || 0) > (local.savedAt || 0))) return shared;
    return local;
  }

  async saveData(data) {
    if (data && typeof data === 'object') data.savedAt = Date.now();
    await super.saveData(data);
    this.#shareSoon();
  }

  #shareSoon() {
    const json = JSON.stringify(this.settings || {});
    const changed = json !== this.sharedJson;
    clearTimeout(this.shareTimer);
    const wait = changed ? 800 : Math.max(0, 60000 - (Date.now() - (this.sharedAt || 0)));
    this.shareTimer = setTimeout(() => void this.shareNow(), wait);
  }

  // Take in a newer shared copy, setting by setting: a setting changed here
  // since the last sync keeps this browser's value (it is shared next); every
  // other setting takes the other browser's value. So a column resized on the
  // laptop never undoes a button added on the phone a minute earlier, or the
  // reverse.
  #mergeRemote(remote) {
    const base = this.sharedBase ? JSON.parse(this.sharedBase) : null;
    const theirs = normalizeSettings(remote.settings);
    let changed = false; let mineAhead = false;
    for (const k of Object.keys(DEFAULT_SETTINGS)) {
      const mine = JSON.stringify(this.settings[k]);
      if (mine === JSON.stringify(theirs[k])) continue;
      if (k === 'tableViews') { // per folder: a view changed in one folder here never undoes another folder's change from elsewhere
        const m = mergeTableViews(base ? base[k] : null, this.settings[k], theirs[k]);
        if (JSON.stringify(m) !== mine) { this.settings[k] = m; changed = true; }
        if (JSON.stringify(m) !== JSON.stringify(theirs[k])) mineAhead = true;
        continue;
      }
      if (base && mine !== JSON.stringify(base[k])) { mineAhead = true; continue; }
      this.settings[k] = theirs[k]; changed = true; // same object: players and panels hold references to it
    }
    this.positions = Object.assign({}, remote.positions || {}, this.positions);
    this.sharedSeen = remote.savedAt;
    this.sharedBase = JSON.stringify(this.settings);
    if (changed) {
      DISPLAY_DECIMALS = this.settings.decimals;
      for (const p of this.players.values()) p.refreshChrome();
    }
    return { changed, mineAhead };
  }

  async #readShared() {
    try { if (await this.app.vault.adapter.exists(SHARED_DATA)) return JSON.parse(await this.app.vault.adapter.read(SHARED_DATA)); } catch (_) { /* offline, or not there */ }
    return null;
  }

  // Another browser saved newer settings (a button added on the phone, a
  // column resized on the laptop): take them in without a reload. Skipped while any
  // player is mid-edit, annotating or exporting; the next focus tries again.
  async pullShared() {
    if (this.pulling || !this.settings) return false;
    if ([...this.players.values()].some((p) => p.editing || p.anno || p.exporting)) return false;
    this.pulling = true;
    try {
      const remote = await this.#readShared();
      if (!remote || !remote.settings || !(remote.savedAt > (this.sharedSeen || 0))) return false;
      const { changed, mineAhead } = this.#mergeRemote(remote);
      await super.saveData({ settings: this.settings, positions: this.positions, savedAt: remote.savedAt });
      if (mineAhead) this.#shareSoon();
      return changed;
    } finally { this.pulling = false; }
  }

  async shareNow() {
    clearTimeout(this.shareTimer); this.shareTimer = 0;
    if (!this.settings) return;
    // someone else shared since this host last looked: merge first, never overwrite
    const remote = await this.#readShared();
    // No readable copy: create it only when it is really not there. A copy that
    // is there but unreadable right now (offline, a server hiccup) is left
    // alone and tried again on the next save, so a fresh browser's defaults
    // never overwrite settings saved elsewhere.
    if (!(remote && remote.settings) && !(await this.#sharedMissing())) return;
    if (remote && remote.settings && remote.savedAt > (this.sharedSeen || 0) && remote.savedAt > (this.sharedAt || 0)) this.#mergeRemote(remote);
    const json = JSON.stringify(this.settings);
    try {
      const savedAt = Date.now();
      await this.app.vault.adapter.write(SHARED_DATA, JSON.stringify({ savedAt, settings: this.settings, positions: this.positions || {} }));
      this.sharedJson = json; this.sharedAt = savedAt; this.sharedSeen = savedAt; this.sharedBase = json;
    } catch (_) { /* no videos folder here, or offline: shared on the next save */ }
  }

  // Is the shared file really missing (not just unreachable)? The vault's index
  // comes from the last full listing, so a file it lists is never taken
  // for missing, and the server is asked once more before the answer is yes.
  async #sharedMissing() {
    const v = this.app.vault;
    if (v.loaded === false || v.getAbstractFileByPath(SHARED_DATA)) return false;
    try { return !(await v.adapter.exists(SHARED_DATA)); } catch (_) { return false; }
  }

  // First run on a new library: write the shared file right away, so every
  // other browser and embed starts from these settings.
  async #createSharedIfMissing() { if (await this.#sharedMissing()) await this.shareNow(); }

  saveSoon() { clearTimeout(this.saveTimer); this.saveTimer = setTimeout(() => void this.saveData({ settings: this.settings, positions: this.positions }), 300); }

  async saveSettings() {
    await this.saveData({ settings: this.settings, positions: this.positions });
    DISPLAY_DECIMALS = this.settings.decimals;
    if (!this.settings.useDecoder) { for (const s of this.scrubbers.values()) { releaseScrubSource(s.id); s.src = null; s.srcWhy = 'decoder off'; } }
    for (const p of this.players.values()) p.refreshChrome();
    this.#scan();
  }

  /** Build the player for a video that was just put in the page, now (not after the scan's 120 ms). */
  scanNow() { clearTimeout(this.scanTimer); this.#scan(); }

  #scheduleScan() { clearTimeout(this.scanTimer); this.scanTimer = setTimeout(() => this.#scan(), 120); }

  // Tear down players whose tab is gone, right away (a closed tab must not
  // hold a decoder cache for another debounce tick).
  #reap() {
    for (const [v, p] of this.players) { if (!v.isConnected) { p.destroy(); this.players.delete(v); } }
    for (const [v, s] of this.scrubbers) { if (!v.isConnected) { s.destroy(); this.scrubbers.delete(v); } }
  }

  #scan() {
    this.#reap();
    for (const leaf of this.app.workspace.getLeavesOfType('video')) {
      const view = leaf.view;
      if (!view || !view.contentEl) continue;
      const v = view.contentEl.querySelector('video');
      if (!v || this.players.has(v)) continue;
      const path = HOST.pathFor(v);
      if (!path) continue;
      this.players.set(v, new Player(this, leaf.view, v, path));
    }
  }

  #player() {
    const leaf = this.app.workspace.activeLeaf;
    const el = leaf && leaf.view && leaf.view.contentEl;
    const v = el && el.querySelector('video');
    if (v && this.players.has(v)) return this.players.get(v);
    const first = this.players.values().next().value;
    if (!first) new Notice('Open a video first.');
    return first || null;
  }

  #onKey(e) {
    if (!this.settings.keysEnabled) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || t.closest('.cm-editor, .prompt, .modal, .menu'))) return;
    const leaf = this.app.workspace.activeLeaf;
    if (!leaf || !leaf.view || !leaf.view.contentEl || leaf.view.getViewType() !== 'video') return;
    if (!leaf.view.containerEl.contains(t) && t !== document.body) return;
    const v = leaf.view.contentEl.querySelector('video');
    const p = v && this.players.get(v);
    if (p && p.handleKey(e)) { e.preventDefault(); e.stopPropagation(); }
  }

  // Decoder status for each opened video, in the browser console.
  diag(s) {
    const rec = { at: new Date().toISOString(), path: s.path, decoder: s.srcWhy, codec: s.src ? s.src.codec : null, frames: s.src ? s.src.count : null, frameSec: s.frameSec, duration: s.duration, chrome: (navigator.userAgent.match(/Chrome\/[\d.]+/) || [''])[0] };
    console.log('[cth-video]', rec);
  }
}

class CthVideoSettingTab extends PluginSettingTab {
  constructor(app, plugin) { super(app, plugin); this.plugin = plugin; }

  display() {
    const { containerEl } = this;
    containerEl.empty();
    const s = this.plugin.settings;
    const save = () => this.plugin.saveSettings();
    const h = (t) => containerEl.createEl('h3', { text: t });

    h('Player');
    new Setting(containerEl).setName('Appearance').setDesc('The whole player: sidebar, controls, timeline. The picture area stays black either way.').addDropdown((d) => d.addOptions({ light: 'Light', dark: 'Dark', auto: 'Follow the system' }).setValue(s.theme).onChange(async (v) => { s.theme = v; await save(); }));
    new Setting(containerEl).setName('Timecode format').addDropdown((d) => d.addOptions({ ms: '00:06:58.7 (seconds, like mChapters)', frames: '06:58.21 (frames)' }).setValue(s.clock).onChange(async (v) => { s.clock = v; await save(); }));
    new Setting(containerEl).setName('Decimals on the clock').setDesc('The transport clock and timeline tooltip. Clips are always stored to the millisecond.').addDropdown((d) => d.addOptions({ '0': '00:06:58', '1': '00:06:58.7', '2': '00:06:58.70', '3': '00:06:58.700' }).setValue(String(s.decimals)).onChange(async (v) => { s.decimals = Number(v); await save(); }));
    new Setting(containerEl).setName('Decimals in the sidebar').setDesc('Start, In and Out cells. Fewer digits, narrower columns.').addDropdown((d) => d.addOptions({ '0': '00:06:58', '1': '00:06:58.7', '2': '00:06:58.70', '3': '00:06:58.700' }).setValue(String(s.sidebarDecimals)).onChange(async (v) => { s.sidebarDecimals = Number(v); await save(); }));
    new Setting(containerEl).setName('Small skip (seconds)').setDesc('J / L keys.').addSlider((sl) => sl.setLimits(1, 60, 1).setValue(s.skipSmall).setDynamicTooltip().onChange(async (v) => { s.skipSmall = v; await save(); }));
    new Setting(containerEl).setName('Large skip (seconds)').setDesc('Shift + J / L keys.').addSlider((sl) => sl.setLimits(5, 300, 5).setValue(s.skipLarge).setDynamicTooltip().onChange(async (v) => { s.skipLarge = v; await save(); }));
    new Setting(containerEl).setName('Frames per arrow press').addSlider((sl) => sl.setLimits(1, 30, 1).setValue(s.stepFrames).setDynamicTooltip().onChange(async (v) => { s.stepFrames = v; await save(); }));
    new Setting(containerEl).setName('Frames per Shift + arrow press').addSlider((sl) => sl.setLimits(1, 300, 1).setValue(s.stepFramesShift).setDynamicTooltip().onChange(async (v) => { s.stepFramesShift = v; await save(); }));
    new Setting(containerEl).setName('Picture area').setDesc('Card: the video sits in a bordered, rounded card on the app background. Edge to edge: black theater around the picture.').addDropdown((d) => d.addOptions({ card: 'Card', edge: 'Edge to edge (black)' }).setValue(s.stageStyle).onChange(async (v) => { s.stageStyle = v; await save(); }));
    new Setting(containerEl).setName('Click the picture to play / pause').addToggle((t) => t.setValue(s.clickToPlay).onChange(async (v) => { s.clickToPlay = v; await save(); }));
    new Setting(containerEl).setName('Resume where you left off').setDesc('Reopening a video returns to the last position.').addToggle((t) => t.setValue(s.resume).onChange(async (v) => { s.resume = v; await save(); }));
    new Setting(containerEl).setName('Full screen').setDesc('F, double-click or the button by the controls. Fill the window keeps the clips and marks and works in any embed; Esc leaves it.').addDropdown((d) => d.addOptions({ auto: 'Full screen (iPhone: its own video player)', fill: 'Fill the window or embed' }).setValue(s.fullscreenMode).onChange(async (v) => { s.fullscreenMode = v; await save(); }));
    new Setting(containerEl).setName('Compact controls below this height (px)').setDesc('In a short window or embed, the button panel hides (B shows it) and the controls fold into one row. 0 = never.').addText((t) => t.setPlaceholder('420').setValue(String(s.shortBelow)).onChange(async (v) => { s.shortBelow = Math.max(0, Number(v) || 0); await save(); }));
    new Setting(containerEl).setName('Keyboard hint in embeds').setDesc('In a Notion page, keys reach the player only after you click it. Shows a small hint until you do.').addToggle((t) => t.setValue(s.embedKeyHint).onChange(async (v) => { s.embedKeyHint = v; await save(); }));
    new Setting(containerEl).setName('Keyboard shortcuts').setDesc('Space, arrows, J / K / L, T and the rest. See "Show player shortcuts".').addToggle((t) => t.setValue(s.keysEnabled).onChange(async (v) => { s.keysEnabled = v; await save(); }));

    h('Clips');
    new Setting(containerEl).setName('Clip log position').setDesc('Which side of the video the clip log sits on. Right mirrors the file tree on the left.').addDropdown((d) => d.addOptions({ left: 'Left', right: 'Right' }).setValue(s.sidebarSide).onChange(async (v) => { s.sidebarSide = v; await save(); }));
    new Setting(containerEl).setName('Sidebar width (px)').setDesc('Or drag its edge. Double-click the edge to reset.').addSlider((sl) => sl.setLimits(28, 900, 4).setValue(s.sidebarWidth).setDynamicTooltip().onChange(async (v) => { s.sidebarWidth = v; await save(); }));
    new Setting(containerEl).setName('Columns').setDesc('Start, In and Out can also be collapsed by clicking their header; drag a header edge to resize, double-click it to reset (Clips goes back to fitting its names).')
      .addToggle((t) => t.setTooltip('Start').setValue(s.cols.t.show !== false).onChange(async (v) => { s.cols.t.show = v; await save(); }))
      .addToggle((t) => t.setTooltip('In').setValue(s.cols.in.show !== false).onChange(async (v) => { s.cols.in.show = v; await save(); }))
      .addToggle((t) => t.setTooltip('Out').setValue(s.cols.out.show !== false).onChange(async (v) => { s.cols.out.show = v; await save(); }));
    new Setting(containerEl).setName('Slide-over sidebar in narrow panes').setDesc('When a video pane is narrower than this many pixels the sidebar opens over the video (S or the corner button) instead of squeezing it. 0 = automatic (sidebar width plus 184).').addText((t) => t.setPlaceholder('0').setValue(String(s.sidebarSheetBelow || 0)).onChange(async (v) => { s.sidebarSheetBelow = Math.max(0, Number(v) || 0); await save(); }));
    new Setting(containerEl).setName('List follows playback').addToggle((t) => t.setValue(s.followPlayback).onChange(async (v) => { s.followPlayback = v; await save(); }));
    new Setting(containerEl).setName('Clip markers on the timeline').addToggle((t) => t.setValue(s.showMarks).onChange(async (v) => { s.showMarks = v; await save(); }));
    new Setting(containerEl).setName('Current clip label beside the timeline').addToggle((t) => t.setValue(s.showPill).onChange(async (v) => { s.showPill = v; await save(); }));
    new Setting(containerEl).setName('After T or +, start editing').setDesc('Clip: start typing right away (the time is already the current frame). Start: mChapters style, Tab moves to the clip.').addDropdown((d) => d.addOptions({ time: 'Start cell', title: 'Clip cell' }).setValue(s.tagAddStartsIn).onChange(async (v) => { s.tagAddStartsIn = v; await save(); }));
    new Setting(containerEl).setName('Enter in the time cell moves to the title').setDesc('Only while the title is blank.').addToggle((t) => t.setValue(s.tagEnterAdvances).onChange(async (v) => { s.tagEnterAdvances = v; await save(); }));
    new Setting(containerEl).setName('Date in clip names').setDesc('New clips end with the day: "Breakout 100226". A name used again that day is numbered in time order: "Breakout 2 100226".').addToggle((t) => t.setValue(s.clipDate !== false).onChange(async (v) => { s.clipDate = v; await save(); }));
    new Setting(containerEl).setName('Lowercase clip names').setDesc('Names lowercase with hyphens ("breakout-100226") instead of as typed. Tags always go in the Tags column, written "#good #d-zone".').addToggle((t) => t.setValue(s.slugNames === true).onChange(async (v) => { s.slugNames = v; await save(); }));
    new Setting(containerEl).setName('Checkbox column').setDesc('Tick clips (or press X on the selected one) to export them together. Shift-click ticks a range.').addToggle((t) => t.setValue(s.showCheckboxes).onChange(async (v) => { s.showCheckboxes = v; await save(); }));
    new Setting(containerEl).setName('Click a selected row\'s cell to edit it').addToggle((t) => t.setValue(s.tagClickToEdit).onChange(async (v) => { s.tagClickToEdit = v; await save(); }));
    new Setting(containerEl).setName('Pause the video when adding a clip').setDesc('So the frame holds still while you type the title.').addToggle((t) => t.setValue(s.pauseOnAdd).onChange(async (v) => { s.pauseOnAdd = v; await save(); }));
    new Setting(containerEl).setName('Esc on a brand-new blank clip removes it').setDesc('No stray "untitled" rows. Blur or Enter keeps it.').addToggle((t) => t.setValue(s.discardBlankOnEscape).onChange(async (v) => { s.discardBlankOnEscape = v; await save(); }));
    new Setting(containerEl).setName('Clips folder').setDesc('Blank keeps each clips note next to its video. Otherwise a folder inside videos, such as "videos/clips".').addText((t) => t.setPlaceholder('next to video').setValue(s.tagFolder).onChange(async (v) => { s.tagFolder = normalizePath(v.trim()).replace(/^\/+|\/+$/g, ''); if (s.tagFolder === '.') s.tagFolder = ''; await save(); }));
    new Setting(containerEl).setName('Clips note suffix').setDesc('"<video name><suffix>.md"').addText((t) => t.setValue(s.tagNoteSuffix).onChange(async (v) => { s.tagNoteSuffix = v.trim() || '.clips'; await save(); }));

    h('Button panel');
    new Setting(containerEl).setName('Show shortcuts on buttons').setDesc('A panel button with a keyboard shortcut shows its key, a little dimmed, just before its name.').addToggle((t) => t.setValue(s.panelKeyBadges !== false).onChange(async (v) => { s.panelKeyBadges = v; await save(); for (const pl of this.plugin.players.values()) pl.panel?.render(); }));
    new Setting(containerEl).setName('Button tooltips').setDesc('Hovering a panel button shows its name and keyboard shortcut.').addToggle((t) => t.setValue(s.panelTooltips !== false).onChange(async (v) => { s.panelTooltips = v; await save(); }));
    new Setting(containerEl).setName('Show the button panel').setDesc('Rows of quick buttons above the timeline. B toggles it.').addToggle((t) => t.setValue(s.panelOpen).onChange(async (v) => { s.panelOpen = v; await save(); }));
    new Setting(containerEl).setName('Default In buffer (seconds before Start)').setDesc('Used by clip buttons whose row and button set no buffer. Blank leaves In empty.').addText((t) => t.setPlaceholder('blank').setValue(s.panelDefaultIn == null ? '' : String(s.panelDefaultIn)).onChange(async (v) => { s.panelDefaultIn = v.trim() === '' ? null : Math.max(0, Number(v) || 0); await save(); }));
    new Setting(containerEl).setName('Default Out buffer (seconds after Start)').addText((t) => t.setPlaceholder('blank').setValue(s.panelDefaultOut == null ? '' : String(s.panelDefaultOut)).onChange(async (v) => { s.panelDefaultOut = v.trim() === '' ? null : Math.max(0, Number(v) || 0); await save(); }));
    new Setting(containerEl).setName('Reset panel to the default rows').addButton((b) => b.setButtonText('Reset').onClick(async () => { s.panel = defaultPanel(); await save(); }));

    h('Export');
    {
      const label = (vp) => (!vp || vp === 'videos' ? 'Videos (top level)' : vp.replace(/^videos\//, ''));
      const st = new Setting(containerEl).setName('Export to').setDesc('The folder exported mp4s land in. Each file is named exactly like its clip; a name already taken gets -2, -3 and so on.');
      if (HOST.pickFolder) st.addButton((b) => b.setButtonText(label(s.exportFolder)).setIcon('folder').setTooltip('Choose the export folder').onClick(() => HOST.pickFolder(s.exportFolder || 'videos', async (vp) => { s.exportFolder = vp || 'videos'; await save(); this.display(); })));
      else st.addText((t) => t.setPlaceholder('videos').setValue(s.exportFolder).onChange(async (v) => { s.exportFolder = normalizePath(v.trim() || 'videos').replace(/^\/+|\/+$/g, ''); await save(); }));
      if (HOST.pickFolder && s.exportFolder && s.exportFolder !== 'videos') st.addButton((b) => b.setButtonText('Reset').onClick(async () => { s.exportFolder = 'videos'; await save(); this.display(); }));
    }
    new Setting(containerEl).setName('Quality').setDesc('H.264, made in the browser. High suits sending to players; Small suits texting.').addDropdown((d) => d.addOptions({ high: 'High', standard: 'Standard', small: 'Small file' }).setValue(s.exportQuality).onChange(async (v) => { s.exportQuality = v; await save(); }));
    new Setting(containerEl).setName('Length when Out is blank (seconds)').setDesc('A clip with no Out exports this long from its In (or Start when In is blank too).').addSlider((sl) => sl.setLimits(2, 60, 1).setValue(s.exportDefaultLength).setDynamicTooltip().onChange(async (v) => { s.exportDefaultLength = v; await save(); }));
    new Setting(containerEl).setName('Clip name in the corner').setDesc('Shows the clip name in the lower-left corner for the first 3 seconds.').addToggle((t) => t.setValue(s.exportNameChip).onChange(async (v) => { s.exportNameChip = v; await save(); }));
    new Setting(containerEl).setName('Untick clips after export').addToggle((t) => t.setValue(s.exportUncheck).onChange(async (v) => { s.exportUncheck = v; await save(); }));

    h('Annotate');
    new Setting(containerEl).setName('Freeze length (seconds)').setDesc('How long a new freeze frame holds. Each freeze can be changed on its chip.').addSlider((sl) => sl.setLimits(0.5, 15, 0.5).setValue(s.annoFreezeDur).setDynamicTooltip().onChange(async (v) => { s.annoFreezeDur = v; await save(); }));
    new Setting(containerEl).setName('How marks appear').setDesc('One by one walks the viewer through the play; together shows everything at once.').addDropdown((d) => d.addOptions({ sequence: 'One by one', together: 'Together' }).setValue(s.annoReveal).onChange(async (v) => { s.annoReveal = v; await save(); }));
    new Setting(containerEl).setName('Time between marks (seconds)').setDesc('For "one by one".').addSlider((sl) => sl.setLimits(0.1, 1.5, 0.05).setValue(s.annoRevealGap).setDynamicTooltip().onChange(async (v) => { s.annoRevealGap = v; await save(); }));
    new Setting(containerEl).setName('Mark color').setDesc('The color new arrows, lines, zones and notes start in. Change any mark with the swatches while annotating. A tool with its own default color (below) keeps it.').addDropdown((d) => { for (const k of ANNO_COLOR_KEYS) d.addOption(k, k[0].toUpperCase() + k.slice(1)); d.setValue(ANNO_COLORS[s.annoMarkColor] ? s.annoMarkColor : 'cyan').onChange(async (v) => { s.annoMarkColor = v; await save(); }); });
    new Setting(containerEl).setName('Ask for a player\'s number when placing it').addToggle((t) => t.setValue(s.annoLabelOnPlace).onChange(async (v) => { s.annoLabelOnPlace = v; await save(); }));
    new Setting(containerEl).setName('Note style').setDesc('New notes start with this look; each note can change it.').addDropdown((d) => d.addOptions({ dark: 'Dark', light: 'Light', color: 'Color', clear: 'Clear (text only)' }).setValue(s.annoNoteStyle).onChange(async (v) => { s.annoNoteStyle = v; await save(); }));
    new Setting(containerEl).setName('Note text size').addDropdown((d) => d.addOptions({ s: 'Small', m: 'Medium', l: 'Large', xl: 'Extra large' }).setValue(s.annoNoteSize).onChange(async (v) => { s.annoNoteSize = v; await save(); }));
    {
      const defs = s.annoToolDefaults || {};
      const ids = Object.keys(defs).filter((k) => defs[k] && Object.keys(defs[k]).length);
      const describe = (d) => [d.color, d.w && d.w !== 1 ? `size ${d.w}` : '', d.dash ? 'dashed' : '', d.fs ? `text ${d.fs.toUpperCase()}` : '', d.style || '', d.r ? 'radius set' : '', d.h ? 'height set' : ''].filter(Boolean).join(', ') || 'custom';
      new Setting(containerEl).setName('Tool defaults').setDesc(ids.length ? 'What new marks start like, per tool. Set them by picking a tool and then a color or size, or right-click any mark and choose "Make this the default".' : 'None yet. Pick a tool and then a color or size, or right-click a mark and choose "Make this the default".')
        .addButton((b) => b.setButtonText('Reset all').setDisabled(!ids.length).onClick(async () => { s.annoToolDefaults = {}; await save(); this.display(); }));
      for (const id of ids) {
        const t = ANNO_TOOLS.find((x) => x && x.id === id);
        new Setting(containerEl).setName(`    ${t ? t.label.split(':')[0].split(' (')[0] : id}${t ? ` (${t.key.toUpperCase()})` : ''}`).setDesc(describe(defs[id]))
          .addButton((b) => b.setButtonText('Reset').onClick(async () => { const all = Object.assign({}, s.annoToolDefaults); delete all[id]; s.annoToolDefaults = all; await save(); this.display(); }));
      }
    }
    new Setting(containerEl).setName('Tool order').setDesc((s.annoToolOrder || []).length ? 'Your own order. Drag tools on the Annotate toolbar to change it.' : 'As built. Drag tools on the Annotate toolbar to put them in your own order.')
      .addButton((b) => b.setButtonText('Reset').setDisabled(!(s.annoToolOrder || []).length).onClick(async () => { s.annoToolOrder = []; await save(); for (const pl of this.plugin.players.values()) pl.anno?.resetToolOrder(); this.display(); }));
    new Setting(containerEl).setName('Quick notes').setDesc('One per line. The list button on a note inserts them.').addTextArea((t) => { t.setValue(s.annoNotePresets).onChange(async (v) => { s.annoNotePresets = v; await save(); }); t.inputEl.rows = 6; t.inputEl.style.width = '100%'; });

    h('Clip log');
    new Setting(containerEl).setName('Clip log gridlines').setDesc('Thin vertical lines between the clip log\'s columns.').addToggle((t) => t.setValue(s.gridlines).onChange(async (v) => { s.gridlines = v; await save(); }));
    new Setting(containerEl).setName('Clip log row height (px)').addSlider((sl) => sl.setLimits(22, 40, 1).setValue(s.rowHeight).setDynamicTooltip().onChange(async (v) => { s.rowHeight = v; await save(); }));

    h('Trackpad scrub');
    new Setting(containerEl).setName('Trackpad scrubbing').addToggle((t) => t.setValue(s.enabled).onChange(async (v) => { s.enabled = v; await save(); }));
    new Setting(containerEl).setName('Sensitivity').setDesc('1.0 matches CTH Studio.').addSlider((sl) => sl.setLimits(0.3, 3, 0.1).setValue(s.sensitivity).setDynamicTooltip().onChange(async (v) => { s.sensitivity = v; await save(); }));
    new Setting(containerEl).setName('Reverse direction').addToggle((t) => t.setValue(s.reverse).onChange(async (v) => { s.reverse = v; await save(); }));
    new Setting(containerEl).setName('Scrub axis').addDropdown((d) => d.addOptions({ horizontal: 'Horizontal swipes only', both: 'Horizontal and vertical' }).setValue(s.axis).onChange(async (v) => { s.axis = v; await save(); }));
    new Setting(containerEl).setName('Fine scrub modifier').addDropdown((d) => d.addOptions({ shift: 'Shift', alt: 'Option', none: 'None' }).setValue(s.fineModifier).onChange(async (v) => { s.fineModifier = v; await save(); }));
    new Setting(containerEl).setName('Frame memory for scrubbing').setDesc('Decoded frames kept for smooth scrubbing. Automatic uses 640 MB, or 128 MB on phones, tablets and machines with 4 GB or less (an iPhone closes the page past about 384 MB). Applies the next time a video opens.').addDropdown((d) => d.addOptions({ '0': 'Automatic', '128': '128 MB', '256': '256 MB', '384': '384 MB', '640': '640 MB', '1024': '1 GB' }).setValue(String(s.frameCacheMB || 0)).onChange(async (v) => { s.frameCacheMB = Number(v); await save(); }));
    new Setting(containerEl).setName('Use own decoder (WebCodecs)').setDesc('Each frame costs a decode, not a seek. Off falls back to element seeking.').addToggle((t) => t.setValue(s.useDecoder).onChange(async (v) => { s.useDecoder = v; await save(); }));

  }
}

HOST.internals = { openScrubSource, releaseScrubSource, drawMark, freezeTimeline, paintFreeze, IN_DUR, roundRect, ANNO_FONT, annoImgs, fmtTag, clamp, indexVideo, Player, Scrubber, cacheBytesFor, shortcutGroups, makeSortable, parseTagsNote, serializeTagsNote, parseTagTime, parseWords, deltaSeconds };
export default CthVideoPlugin;
